/** @typedef {import('headlight-configurable-application').Pict} Pict */

/** HIGH-LEVEL NOTES ABOUT BEHAVIOR
 * FOR DATA MANAGEMENT:
 * 	- FOR NON-SETS (Document joined observations array, either in full, or filtered by some criteria such as type or label):
 * 		- The input hash stored is the namespace that is registered in the MediaRegistry for the set of observations that we want to display in the deck.
 *  	- If, we don't find a match for the namespace on load, we assume it's a new collection that needs to be created and we create a new namespace and store it.
 * 			- Attempt to apply any filters that were passed in through options to try to find a matching set of observations to display from the document collection - no results would show an empty message.
 * 		- Else, if not filters are set, then we fallback to all document observations case, where we just display all image observations that are associated with the document in the deck.
 * 			- Additionally, there is an "excludeObservationSets" option that can be set to exclude any observations that are part of an observation set from being displayed in the deck (the "negative"/"orphan" set case).
 * 	- FOR OBSERVATION-SETS (Records in the database that are bound to IDObservationSet values):
 * 		- The IDObservationSet is stored as the value of the hidden input (either by default value, by new creation on first addition, or by selector choice)
 * 		and the namespace for that observation set is stored in the dynamic GUID map for the input hash so that we can look up the relevant observations to display in the deck.
 * FOR INTERACTIVE RENDERING:
 * - The deck layout can be configured through options, and can support different display styles such as 'thumbnail', or 'list'.
 * - We will support two types of display (but only need thumbnail/list for V1, the others are for future use):
 *  - Thumbnail: The default view which is basically the same Observation Image Set template layout we've had before.
 *  - List: The list layout of the observations, which can be useful for displaying more details about each observation in a more compact format. Makes reordering vertical instead of horizontal.
 *  - Details: shows a "media card" for each observation in the set, which can display more information and metadata about the observation in a more visually appealing way. This can be useful for observations that have rich media or a lot of details to show.
 * FOR PRINT RENDERING:
 * - In print mode, we support all the previous templates through the Media Card Builder, including "minimalist", "embed", and "default" layouts, which can be configured through options.
 * - The "embed" layout will attempt to embed the actual media content directly in the card for supported types such as images and PDFs, which can be useful for users who want to see the content of the media directly in print without having to go to a separate media section or open each media item individually.
 * FUTURE PROOFING:
 *  - Would like to make this work for ANY observation type, not locked down to Media types - could provide observation editing through modals.
*/

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;
const MediaCardBuilder = require('./../../../media-detail-cards/source/MediaCardBuilder.js');
const libUserMarkdown = require('./_UserMarkdown.js');

class ObservationCardDeck extends PictSectionInputExtension
{
	/**
	 * @param {Object} pFable - The Fable application instance.
	 * @param {Object} pOptions - The provider options.
	 * @param {string} pServiceHash - The service hash for this provider registration.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pdfjsLib = null;
		// commonly used DOM markers for the deck elements and cards within the deck to make it easier to manage the DOM manipulation for the deck
		this.cardClassName = 'observation-card';
		this.cardAttributeId = 'data-observation-id';
		this.deckClassName = 'observation-card-deck';
		this.deckIsDocumentListAttribute = 'data-deck-is-full-document-list';
		this.deckIsExcludedSetAttribute = 'data-deck-is-excluding-observation-sets';
		this.deckIsFilteredDocumentListAttribute = 'data-deck-is-filtered-document-list';
		this.deckIsObservationSetAttribute = 'data-deck-is-observation-set';
		this.deckInputHashAttribute = 'data-input-hash';
		this.deckViewHashAttribute = 'data-view-hash';
		this.packageTypeSelectorMap = {
			'AllDocumentLists': `.${this.deckClassName}[${this.deckIsDocumentListAttribute}="true"]`, // this includes all filtered lists and 'AllNegativeDocumentLists' since those are subsets of the document lists, aka, everthing NOT an Observation Set
			'AllNegativeDocumentLists': `.${this.deckClassName}[${this.deckIsExcludedSetAttribute}="true"]`,
			'AllObservationSets': `.${this.deckClassName}[${this.deckIsObservationSetAttribute}="true"]`,
		};
		// observation types that are considered "actionable" for filtered decks — quick capture and upload always produce one of these types,
		// so decks filtered by these types can safely allow Add/Remove/QuickCapture actions
		this.actionableObservationTypes = [ 'Legacy Image', 'Legacy Video', 'Legacy Audio', 'File' ];
		// additional options that only this provider should be providing (not configurable by users) that are necessary for the functionality of the deck and to manage some of the edge cases with the deck content and rendering
		this.cacheBusterAttemptTimers = [ 5000, 20000 ];
		// print rendering has no other backstop: the rasterizer only force-terminates the whole page. This bounds each
		// INDIVIDUAL pdf.js operation (document load, page load, page raster) so a stalled artifact cannot hang the
		// document. It is deliberately not a per-attachment budget — total time must stay free to scale with page count.
		this.embeddedPDFOperationTimeoutMS = 30000;
		// print rendering waits on the creator-name lookup (resolveCreatorNamesForDeck), so bound it too
		this.creatorNameLookupTimeoutMS = 10000;
		this.customCSSInjected = false;
		this.draggableConfigurationForDeck = {};
		// default options for the deck, can be overridden by passing in options through the Pict descriptor configuration for the input, or through the observation set configuration if using observation sets to manage the deck content
		this.defaultConfiguration = {
			appendNewCardToStartOfDeck: false, // option to append new cards to the start of the deck instead of the end when they are added (e.g. through quick capture or other means)
			customEmptyStateMessage: 'No items have been added to this collection.',
			customEmptyStateMessageForPrint: 'No items to display.', // option to set a custom empty state message for print view that is different from the interactive view message, which can be useful if the default message doesn't make sense in the context of printing or if we want to provide more specific instructions for users in print view
			customIconClassForQuickCaptureButton: '', // option to set a custom icon class for the quick capture button (e.g. "fa-camera" or "fas fa-camera"), if not set, it will default to a camera circle icon
			customTextForQuickCaptureButton: '', // option to set custom text for the quick capture button, if not set, it will default to "Quick Capture"
			deckLayout: 'thumbnail', // default layout style for the deck (can be 'thumbnail', 'list', or 'details')
			deckLayoutForPrint: 'default', // default layout style for the deck in print view (can be 'default', or 'minimalist' for a more compact card style)
			preventEmbeddingPDFsForPrint: false, // when true, prevents PDFs from being embedded (rendered page-by-page via pdf.js) in the deck in print view — by default PDFs are embedded so users can see the content directly in the printed output without navigating to a separate media section
			excludeObservationSets: false, // option to exclude observations that are part of an observation set from being displayed in the deck (only applicable when not using observation sets to manage the deck content)
			alwaysExcludeObservationSets: false, // when true, forces excludeObservationSets to always be active and disables the user toggle — used for decks that structurally represent the orphan/negative set (e.g. Inspection Photos)
			hideIndexTags: false, // option to hide the index tags that show up on top of the card thumbnails
			hideFiltersInHeader: false, // option to hide the filter tags in the header of the deck
			hideQuickCapture: false, // option to hide the quick capture button for adding new observations to the deck
			indexTagScope: 'local', // option to configure the scope of the index tags on card - can be "local" (default) or "document" (note: "global" might eventually be a third option later if we have a server provided index that is consistent across all clients)
			preventAddingCards: false, // flag to prevent adding new cards to the deck when true (used for print view and can be configured for interactive view as well through options)
			preventCrossDeckDragging: true, // (TODO: FALSE is not implimented yet!) flag to prevent dragging and dropping cards between different decks (true until we can account for filtering)
			preventReorderingCards: false, // flag to prevent reordering of cards in the deck when true (used for print view and can be configured for interactive view as well through options)
			preventRemovingCards: false, // flag to prevent removing cards from the deck when true (used for print view and can be configured for interactive view as well through options)
			sortOrder: 'custom', // use the media registry by default (for now)
			useObservationSet: false, // flag to indicate whether we are using Observation Sets to manage the observations in the deck, or if we are just using a flat list of observations from the MediaRegistry
			useObservationSetSelector: false, // flag to indicate whether to show the observation set selector in the header of the deck when using observation sets to manage the content in the deck
			requireExplicitSetCreation: false, // when true (and useObservationSetSelector is true), the deck will NOT auto-create a set on Add/QuickCapture clicks — the user must explicitly create a set via "Create New Set" first, and Add/QuickCapture buttons are hidden until a set is selected
			filters: { Include: {}, Exclude: {} }, // default filters for the deck, used to filter observations displayed based on different criteria such as observation type, label types, etc.
		};
		// default options for when we were routed here from an old "ObservationImageSet", "ObservationStandardSet", or "MediaDetailCards" InputType with legacy configuration options that we want to attempt to map to the new configuration options for the deck to preserve backwards compatibility with existing forms that use those input types
		this.patchedConfiguration = {
			ObservationCardDeck: {}, // this is just a placeholder to make it easier to check
			ObservationImageSet: {
				deckLayout: 'thumbnail',
				useObservationSet: true,
				useObservationSetSelector: false,
				filters: { Include: { ObservationTypes: [ 'Legacy Image', 'File' ] } },
			},
			// "ObservationSet" is the MTIWHA InputType name (maps to InputType-ObservationSet.js template);
			// "ObservationStandardSet" is the provider name (PictProvider-InputType-ObservationStandardSet.js).
			// Both aliases are needed because pInput.PictForm.InputType can be either value depending on context.
			ObservationSet: {
				deckLayout: 'thumbnail',
				useObservationSet: true,
				useObservationSetSelector: true,
				requireExplicitSetCreation: true,
				filters: { Include: { ObservationTypes: this.actionableObservationTypes } },
			},
			ObservationStandardSet: {
				deckLayout: 'thumbnail',
				useObservationSet: true,
				useObservationSetSelector: true,
				requireExplicitSetCreation: true,
				filters: { Include: { ObservationTypes: this.actionableObservationTypes } },
			},
			// "MediaDetailCards" is the legacy InputType that previously had its own provider and template.
			// It now routes through ObservationCardDeck via template redirect (same pattern as ObservationImageSet).
			// (Note: an earlier "MediaCards" entry existed here by mistake — no form ever used that InputType name.)
			MediaDetailCards: {
				deckLayout: 'details',
				useObservationSet: false,
				useObservationSetSelector: false,
				excludeObservationSets: false,
				filters: { Include: { ObservationTypes: this.actionableObservationTypes } },
			},
		};
		/** @type {Pict} */
		this.pict;
		this.pict.TemplateProvider.addTemplate('Observation-Description-Popover', /*html*/`<div class="px-2 py-1">{~D:Record.Description~}</div>`);
		this.pict.AppData.ObservationCardDeckProvider =
		{
			dynamicGUIDMap: {}, // separate map to store dynamic GUIDs for each input hash for better management and to avoid conflicts with the main GUID map used for other purposes in the application
			dynamicSettingsMap: {}, // separate map to store dynamic settings (including filters) for each input hash
			customHandleObservationSetCreateSetButtonClick: null, // custom handler for the create set button, set via setCustomHandleObservationSetCreateSetButtonClick()
			customOnAfterObservationSetCreated: null, // callback invoked after a new observation set is created via the modal (receives: pSavedSet, pInput)
			customOnAfterObservationSetChanged: null, // callback invoked after an observation is added to or removed from an observation set (receives: pInput, pIDObservation, pObservationSet)
			documentContextCallback: null, // callback invoked after observation set content changes for broader document-level side effects (e.g. orphan observation recalculation)
			customHandleSearchFiltersForSelector: null, // optional custom filter callback for the set selector's Select2 AJAX query (receives: pFilters[], returns: pFilters[])
			customOnAfterMarshal: null, // callback invoked after the CardDeck finishes marshalling and rendering cards for an input (receives: pInput, pObservationList)
			settingsOverridesByInputAddress: {}, // map of input Address → CardDeckSettings overrides, merged in setCurrentSettingsForInput after pSettings but before persisted overrides
			documentScopeEmptyState: false, // flag set by the custom search filter when document scope is active but no sets exist yet — used by processResults to show a contextual empty message
			userNameCache: {}, // map of IDUser → display name, populated by resolveCreatorNamesForDeck() for the details view and print cards
		};
		// fallback in-memory store for user-toggled overrides (e.g. excludeObservationSets) used when no FormData root is available yet.
		// The persisted store lives at FormData.ObservationCardDeckOverrides so it survives save/reload alongside MediaRegistryStore.
		this._inMemoryOverridesStore = {};
		// cache of IDObservationSet → saved Name, populated lazily on first header render.
		// Values are Promises while the fetch is in flight, then resolved strings, so concurrent callers share a single request.
		/** @type {Object<string, string|Promise<string>>} */
		this._observationSetNameCache = {};
		// caches for managing the state of the deck content and rendering to account for some of the edge cases with the deck such as multiple decks on the same form,
		// multiple views with the same deck, and keeping the content up to date when changes are made to the observations in the document through other means outside of the deck (e.g. through the observation panels, quick capture, etc.)
		/**
		 * @type {Promise<any>[]}
		 */
		this.renderPromises = [];
		/**
		 * @type {Promise<any>[]}
		 */
		this.marshalPromises = [];
		this._isRemarshalInProgress = false;
		this._previousObservationSetIDs = {};
		/**
		 * @type {any[]}
		 */
		this.registeredDependencyInputHashList = [];
		// tracks Add/QuickCapture buttons that have been put into an `is-loading` state pending the
		// completion of a panel-driven add flow. If the user dismisses the panel without completing
		// the flow (X button, escape, background click, etc.), the panel's closeHandler calls
		// clearPendingAddButtonLoadingState() to ensure these don't get stuck in the loading state.
		/** @type {Set<HTMLElement>} */
		this._pendingAddButtonElements = new Set();
		// Per-input re-entrancy guard for the async print-view deck build (buildCardDeckCardsForPrintView).
		// The form solve/marshal cycle can invoke the build more than once for the same input during a single
		// PDF render; because the build awaits pdf.js, overlapping runs would interleave their appends and
		// duplicate embedded PDFs. This flag lets a build-in-flight short-circuit later overlapping calls.
		/** @type {Object<string, boolean>} */
		this._printBuildInFlight = {};
	}

	/**
	 * Whether the current document is locked (read-only).
	 * @returns {boolean}
	 */
	get isDocumentLocked()
	{
		return !!this.pict.AppData?.DocumentData?.ReportData?.Locked;
	}

	/**
	 * Whether the current user has edit permissions on the document.
	 * @returns {boolean}
	 */
	get canEditDocument()
	{
		return !!this.pict.AppData?.DocumentData?.ReportData?.CanEdit;
	}

	/**
	 * Returns the root object used to persist user-toggled deck overrides across save/reload.
	 * Lives under FormData (like MediaRegistryStore) so it is saved with the document, and falls back to an
	 * in-memory object when no FormData root is available (e.g. unit tests, pre-document-init bootstrap).
	 * @returns {Object} - The persisted override store object, keyed by input hash.
	 */
	getPersistedOverridesStore()
	{
		const formData = this.pict.AppData?.DocumentData?.ReportData?.FormData;
		if (!formData)
		{
			return this._inMemoryOverridesStore;
		}
		if (!formData.ObservationCardDeckOverrides)
		{
			formData.ObservationCardDeckOverrides = {};
		}
		return formData.ObservationCardDeckOverrides;
	}

	/**
	 * Returns the persisted overrides for a given input (or null if none saved).
	 * @param {Object} pInput - The input object (used to access the input hash).
	 * @returns {Object|null}
	 */
	getPersistedOverridesForInput(pInput)
	{
		const store = this.getPersistedOverridesStore();
		return store[pInput.Hash] || null;
	}

	/**
	 * Writes a single override key/value to the persisted overrides store for a given input.
	 * @param {Object} pInput - The input object (used to access the input hash).
	 * @param {string} pOverrideKey - The override field name (e.g. 'excludeObservationSets').
	 * @param {any} pOverrideValue - The override value to persist.
	 */
	setPersistedOverrideForInput(pInput, pOverrideKey, pOverrideValue)
	{
		const store = this.getPersistedOverridesStore();
		if (!store[pInput.Hash])
		{
			store[pInput.Hash] = {};
		}
		store[pInput.Hash][pOverrideKey] = pOverrideValue;
	}

	getHiddenInputHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#${pInputHTMLID}`;
	}
	getLabelHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#LABEL-FOR-${pInputHTMLID}`;
	}
	getCardDeckContainerHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#CARD-DECK-CONTAINER-FOR-${pInputHTMLID}`;
	}
	getCardDeckHeaderFilterContainerHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#CARD-DECK-HEADER-FILTERS-FOR-${pInputHTMLID}`;
	}
	getCardDeckHeaderExcludeObservationSetHiddenInputHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#CARD-DECK-HEADER-EXCLUDE-OBSERVATION-SET-HIDDEN-INPUT-FOR-${pInputHTMLID}`;
	}
	getCardDeckHeaderObservationSetSelectorContainerHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#CARD-DECK-HEADER-OBSERVATION-SET-SELECTOR-CONTAINER-FOR-${pInputHTMLID}`;
	}
	getCardDeckHeaderObservationSetCreateButtonHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#CARD-DECK-HEADER-OBSERVATION-SET-CREATE-BUTTON-FOR-${pInputHTMLID}`;
	}
	getCardDeckHeaderObservationSetEditButtonHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#EDIT-BUTTON-FOR-CARD-DECK-HEADER-OBSERVATION-SET-SELECTOR-FOR-${pInputHTMLID}`;
	}
	getCardDeckHeaderObservationSetSelectorHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#CARD-DECK-HEADER-OBSERVATION-SET-SELECTOR-FOR-${pInputHTMLID}`;
	}
	getCardDeckHeaderObservationSetDisplayHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#CARD-DECK-HEADER-OBSERVATION-SET-DISPLAY-FOR-${pInputHTMLID}`;
	}
	getCardDeckContentHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#CARD-DECK-CONTENT-FOR-${pInputHTMLID}`;
	}
	getCardDeckContainerEmptyMessageHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#EMPTY-MESSAGE-FOR-${pInputHTMLID}`;
	}
	getCardDeckHeaderActionsContainerHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#CARD-DECK-HEADER-ACTIONS-FOR-${pInputHTMLID}`;
	}
	getCardDeckHeaderActionsButtonAddHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#CARD-DECK-HEADER-ACTIONS-ADD-BUTTON-FOR-${pInputHTMLID}`;
	}
	getCardDeckHeaderActionsButtonQuickCaptureHTMLID(/** @type {string} */ pInputHTMLID)
	{
		return `#CARD-DECK-HEADER-ACTIONS-QUICK-CAPTURE-BUTTON-FOR-${pInputHTMLID}`;
	}

	/**
	 * Updates the text content of the empty message element for a given input's card deck.
	 * @param {Object} pInput - The input object.
	 * @param {string} pMessage - The message to display.
	 */
	updateEmptyMessageText(pInput, pMessage)
	{
		const emptyMessageElement = document.querySelector(this.getCardDeckContainerEmptyMessageHTMLID(pInput.Macro.RawHTMLID));
		if (emptyMessageElement)
		{
			const textSpan = emptyMessageElement.querySelector('.icon-text');
			if (textSpan)
			{
				textSpan.textContent = pMessage;
			}
		}
	}

	/**
	 * Function to build a consistent namespace string for a card deck based on input hash, observation set ID, and whether it is temporary.
	 * @param {string} pInputHash - The hash of the input element.
	 * @param {string|number|null} [pIDObservationSet=null] - The ID of the observation set, if applicable.
	 * @param {boolean} [isTemporary=false] - Flag indicating if the card deck is temporary - for Observation Set that have not yet been saved (we only save a new pristine set up user action taken, not form load).
	 * @returns {string} - The constructed namespace string or an error string if inputs are invalid.
	 */
	buildNamespaceForCardDeck(pInputHash, pIDObservationSet = null, isTemporary = false)
	{
		if (isTemporary && !pIDObservationSet)
		{
			return `TEMP-CardDeck-For-IDObservationSet-${pInputHash}`;
		}
		else if (pIDObservationSet)
		{
			return `CardDeck-For-IDObservationSet-${pIDObservationSet}`;
		}
		else if (pInputHash)
		{
			return `CardDeck-For-${pInputHash}`;
		}
		else
		{
			this.pict.log.error(`Cannot build namespace for card deck without either a valid input hash or IDObservationSet. Received input hash: ${ pInputHash }, IDObservationSet: ${ pIDObservationSet }, isTemporary: ${ isTemporary }`);
			return 'ERROR-Invalid-Namespace-For-CardDeck';
		}
	}

	/**
	 * Drains a queue of promises, logging any errors, then empties the queue.
	 * @param {Promise<any>[]} queue - The promise queue to drain.
	 * @param {string} label - A label for error messages to identify which queue failed.
	 */
	async _drainPromiseQueue(queue, label)
	{
		for (const p of queue)
		{
			try
			{
				await p;
			}
			catch (error)
			{
				this.pict.log.error(`Error in ${label}: ${ error }`);
			}
		}
		queue.length = 0;
	}

	/**
	 * Function that can be awaited by other functions to ensure that this input type has finished rendering.
	 */
	async completeRender()
	{
		await this._drainPromiseQueue(this.renderPromises, 'PictProvider-InputType-ObservationCardDeck');
	}

	/**
	 * Function that can be awaited to ensure that all pending marshal operations (triggered by reMarshalAllViewsWithCardDecks) have completed.
	 * This is used to serialize view refreshes so that multiple card deck instances don't race over shared state.
	 */
	async waitForPendingMarshals()
	{
		await this._drainPromiseQueue(this.marshalPromises, 'pending marshal');
	}

	/**
	 * This is a callback for external functions to call when they want to trigger a refresh of the card decks that are displaying a specific observation,
	 * such as when an observation is added or removed from the document through other means outside of the deck (e.g. through the observation panels, quick capture, etc.)
	 * to ensure that the content in the deck stays up to date and reflects the current state of the document.
	 * @param {string} pIDObservation - The ID of the observation that was added, removed, or updated.
	 * @param {string} action - The type of action that triggered the refresh, which can be 'add', 'remove', or 'update', and can be used to determine how to refresh the deck content (e.g. whether to add a new card, remove an existing card, or update the content of an existing card).
	 * @param {any[]} pUpdatedObservationList - The updated list of observations in the document, used to refresh the deck content accurately.
	 * @param {string} source - The source of the action that triggered the refresh, which can be used for logging or to determine if certain decks should be refreshed based on where the action originated from (e.g. if the action came from the observation panels, we might want to refresh all decks, but if it came from a specific deck, we might only want to refresh that deck).
	 * @return {Promise<void>}
	 */
	async refreshCardDecks(pIDObservation, action, pUpdatedObservationList, source)
	{
		if (!this.pict.AppData?.DocumentData)
		{
			this.pict.log.warn('No document data found. Skipping refreshCardDecks.');
			return;
		}
		if (this.isDocumentLocked)
		{
			this.pict.log.warn('Document is locked. Skipping refreshCardDecks.');
			return;
		}
		// we need basic info to even attempt to find the cards to update, so if we don't have that, we should log a warning and exit the function to avoid unnecessary processing and potential errors
		if (!pIDObservation || !action || !source)
		{
			this.pict.log.warn(`refreshCardDecks called with invalid parameters. pIDObservation: ${pIDObservation}, action: ${action}, pUpdatedObservationList: ${pUpdatedObservationList}, source: ${source}`);
			return;
		}
		if (!pUpdatedObservationList || !Array.isArray(pUpdatedObservationList))
		{
			this.pict.log.warn(`refreshCardDecks called without a valid updated observation list. This should be the latest state of the DocumentRecord.Observations array.`);
			return;
		}
		// make sure we're using the latest list of observations from the document to ensure that any changes are reflected accurately in the deck content
		this.pict.AppData.DocumentData.ReportData.Observations = pUpdatedObservationList;
		await this.pict.providers.MediaRegistry.refreshDocumentObservations(pUpdatedObservationList);
		// refresh both document-list decks and observation-set decks, since an add/remove action can affect either type
		await this.reMarshalAllViewsWithCardDecks(pIDObservation, 'AllDocumentLists', true);
		await this.reMarshalAllViewsWithCardDecks(pIDObservation, 'AllObservationSets', true);
		return;
	}

	/**
	 * Function to register a promise that pdf rendering depends on.
	 * @param {Promise<any>} dependency - The promise we want to add to the dependency set.
	 */
	registerRenderDependency(dependency)
	{
		if (!this.pict.AppData.Interactive && dependency instanceof Promise)
		{
			this.renderPromises.push(dependency);
		}
	}

	/**
	 * Function to remove the initial loading message from the deck elements once content is loaded (we default to showing a loading message on the empty container message that is displayed when the deck is empty or prior to content being loaded).
	 * @param {Object} pInput - The input object containing the deck elements.
	 * @param {Object} pInput.Macro - The macro object for the input, which contains the raw HTML ID used to find the relevant deck content area element in the DOM to remove the loading message from.
	 * @param {string} pInput.Macro.RawHTMLID - The raw HTML ID of the input, used to find the relevant deck content area element in the DOM to remove the loading message from.
	 */
	removeInitialLoadingMessage(pInput)
	{
		const cardDeckContentAreaHTMLID = this.getCardDeckContentHTMLID(pInput.Macro.RawHTMLID);
		const cardDeckContentAreaElement = document.querySelector(cardDeckContentAreaHTMLID);
		if (cardDeckContentAreaElement)
		{
			const loadingMessageElement = cardDeckContentAreaElement.querySelector('.initial-loading-message');
			if (loadingMessageElement)
			{
				loadingMessageElement.remove();
			}
		}
	}

	/**
	 * Parse the legacy "key:value,key:value" string from PictForm.InputExtra into an options object
	 * for MediaCardBuilder. Carried over from the removed MediaDetailCards provider so legacy form
	 * configs (cardTheme, cardSize, ordinalScope, etc.) still flow through on print.
	 * @param {string} pInputExtraString - The stringy configuration data from the PictForm.InputExtra field
	 * @return {Object} - The processed configuration options
	 */
	processPictDescriptorConfigurationOptions(pInputExtraString)
	{
		if (!pInputExtraString || pInputExtraString.length === 0)
		{
			return {};
		}
		const newConfigOptions = {};
		const tmpConfig = pInputExtraString.split(',').map((item) => item.trim());
		tmpConfig.forEach((item) =>
		{
			const [ key, value ] = item.split(':').map((part) => part.trim());
			if (key && value)
			{
				if (value === 'true' || value === 'false')
				{
					newConfigOptions[key] = value === 'true';
				}
				else
				{
					newConfigOptions[key] = value;
				}
			}
		});
		return newConfigOptions;
	}

	/**
	 * Function to handle rendering of the deck in print view for variable templates. In print view, we want to render the content of the deck in a way that is optimized for printing,
	 * which may include showing more details about each observation and adjusting the layout to fit better on a printed page. We will also hide interactive elements such as buttons
	 * and popovers in print mode. The rendering logic will adjust based on the selected layout style for print view, which can be configured through options.
	 *
	 * TODO: Rename this to "ObservationCardPrintBuilder" (or similar) and refactor to support any observation type —
	 *       non-media observations should render with thumbnail placeholders rather than being filtered out by
	 *       curateMediaObservationList. This would replace the prevention logic added for non-media types and let the
	 *       print builder be a general-purpose observation card renderer. Deferring until current fixes are shipped.
	 *       When this refactor happens, also update the bridge element conditions provider's _hasRenderableMediaArtifact()
	 *       filter in bridgeinspection-elementconditions-provider.js to align with the builder's acceptance logic.
	 *
	 * @param {Object} pView - the view object for the input, which contains information about the current view and can be used to determine if we are in print view or not
	 * @param {Object} pInput - the input object for which we are rendering the deck, which contains information about the input and can be used to get the current settings and filters for the deck
	 * @param {string} pNamespace - the namespace for the deck content, which can be used to look up the relevant observations from the MediaRegistry to display in the deck
	 * @param {any[]} pObservationList - the list of observations to be rendered in the deck, which can be filtered and sorted based on the current settings and filters for the deck
	 */
	async buildCardDeckCardsForPrintView(pView, pInput, pNamespace, pObservationList)
	{
		// Re-entrancy guard. The form's solve/marshal cycle can call this multiple times for the same input
		// during one PDF render. The build is async (pdf.js rasterizes embedded PDFs page-by-page), so
		// overlapping runs each clear-then-append and interleave their canvases — duplicating embedded PDFs
		// in the output. While a build for this input is in flight, skip later overlapping calls: print data
		// is static, so the in-flight build already reflects the final content. (Interactive mode doesn't need
		// this because buildObservationCardDeck reconciles via a DOM diff instead of appending.)
		if (this._printBuildInFlight[pInput.Hash])
		{
			return;
		}
		this._printBuildInFlight[pInput.Hash] = true;
		try
		{
			await this._buildCardDeckCardsForPrintView(pView, pInput, pNamespace, pObservationList);
		}
		finally
		{
			this._printBuildInFlight[pInput.Hash] = false;
		}
	}

	/**
	 * Inner implementation of the print-view deck build. Always invoke via buildCardDeckCardsForPrintView,
	 * which guards against re-entrant/overlapping builds for the same input.
	 * @param {Object} pView - the view object for the input
	 * @param {Object} pInput - the input object for which we are rendering the deck
	 * @param {string} pNamespace - the namespace for the deck content
	 * @param {any[]} pObservationList - the list of observations to be rendered in the deck
	 */
	async _buildCardDeckCardsForPrintView(pView, pInput, pNamespace, pObservationList)
	{
		const currentSettings = this.getCurrentSettingsForInput(pInput);
		const displayStyle = currentSettings?.deckLayoutForPrint || 'default';
		const cardDeckContentAreaHTMLID = this.getCardDeckContentHTMLID(pInput.Macro.RawHTMLID);
		const cardDeckContentAreaElement = document.querySelector(cardDeckContentAreaHTMLID);
		// if we can't find the container element for the deck, we have a problem and can't render the deck, so we should log an error and bail out of the function to avoid further issues
		if (!cardDeckContentAreaElement)
		{
			this.pict.log.error(`Card deck container not found for HTML ID: ${cardDeckContentAreaHTMLID}`);
			return;
		}
		// Reset the content area before (re)building. Unlike the interactive buildObservationCardDeck (which diffs cards
		// against the DOM and is idempotent), the print builder only appends — so a second marshal pass during PDF
		// generation would otherwise tack on a duplicate copy of every card / embedded PDF.
		cardDeckContentAreaElement.innerHTML = '';
		// Determine whether PDFs should be fully embedded (rendered page-by-page via pdf.js).
		// By default PDFs are embedded unless the form explicitly opts out via preventEmbeddingPDFsForPrint.
		// The minimalist layout always embeds regardless of the setting.
		const willEmbedPDFs = !currentSettings?.preventEmbeddingPDFsForPrint || displayStyle === 'minimalist';
		const embeddedPDFIds = new Set(
			willEmbedPDFs ?
				pObservationList
					.filter((/** @type {any} */ o) => o?.Details?.File?.[0]?.Datum?.FileMimeType === 'application/pdf')
					.map((/** @type {any} */ o) => o.IDObservation) :
				[],
		);

		// Shared config for building thumbnail cards via MediaCardBuilder
		const pictConfigOptions = this.processPictDescriptorConfigurationOptions(pInput?.PictForm?.InputExtra || '');
		const blendedConfigOptions = { ...{
			cardTheme: pictConfigOptions?.cardTheme || displayStyle,
			cardSize: pictConfigOptions?.cardSize || (displayStyle === 'minimalist' ? 'half' : ''),
			ordinalScope: pictConfigOptions?.ordinalScope || (displayStyle === 'minimalist' ? 'global' : ''),
			landscapeMode: this.pict.AppData?.DocumentData?.ReportData?.ReportOptions?.Meta?.orientation === 'landscape',
			isPrintView: !this.pict.AppData.Interactive,
			// cached creator names render directly; the rest are back-filled by resolveCreatorNamesForDeck
			userNameMap: this.pict.AppData.ObservationCardDeckProvider.userNameCache,
			// Disable the default DesignatedTime sort inside MediaCardBuilder.buildCards() so that the
			// user's saved drag/reorder sequence (already reflected in pObservationList) is preserved.
			// Without this, curateMediaObservationList re-sorts the batch by date and destroys the custom order.
			sortBy: null,
			sortOrder: null,
		}, ...pictConfigOptions };

		// Walk observations IN ORDER so that embedded PDFs and thumbnail cards are interleaved
		// at their correct positions — preserving the user's custom sort/drag order.
		let thumbnailBatch = [];
		let batchIndex = 0;

		/**
		 * Flush any accumulated thumbnail-card observations into a wrapper div appended to the container.
		 * Uses MediaCardBuilder.buildCards with a temporary target element.
		 */
		const flushThumbnailBatch = () =>
		{
			if (!thumbnailBatch.length)
			{
				return;
			}
			const batchWrapper = document.createElement('div');
			const batchId = `${pInput.Macro.RawHTMLID}-print-batch-${batchIndex++}`;
			batchWrapper.id = batchId;
			cardDeckContentAreaElement.appendChild(batchWrapper);
			const mediaCardBuilder = new MediaCardBuilder();
			mediaCardBuilder.buildCards(thumbnailBatch, { ...blendedConfigOptions, target: batchId });
			thumbnailBatch = [];
		};

		for (const obs of pObservationList)
		{
			if (embeddedPDFIds.has(obs.IDObservation))
			{
				// Flush any preceding thumbnail cards before inserting the embedded PDF
				flushThumbnailBatch();
				await this.buildEmbeddedPDFsForPrintView([ obs ], cardDeckContentAreaElement);
			}
			else
			{
				thumbnailBatch.push(obs);
			}
		}
		// Flush any remaining thumbnail cards after the last embedded PDF
		flushThumbnailBatch();
	}

	/**
	 * Races a promise against a timeout so a stalled dependency cannot hang print rendering indefinitely.
	 *
	 * @param {Promise<any>} pPromise - The promise to bound.
	 * @param {number} pTimeoutMS - Milliseconds to wait before rejecting.
	 * @param {string} pLabel - Description of the awaited work, used in the timeout message.
	 * @return {Promise<any>} - The promise result, or a rejection if the timeout elapsed first.
	 */
	async _withTimeout(pPromise, pTimeoutMS, pLabel)
	{
		let tmpTimeoutID;
		try
		{
			return await Promise.race(
				[
					pPromise,
					new Promise((resolve, reject) =>
					{
						tmpTimeoutID = setTimeout(() => reject(new Error(`${pLabel} timed out after ${pTimeoutMS} ms.`)), pTimeoutMS);
					}),
				]);
		}
		finally
		{
			clearTimeout(tmpTimeoutID);
		}
	}

	/**
	 * Builds the print-view stand-in shown where an embedded PDF could not be rendered, so a missing
	 * attachment is visible in the printed output rather than silently absent.
	 *
	 * @param {string} pObservationName - Display name of the observation the attachment belongs to.
	 * @param {number} [pRenderedPageCount] - Pages that rendered before the failure, so a partial embed reads correctly.
	 * @return {HTMLDivElement} - The placeholder element.
	 */
	buildEmbeddedPDFErrorPlaceholder(pObservationName, pRenderedPageCount)
	{
		const tmpContainer = document.createElement('div');
		tmpContainer.style['break-inside'] = 'avoid';
		tmpContainer.style['margin-bottom'] = '10px';
		tmpContainer.style['padding'] = '10px';
		tmpContainer.style['border'] = '2px solid grey';
		const tmpLabel = document.createElement('b');
		tmpLabel.style.fontSize = '15px';
		tmpLabel.textContent = pRenderedPageCount > 0 ?
			`Observation: ${ pObservationName } - the remaining pages of this attachment could not be displayed.` :
			`Observation: ${ pObservationName } - attachment could not be displayed.`;
		tmpContainer.appendChild(tmpLabel);
		return tmpContainer;
	}

	/**
	 * Resolve pdf.js on first use. Only the PDF templates load it, so the interactive form never pays for it.
	 * @returns {any} the pdf.js library, or null when the page did not load it
	 */
	resolvePDFJS()
	{
		// @ts-ignore
		const tmpPDFJS = globalThis.pdfjsLib;
		if (!this.pdfjsLib && tmpPDFJS)
		{
			tmpPDFJS.GlobalWorkerOptions.workerSrc = './pdfjs-dist/build/pdf.worker.mjs';
			this.pdfjsLib = tmpPDFJS;
		}
		return this.pdfjsLib;
	}

	/**
	 * Function to build embedded PDFs in the deck for print view when the deck is configured to embed PDFs directly in print view instead of linking to them.
	 * @param {any[]} pdfs - the list of observation objects whose files are PDFs to be embedded into the print view
	 * @param {Element} containerElement - the container element for the deck content area where the embedded PDFs will be rendered
	 */
	async buildEmbeddedPDFsForPrintView(pdfs, containerElement)
	{
		if (!this.resolvePDFJS())
		{
			throw new Error(`Currently the ObservationCardDeck InputType provider requires pdfjs to be loaded in the DOM, but it isn't.`);
		}
		if (!pdfs.length || !containerElement)
		{
			return;
		}
		const embedTemplate = document.createElement('div');
		for (const o of pdfs)
		{
			const obsName = o?.Name?.length > 60 ? o.Name.substring(0, 60) + '...' : o.Name;
			const tmpIDArtifact = o?.Details?.File?.[0]?.IDArtifact;
			const tmpVersion = o?.Details?.File?.[0]?.Version;
			const tmpMediaURL = `/1.0/Artifact/Media/${tmpIDArtifact}/${tmpVersion}`;
			//@ts-ignore
			const task = this.pdfjsLib.getDocument(tmpMediaURL);
			let tmpRenderedPageCount = 0;
			try
			{
				const pdf = await this._withTimeout(task.promise, this.embeddedPDFOperationTimeoutMS, `Loading embedded PDF ${tmpMediaURL}`);
				const pageTotal = pdf.numPages;
				for (let i = 0; i < pageTotal; i++)
				{
					const page = await this._withTimeout(pdf.getPage(i + 1), this.embeddedPDFOperationTimeoutMS, `Loading page ${i + 1} of embedded PDF ${tmpMediaURL}`);
					const viewport = page.getViewport({ scale: 2 });
					const container = document.createElement('div');
					container.style['margin-bottom'] = '10px';
					// Build the page header from DOM nodes — obsName is the user-supplied observation/file name, so avoid HTML-string injection
					const headerLabel = document.createElement('span');
					headerLabel.style.fontSize = '15px';
					const headerBold = document.createElement('b');
					headerBold.textContent = `Observation: ${ obsName } - Page ${ i + 1 }`;
					headerLabel.appendChild(headerBold);
					container.appendChild(headerLabel);
					container.style['break-inside'] = 'avoid';
					const canvas = document.createElement( 'canvas' );
					canvas.style.display = 'block';
					canvas.style['position'] = 'absolute';
					canvas.style['left'] = '0px';
					canvas.style['margin-left'] = '20px';
					const scaleFactor = (window.innerWidth - 60) / viewport.width;
					canvas.style.width = `${ scaleFactor * viewport.width }px`;
					canvas.style.height = `${ scaleFactor * viewport.height }px`;
					container.style['height'] = `${ (scaleFactor * viewport.height) + 20 }px`;
					canvas.style.border = '2px solid grey';
					const context = canvas.getContext('2d');
					canvas.height = viewport.height;
					canvas.width = viewport.width;
					await this._withTimeout(page.render({ canvasContext: context, viewport: viewport }).promise, this.embeddedPDFOperationTimeoutMS, `Rendering page ${i + 1} of embedded PDF ${tmpMediaURL}`);
					container.appendChild(canvas);
					embedTemplate.appendChild(container);
					tmpRenderedPageCount++;
				}
			}
			catch (pError)
			{
				this.pict.log.error(`[ObservationCardDeck] Could not embed PDF ${tmpMediaURL} in print view: ${pError.message || pError}`,
					{
						IDObservation: o?.IDObservation,
						IDArtifact: tmpIDArtifact,
						Version: tmpVersion,
					});
				embedTemplate.appendChild(this.buildEmbeddedPDFErrorPlaceholder(obsName, tmpRenderedPageCount));
				Promise.resolve(task.destroy()).catch((pDestroyError) =>
				{
					this.pict.log.warn(`[ObservationCardDeck] Failed to release the pdf.js task for ${tmpMediaURL}: ${pDestroyError.message || pDestroyError}`);
				});
			}
		}
		// update the styles for the content area to allow the embedded PDFs to break into multiple pages when we have a lot of them,
		// and to avoid awkward blank spaces at the bottom of pages when the PDF viewer is too large to fit on one page but gets cut off instead of breaking onto the next page
		containerElement['style']['break-inside'] = 'auto';
		if (containerElement.parentElement)
		{
			containerElement.parentElement['style']['break-inside'] = 'auto';
		}
		containerElement.appendChild(embedTemplate);
		return true;
	}

	/**
	 * Toggle the deck style for the observation card deck.
	 * @param {string} id - the ID of the element to toggle the deck style for
	 * @param {string} style - the style to apply to the deck
	 */
	toggleDeckStyle(id, style)
	{
		/** @type {HTMLElement} */
		const element = /** @type {HTMLElement} */ (document.getElementById(id));
		if (!element)
		{
			this.pict.log.error(`Could not find element with ID ${ id } to toggle deck style.`);
			return;
		}
		element.classList.remove('observation-deck-style-thumbnail', 'observation-deck-style-cards', 'observation-deck-style-list', 'observation-deck-style-details');
		element.classList.add(`observation-deck-style-${style}`);
		// toggle active state on the buttons
		const buttons = element.parentElement.querySelectorAll('button');
		buttons.forEach((btn)=>
		{
			btn.classList.remove('is-active');
			if (btn.dataset.deckStyle === style)
			{
				btn.classList.add('is-active');
			}
		});
	}

	/**
	 * Build the modifier (filter/observation set) tags for the observation card deck header. These are little color (red, green, blue, or gray) coded tags
	 * that show up in the header next to the label to indicate which modifications are currently applied to the deck. This is helpful because the deck can be
	 * configured to show different subset lists of document observations based on different filters applied, or it may be an Observation Set, so it can be easy for
	 * users to forget which modifiers are currently applied, especially if they navigate away from the page and come back later. These tags provide a quick visual
	 * reference for users to understand what they're looking at in the deck. Optionally, this UI can be configured to be hidden through options if users find it overwhelming or distracting.
	 * @param {Object} pView - the pict-view instance hosting the input (used to look up bound form metadata)
	 * @param {Object} pInput - the input object for which to build the modifier tags (used to get the raw HTML ID for the input, which we use to find the container element for the header and to store the current modifiers in a data attribute on the input element)
	 * @param {boolean} hideModifiersInHeader - flag to indicate whether we should hide the modifier tags in the header based on options (if true, we won't build or show any modifier tags, even if there are modifiers applied)
	 * @returns {string} - the HTML string for the modifier tags to be inserted into the header
	 */
	/**
	 * Builds the icon + label pair used by the deck header tags and toggles as DOM nodes rather than an
	 * HTML string. Emits no whitespace between the two spans, matching the markup this replaced.
	 * @param {string} pIconClasses - classes for the <i> element (e.g. 'fa fa-fw fa-toggle-on has-text-link')
	 * @param {string} pLabelText - the label text
	 * @param {string} [pIconSpanExtraClasses] - extra classes for the icon <span>, beyond 'icon'
	 * @param {string} [pLabelSpanClasses] - classes for the label <span>; omit for a bare <span>
	 * @return {DocumentFragment}
	 */
	buildIconLabelNodes(pIconClasses, pLabelText, pIconSpanExtraClasses, pLabelSpanClasses)
	{
		const tmpFragment = document.createDocumentFragment();
		const tmpIconSpan = document.createElement('span');
		tmpIconSpan.className = pIconSpanExtraClasses ? `icon ${ pIconSpanExtraClasses }` : 'icon';
		const tmpIcon = document.createElement('i');
		tmpIcon.className = pIconClasses;
		tmpIconSpan.appendChild(tmpIcon);
		const tmpLabelSpan = document.createElement('span');
		if (pLabelSpanClasses)
		{
			tmpLabelSpan.className = pLabelSpanClasses;
		}
		tmpLabelSpan.textContent = pLabelText;
		tmpFragment.appendChild(tmpIconSpan);
		tmpFragment.appendChild(tmpLabelSpan);
		return tmpFragment;
	}

	buildFilterTagsForCardDeckHeader(pView, pInput, hideModifiersInHeader = false)
	{
		if (hideModifiersInHeader)
		{
			return '';
		}
		const currentFilters = this.getCurrentFiltersForInput(pInput);
		const currentSettings = this.getCurrentSettingsForInput(pInput);
		const tagDisplayLimit = 3; // max number of tags to show in the header before we switch to showing a summary tag instead
		const hasModifiersApplied = this.hasFiltersApplied(pInput);

		// if we don't have any modifiers to show, we should show a tag that indicates that we're showing all document observations so that users have some context for what they're looking at in the deck instead of just seeing an empty header with no tags and wondering if something is broken
		if (!hasModifiersApplied)
		{
			const headerContainer = this.getCardDeckHeaderFilterContainerHTMLID(pInput.Macro.RawHTMLID);
			const headerContainerElement = document.querySelector(headerContainer);
			if (!headerContainerElement)
			{
				this.pict.log.error(`Could not find header container element with ID ${ headerContainer } to build modifier tags for header.`);
				return '';
			}
			headerContainerElement.innerHTML = '';
			headerContainerElement.classList.add('is-flex', 'is-align-items-center', 'gap-1');
			const tag = document.createElement('span');
			tag.classList.add('tag', 'pl-1', 'is-primary', 'is-light');
			tag.appendChild(this.buildIconLabelNodes('fa fa-fw fa-file', 'All Document Observations', 'mr-0', 'icon-text'));
			headerContainerElement.appendChild(tag);

			// only show the exclude observation sets toggle if there are other card decks on the form that use observation sets, otherwise it's not relevant
			const isAlwaysExcluding = !!currentSettings.alwaysExcludeObservationSets;
			if (!isAlwaysExcluding)
			{
				// Only show the toggle when other set-based decks exist on the form — otherwise there are
				// no sets to exclude and the button would be meaningless.
				const hasObservationSetDecks = Object.values(this.pict.AppData.ObservationCardDeckProvider.dynamicSettingsMap).some((settings) => settings.useObservationSet);
				if (!hasObservationSetDecks)
				{
					return '';
				}
			}
			// add a toggle switch for excluding observation sets so users can flip between views in real time
			const isExcluding = !!currentSettings.excludeObservationSets;
			const toggleButton = document.createElement('button');
			toggleButton.type = 'button';
			toggleButton.classList.add('button', 'is-ghost', 'is-flex', 'is-align-items-center', 'gap-1', 'ml-1', 'px-2');
			toggleButton.style.fontSize = '0.85em';
			toggleButton.style.whiteSpace = 'nowrap';
			toggleButton.style.textDecoration = 'none';
			toggleButton.setAttribute('data-input-hash', pInput.Hash);
			toggleButton.setAttribute('data-raw-html-id', pInput.Macro.RawHTMLID);
			toggleButton.setAttribute('data-excluding', isExcluding ? 'true' : 'false');
			if (isAlwaysExcluding)
			{
				// Locked on — show as permanently active with no click handler
				toggleButton.appendChild(this.buildIconLabelNodes('fa fa-fw fa-toggle-on has-text-link', 'Exclude Observation Sets'));
				toggleButton.setAttribute('disabled', 'disabled');
				toggleButton.style.opacity = '0.7';
				toggleButton.style.cursor = 'default';
			}
			else
			{
				toggleButton.appendChild(this.buildIconLabelNodes(`fa fa-fw ${ isExcluding ? 'fa-toggle-on has-text-link' : 'fa-toggle-off has-text-grey' }`, 'Exclude Observation Sets'));
				toggleButton.addEventListener('click', () => this.handleExcludeObservationSetsToggle(toggleButton));
			}
			headerContainerElement.appendChild(toggleButton);
			return '';
		}
		// make a deep copy of the filters so we don't mutate the original object when we manipulate the label types for display
		const filterCopy = JSON.parse(JSON.stringify(currentFilters));
		const headerContainer = this.getCardDeckHeaderFilterContainerHTMLID(pInput.Macro.RawHTMLID);
		/**
		 * @type {HTMLSpanElement[]}
		 */
		const tmpFiltersTags = [];
		/** @type {HTMLElement} */
		const headerContainerElement = document.querySelector(headerContainer);
		if (!headerContainerElement)
		{
			this.pict.log.error(`Could not find header container element with ID ${ headerContainer } to build modifier tags for header.`);
			return '';
		}
		headerContainerElement.classList.add('is-flex', 'is-align-items-left', 'gap-1');
		// for each modifier type (include/exclude), we want to create a tag for each modifier that is applied, and color code it green for include and red for exclude.
		let modifierCount = 0;
		Object.entries(filterCopy).forEach(([ type ])=>
		{
			// skip the internal default-filter flag — it's not a real filter direction
			if (type === '_isDefaultTypeFilter')
			{
				return;
			}
			const joinType = type; // include or exclude
			for (let [ key, value ] of Object.entries(filterCopy[type]))
			{
				// Label Types are nested objects and we need to pull out the tags to be [LabelType]: [LabelValue1, LabelValue2] instead of just showing the raw object in the tag, which isn't helpful for users to understand what the modifier is
				if (key === 'LabelTypes')
				{
					value = value.map((/** @type {any} */ v)=>
					{
						for (const [ labelType, labelValues ] of Object.entries(v))
						{
							if (labelType.toLowerCase() === 'type')
							{
								v.Type = labelValues;
							}
							else if (labelType.toLowerCase() === 'value')
							{
								v.Values = labelValues;
							}
						}
						return `${ v.Type }: ${ v.Values.join(', ') }`;
					});
				}
				else
				{
					value = Array.isArray(value) ? value?.join(', ') : value;
					const tmpKey = key === 'ObservationTypes' ? 'Type' : key; // just to make it a little more user friendly in the tag
					const tmpValue = value.replace(/Legacy\s*/g, ''); // strip all "Legacy " prefixes from observation type names for cleaner display (e.g. "Legacy Image" → "Image")
					value = `${ tmpKey }: ${ tmpValue }`;
				}
				modifierCount++;
				const tag = document.createElement('span');
				tag.classList.add('tag', 'pl-1', 'mr-1', joinType.toLowerCase() === 'include' ? 'is-success' : 'is-danger', 'is-light');
				const tagIconWrapper = document.createElement('span');
				tagIconWrapper.classList.add('icon', 'mr-0');
				const tagIcon = document.createElement('i');
				tagIcon.classList.add('fa', 'fa-fw', joinType.toLowerCase() === 'include' ? 'fa-plus-circle' : 'fa-minus-circle');
				tagIconWrapper.appendChild(tagIcon);
				const tagText = document.createElement('span');
				tagText.classList.add('icon-text');
				// value is derived from user-influenced filter/label data — set as text, not markup
				tagText.textContent = modifierCount > tagDisplayLimit ? `${ modifierCount }+ modifiers applied` : value;
				tag.appendChild(tagIconWrapper);
				tag.appendChild(tagText);
				tmpFiltersTags.push(tag);
			}
		});
		// clear out any existing tags in the header container before we add the new ones so we don't end up with duplicates when the modifiers change
		headerContainerElement.innerHTML = '';
		// if we have more than tagDisplayLimit modifier tags, we should just show a summary tag instead of overwhelming the user with a million tags in the header
		// we will show them the summary of modifiers on hover with a tooltip for more info
		if (modifierCount > tagDisplayLimit)
		{
			const summaryTag = document.createElement('span');
			summaryTag.classList.add('tag', 'pl-1', 'is-info', 'is-light');
			const summaryIconWrapper = document.createElement('span');
			summaryIconWrapper.classList.add('icon', 'mr-0');
			const summaryIcon = document.createElement('i');
			summaryIcon.classList.add('fa', 'fa-fw', 'fa-info-circle');
			summaryIcon.setAttribute('tooltip', tmpFiltersTags.map((tag) => tag.innerText).join(', '));
			summaryIconWrapper.appendChild(summaryIcon);
			const summaryText = document.createElement('span');
			summaryText.classList.add('icon-text');
			summaryText.textContent = `${ modifierCount } modifiers applied`;
			summaryTag.appendChild(summaryIconWrapper);
			summaryTag.appendChild(summaryText);
			headerContainerElement.appendChild(summaryTag);
		}
		else
		{
			tmpFiltersTags.forEach((tag)=>
			{
				headerContainerElement.appendChild(tag);
			});
		}
		// When alwaysExcludeObservationSets is set, append the locked-on toggle after the filter tags
		// so it's always visible regardless of which filters are applied.
		if (currentSettings.alwaysExcludeObservationSets)
		{
			const toggleButton = document.createElement('button');
			toggleButton.type = 'button';
			toggleButton.classList.add('button', 'is-ghost', 'is-flex', 'is-align-items-center', 'gap-1', 'ml-1', 'px-2');
			toggleButton.style.fontSize = '0.85em';
			toggleButton.style.whiteSpace = 'nowrap';
			toggleButton.style.textDecoration = 'none';
			toggleButton.style.opacity = '0.7';
			toggleButton.style.cursor = 'default';
			toggleButton.setAttribute('disabled', 'disabled');
			toggleButton.setAttribute('data-excluding', 'true');
			toggleButton.appendChild(this.buildIconLabelNodes('fa fa-fw fa-toggle-on has-text-link', 'Exclude Observation Sets'));
			headerContainerElement.appendChild(toggleButton);
		}
		return '';
	}

	/**
	 * Returns the MediaRegistry namespaces for observation set card decks that are currently rendered on the DOM.
	 * This is used to scope the "exclude observation sets" logic so that only observations visible in other set-based decks
	 * on the form are excluded, rather than excluding observations from every set in the NamespaceObservationSetMap.
	 * @returns {Array<string>} - Array of MediaRegistry namespace strings for observation set decks on the DOM.
	 */
	getObservationSetNamespacesOnDOM()
	{
		if (typeof document === 'undefined')
		{
			return [];
		}
		const namespaces = [];
		const setDecksOnDOM = document.querySelectorAll(this.packageTypeSelectorMap['AllObservationSets']);
		for (const deckElement of setDecksOnDOM)
		{
			const inputHash = deckElement.getAttribute(this.deckInputHashAttribute);
			if (!inputHash)
			{
				continue;
			}
			// get the RawHTMLID from the container element's ID (format: CARD-DECK-CONTAINER-FOR-{RawHTMLID})
			const containerElement = deckElement.closest('[id^="CARD-DECK-CONTAINER-FOR-"]');
			if (!containerElement)
			{
				continue;
			}
			const rawHTMLID = containerElement.id.replace('CARD-DECK-CONTAINER-FOR-', '');
			// read the IDObservationSet from the hidden input element
			const hiddenInput = /** @type {HTMLInputElement} */ (document.getElementById(rawHTMLID));
			const tmpIDObservationSet = hiddenInput?.value ? parseInt(hiddenInput.value) : null;
			// build the namespace the same way the marshal flow does
			if (tmpIDObservationSet)
			{
				namespaces.push(this.buildNamespaceForCardDeck(inputHash, tmpIDObservationSet));
			}
			else
			{
				// temporary set that hasn't been saved yet
				namespaces.push(this.buildNamespaceForCardDeck(inputHash, null, true));
			}
		}
		return namespaces;
	}

	/**
	 * Resolves the list of set-based deck namespaces to use when filtering observations for an exclude-sets deck.
	 * Prefers DOM-discovered set decks (accurate to what is currently rendered on this form), but falls back to the
	 * persisted NamespaceObservationSetMap when the DOM list is empty — this is important on cold reload, where
	 * document-list decks can marshal before set-based decks have applied their DOM marker attributes. Without the
	 * fallback, the deck would rebuild the registry with the full (unfiltered) document observation list on every reload.
	 * @returns {Array<string>|null} - An array of namespaces, or null to signal "use everything in NamespaceObservationSetMap".
	 */
	getObservationSetNamespacesForExcludeFiltering()
	{
		const domNamespaces = this.getObservationSetNamespacesOnDOM();
		if (domNamespaces.length)
		{
			return domNamespaces;
		}
		// null signals to MediaRegistry.getDocumentObservationsExcludingCurrentSets to use all persisted set namespaces
		return null;
	}

	/**
	 * Handles the toggle for excluding observation sets from the card deck. This is called when the user clicks the "Exclude Observation Sets" toggle button in the header.
	 * It updates the dynamic settings for the deck and triggers a remarshal to refresh the deck contents in real time.
	 * @param {HTMLButtonElement} pToggleButton - The toggle button element that was clicked.
	 */
	handleExcludeObservationSetsToggle(pToggleButton)
	{
		const inputHash = pToggleButton.getAttribute('data-input-hash');
		const rawHTMLID = pToggleButton.getAttribute('data-raw-html-id');
		// look up the view hash from the deck container at click time (it may not be available at build time)
		const deckContainerElement = rawHTMLID ? document.querySelector(`${this.getCardDeckContainerHTMLID(rawHTMLID)} .${this.deckClassName}`) : null;
		const viewHash = deckContainerElement?.getAttribute(this.deckViewHashAttribute);
		if (!inputHash || !viewHash || !rawHTMLID)
		{
			this.pict.log.error('Could not find input hash, view hash, or raw HTML ID on the exclude observation sets toggle element. Cannot toggle setting.');
			return;
		}
		const currentSettings = this.pict.AppData.ObservationCardDeckProvider.dynamicSettingsMap[inputHash];
		if (!currentSettings)
		{
			this.pict.log.error(`Could not find settings for input hash ${inputHash}. Cannot toggle excludeObservationSets.`);
			return;
		}
		// flip the current state
		const newValue = pToggleButton.getAttribute('data-excluding') !== 'true';
		const tmpView = this.pict.views[viewHash];
		const pInput = tmpView?.getInputFromHash(inputHash);
		// persist the override to FormData so it survives save/reload and remarshals within this session
		if (pInput)
		{
			this.setPersistedOverrideForInput(pInput, 'excludeObservationSets', newValue);
		}
		currentSettings.excludeObservationSets = newValue;
		// update the exclude hidden input so it stays in sync with the toggle state for reference
		const hiddenInputElement = this.pict.ContentAssignment.getElement(this.getCardDeckHeaderExcludeObservationSetHiddenInputHTMLID(rawHTMLID))?.[0];
		if (hiddenInputElement)
		{
			hiddenInputElement.value = newValue ? 'true' : 'false';
		}
		// show a loading indicator immediately so the user sees feedback during the multi-second async rebuild:
		// clear the deck body, drop in the spinner, and mark the toggle button as loading
		this.showLoadingStateForDeck(rawHTMLID);
		pToggleButton.classList.add('is-loading');
		pToggleButton.setAttribute('disabled', 'disabled');
		if (tmpView && pInput)
		{
			// dataChanged() triggers marshalToView() (which rebuilds the deck) and notifies FormPersistence for autosave
			tmpView.dataChanged(inputHash);
		}
		// explicitly trigger a document autosave so the FormData-persisted override reaches the server even when
		// no bound input value actually changed (dataChanged only dirties the input record via Informary)
		if (this.pict.AppData?.DocumentData?.ReportData?.IDDocument && this.pict.providers?.DocumentProvider?.handleSave)
		{
			this.pict.providers.DocumentProvider.handleSave(true);
		}
	}

	/**
	 * Clears the deck body and injects the initial loading spinner, signaling to the user that a rebuild is in flight.
	 * Used when the deck content is about to change due to a user action (e.g. toggling excludeObservationSets) so there is visible feedback during the async rebuild.
	 * @param {string} pInputHTMLID - The raw HTML ID of the deck input.
	 */
	showLoadingStateForDeck(pInputHTMLID)
	{
		const cardDeckContentAreaElement = document.querySelector(this.getCardDeckContentHTMLID(pInputHTMLID));
		if (cardDeckContentAreaElement)
		{
			const tmpLoadingMessage = document.createElement('span');
			tmpLoadingMessage.className = 'initial-loading-message';
			const tmpSpinnerIcon = document.createElement('i');
			tmpSpinnerIcon.className = 'fa fa-fw fa-circle-notch fa-spin';
			tmpLoadingMessage.appendChild(tmpSpinnerIcon);
			// this container is looked up from the DOM, not freshly created, so clear it as the assignment did
			cardDeckContentAreaElement.textContent = '';
			cardDeckContentAreaElement.appendChild(tmpLoadingMessage);
		}
	}

	/**
	 * Build the observation card deck for a given input.
	 * @param {Object} pView - the view object for which to build the observation card deck (used to get the current view hash for storing in data attributes on the cards for reference in interactivity, and for logging purposes)
	 * @param {Object} pInput - the input object for which to build the observation card deck (used to get the raw HTML ID for the input, which we use to find the container element for the deck and to store the current namespace for the deck in a data attribute on the input element)
	 * @param {string} pNamespace - the namespace for the observation list
	 * @param {any[]} pObservationList - the list of observations to display in the card deck
	 */
	buildObservationCardDeck(pView, pInput, pNamespace, pObservationList)
	{
		const deckContent = this.pict.ContentAssignment.getElement(this.getCardDeckContentHTMLID(pInput.Macro.RawHTMLID))?.[0];
		const deckContainerElement = this.pict.ContentAssignment.getElement(this.getCardDeckContainerHTMLID(pInput.Macro.RawHTMLID) + (` .${this.deckClassName}`))?.[0];

		if (!deckContent || !deckContainerElement)
		{
			this.pict.log.error(`Could not find deck container elements for input with HTML ID ${pInput.Macro.RawHTMLID}. Cannot build observation card deck.`);
			return;
		}
		// apply the deck layout style as a class to the container for styling purposes (default is 'thumbnail', but can be configured through options to be 'thumbnail', 'list', or 'details')
		const currentSettings = this.getCurrentSettingsForInput(pInput);
		const deckLayout = currentSettings?.deckLayout || 'thumbnail';
		deckContent.classList.add(`observation-deck-style-${deckLayout}`);
		// sync the toggle button active state to match the configured deck layout (template defaults to thumbnail being active)
		if (deckLayout !== 'thumbnail')
		{
			const toggleButtons = deckContainerElement.querySelectorAll('.observation-card-deck-style-toggle button');
			toggleButtons.forEach((/** @type {HTMLElement} */ btn) =>
			{
				btn.classList.remove('is-active');
				if (btn.dataset.deckStyle === deckLayout)
				{
					btn.classList.add('is-active');
				}
			});
		}
		// container marker attributes (view/input hash + deck type markers) are applied by prepareUIBeforeBuildingCardDeck via applyDeckContainerMarkerAttributes
		// surgical DOM diff: only add/remove cards that actually changed to avoid flickering existing images
		const desiredIDs = pObservationList.map((/** @type {any} */ o) => String(o.IDObservation));
		const existingCards = Array.from(deckContent.querySelectorAll(`.${this.cardClassName}`));
		const existingIDMap = new Map();
		for (const card of existingCards)
		{
			existingIDMap.set(card.getAttribute(this.cardAttributeId), card);
		}
		// remove cards that are no longer in the observation list
		for (const [ id, card ] of existingIDMap)
		{
			if (!desiredIDs.includes(id))
			{
				card.remove();
				existingIDMap.delete(id);
			}
		}
		// add new cards and reorder all cards to match pObservationList
		let previousSibling = null;
		for (const [ index, observation ] of pObservationList.entries())
		{
			const obsID = String(observation.IDObservation);
			let card = existingIDMap.get(obsID);
			if (!card)
			{
				// new card — build and make individually draggable
				card = this.buildObservationCard(pView, pInput, observation, index);
				this.pict.providers.DraggableUtil.makeElementDraggable(card, this.draggableConfigurationForDeck);
			}
			// position the card correctly (move existing or insert new)
			const nextSibling = previousSibling ? previousSibling.nextSibling : deckContent.firstChild;
			if (card !== nextSibling)
			{
				deckContent.insertBefore(card, nextSibling);
			}
			previousSibling = card;
		}
		// on first render (deck was empty), initialize draggable on the full collection
		if (existingCards.length === 0)
		{
			_Pict.providers.DraggableUtil.initializeDraggableCollection(deckContent, this.draggableConfigurationForDeck);
		}
		// update index numbers and remove loading skeletons
		this.updateIndexNumbersOnUIForDeck(pInput, pObservationList);
		this.removeInitialLoadingMessage(pInput);
	}

	/**
	 * Function to update the index numbers that show up on top of the card thumbnails in the deck based on the current order of the observations in the deck on the DOM.
	 * @param {Object} pInput - the input object for which to update the index numbers (used to get the raw HTML ID for the input, which we use to find the card elements in the deck and to get the current options for the deck)
	 * @param {any[]} [pOrderedIDObservationList] - the list of observation IDs in the current order they are displayed in the deck, which we can use to update the index numbers on the cards to reflect their current position in the deck (if not provided, we will just use the order of the card elements in the DOM to update the index numbers)
	*/
	updateIndexNumbersOnUIForDeck(pInput, pOrderedIDObservationList)
	{
		const currentSettings = this.getCurrentSettingsForInput(pInput);
		const indexTagScope = currentSettings.indexTagScope || 'local';
		// only update local indices when the scope is 'local' — document-scoped decks will be handled by updateDocumentIndexNumbers below
		if (indexTagScope === 'local')
		{
			const cardElements = document.querySelectorAll(this.getCardDeckContentHTMLID(pInput.Macro.RawHTMLID) + ` .${this.cardClassName}`);
			cardElements.forEach((card, index) =>
			{
				const obsID = card.getAttribute(this.cardAttributeId);
				const obsIndex = pOrderedIDObservationList?.length ? pOrderedIDObservationList.findIndex((/** @type {any} */ o) => o.IDObservation == obsID) : index;
				if (obsIndex !== -1)
				{
					const indexTag = card.querySelector('.card-index');
					if (indexTag)
					{
						indexTag.textContent = (obsIndex + 1).toString();
					}
				}
			});
		}
		// always run the document-level index update — it will only affect decks configured with indexTagScope: 'document'
		this.updateDocumentIndexNumbers();
	}

	/**
	 * Resolves the current settings for a deck by looking up the input hash stored on the deck's parent container element.
	 * @param {Element} pDeckBodyElement - a `.observation-card-deck-body` element
	 * @returns {Object|null} - the settings object for the deck's input, or null if the input could not be resolved
	 */
	_getSettingsForDeckElement(pDeckBodyElement)
	{
		const deckContainer = pDeckBodyElement?.closest(`.${this.deckClassName}`);
		if (!deckContainer)
		{
			return null;
		}
		const inputHash = deckContainer.getAttribute(this.deckInputHashAttribute);
		if (!inputHash)
		{
			return null;
		}
		return this.pict.AppData.ObservationCardDeckProvider.dynamicSettingsMap[inputHash] || null;
	}

	/**
	 * Updates index numbers across ALL card decks on the page that are configured with `indexTagScope: 'document'`.
	 * Cards are numbered sequentially based on their visual DOM order from top to bottom of the page.
	 */
	updateDocumentIndexNumbers()
	{
		if (typeof document === 'undefined')
		{
			return;
		}
		const allDeckBodies = document.querySelectorAll('.observation-card-deck-body');
		if (!allDeckBodies.length)
		{
			return;
		}
		let documentIndex = 1;
		allDeckBodies.forEach((deckBody) =>
		{
			const settings = this._getSettingsForDeckElement(deckBody);
			const indexTagScope = settings?.indexTagScope || 'local';
			if (indexTagScope !== 'document')
			{
				return;
			}
			const cardElements = deckBody.querySelectorAll(`.${this.cardClassName}`);
			cardElements.forEach((card) =>
			{
				const indexTag = card.querySelector('.card-index');
				if (indexTag)
				{
					indexTag.textContent = documentIndex.toString();
				}
				documentIndex++;
			});
		});
	}

	/**
	 * Set the observations for the deck based on a provided list of observations. This will overwrite any existing observations in the deck.
	 * NOTE: This is only used for Interactive mode, there is a separate function for building the deck in Print called "buildCardDeckCardsForPrintView()"
	 * @param {any} pView - the pict-view instance hosting the input
	 * @param {any} pInput - the input object for which to build the observation card
	 * @param {Object} pObservation - the observation object to set for the deck (should have the necessary properties for display in the deck)
	 * @param {number} pIndex - the index of the observation in the deck (used to determine the ordinal number for display)
	 * @returns {HTMLElement|undefined} - returns the card template of the observation
	 */
	buildObservationCard(pView, pInput, pObservation, pIndex)
	{
		if (!this.pict.AppData.Interactive)
		{
			this.pict.log.error(`Attempted to build an observation card for a non-interactive document, which is not supported. Please use the buildCardDeckCardsForPrintView function for rendering the deck in print view instead, which is optimized for non-interactive documents.`);
			return;
		}
		if (pObservation?.IDObservation === undefined)
		{
			this.pict.log.error(`Observation object is missing IDObservation property or it is undefined. Cannot build card for observation: ${ JSON.stringify(pObservation) }`);
			return;
		}
		// don't add duplicates
		if (document.querySelector(this.getCardDeckContentHTMLID(pInput.Macro.RawHTMLID) + ` .${this.cardClassName}[${this.cardAttributeId}="${pObservation.IDObservation}"]`))
		{
			return;
		}
		const cardElement = document.createElement('div');
		cardElement.setAttribute(this.cardAttributeId, pObservation.IDObservation);
		cardElement.classList.add(this.cardClassName);
		cardElement.innerHTML = this.buildObservationCardMarkup(pView, pInput, pObservation, pIndex);
		return cardElement;
	}

	/**
	 * Builds an observation card's inner markup: the compact layout (thumbnail and list deck styles) and the details layout, toggled by CSS.
	 * @param {any} pView - the pict-view instance hosting the input
	 * @param {any} pInput - the input the card belongs to
	 * @param {Object} pObservation - the observation the card shows
	 * @param {number} pIndex - the card's position in the deck (shown as its ordinal)
	 * @returns {string} - the card's inner markup
	 */
	buildObservationCardMarkup(pView, pInput, pObservation, pIndex)
	{
		const currentSettings = this.getCurrentSettingsForInput(pInput);
		const tmpOrdinal = pIndex + 1; /* we want the number in the UI to be local to the set, not the Ordinal itself since that can be global and not reflect the current order in the deck if there are other sets of observations in the document */

		const MAX_DESCRIPTION_LENGTH = 250;

		const hasDescription = typeof pObservation.Description === 'string' && pObservation.Description.trim() !== '';
		let shortDescription = '';
		let fullDescription = '';
		let useReadMore = false;

		if (hasDescription)
		{
			fullDescription = libUserMarkdown.escapeHTML(pObservation.Description);
			if (pObservation.Description.length > MAX_DESCRIPTION_LENGTH)
			{
				shortDescription = libUserMarkdown.escapeHTML(pObservation.Description.slice(0, MAX_DESCRIPTION_LENGTH)) + '...';
				useReadMore = true;
			}
			else
			{
				shortDescription = fullDescription;
			}
		}

		let removeButtonTemplate = '';
		// if the document is locked, we can still show the image, but we can't remove it from the set
		if (!currentSettings.preventRemovingCards && !this.isDocumentLocked && this.canEditDocument)
		{
			removeButtonTemplate = `<button type="button" class="delete card-delete"
				onclick="_Pict.providers.ObservationCardDeckProvider.removeCardFromDeck('${ pView.Hash }', '${ pInput.Hash }', ${ pObservation.IDObservation })"></button>`;
		}

		// format designated time and creator name for the details view
		const tmpDesignatedDate = pObservation.DesignatedTime ? new Date(pObservation.DesignatedTime).toLocaleDateString() : '';
		const tmpDesignatedTime = pObservation.DesignatedTime ? new Date(pObservation.DesignatedTime).toLocaleTimeString() : '';
		// resolve creator name from the user name cache (populated asynchronously by resolveCreatorNamesForDeck)
		const userNameCache = this.pict.AppData.ObservationCardDeckProvider?.userNameCache || {};
		const tmpCreatorName = userNameCache[pObservation.CreatingIDUser] || '';

		// Compact layout — used by thumbnail and list deck styles (toggled purely via CSS)
		const compactLayout =
		`<article class="media card-layout-compact" style="position: relative;">
			<span class="${ currentSettings.hideIndexTags ? 'is-hidden' : '' } card-index badge is-info">${ tmpOrdinal }</span>
			${ removeButtonTemplate }
			<figure class="image ${this.cardClassName}-content has-background-text-soft is-128x128 media-left">
				<img loading="lazy" class="photo-image is-clickable" height="128px" width="128px" src="/1.0/Observation/${ pObservation.IDObservation }/Image/Thumbnail-Dense?t=${ new Date().getTime() }"
					onclick="_Pict.providers.ObservationCardDeckProvider.handleClickForCardThumbnail(event, '${ pView.Hash }', '${ pInput.Hash }', ${ pObservation.IDObservation })" />
			</figure>
			<div class="media-content">
				<div class="content pr-5">
					<strong class="title is-size-6">${ libUserMarkdown.escapeHTML(pObservation.Name || `Observation #${pObservation.IDObservation}`) }</strong>
					<p class="subtitle is-size-7 has-text-text-30">
						${ shortDescription || '<i class="has-text-text-50">No description</i>' }
						${ useReadMore ? `<a href="javascript:void(0);" onclick="_Pict.providers.ObservationCardDeckProvider.handleClickForCardThumbnail(event, '${ pView.Hash }', '${ pInput.Hash }', ${ pObservation.IDObservation })">Read More</a>` : '' }
					</p>
				</div>
				<span class="${ currentSettings.preventReorderingCards ? 'is-hidden' : '' } card-drag-handle icon has-text-text-50">
					<i class="fa fa-fw fa-grip"></i>
				</span>
			</div>
		</article>`;

		// Build label tags for the details view from the raw observation Labels object
		let labelTagsHtml = '';
		if (pObservation.Labels && typeof pObservation.Labels === 'object')
		{
			const MAX_LABEL_TAGS = 6; // truncate to first few to keep the card compact
			let tagCount = 0;
			let truncated = false;
			const tagParts = [];
			for (const [ labelTypeKey, labelObjects ] of Object.entries(pObservation.Labels))
			{
				if (labelTypeKey === 'SAVE') continue;
				const items = Array.isArray(labelObjects) ? labelObjects : [ labelObjects ];
				for (const labelObj of items)
				{
					if (!labelObj?.Tag || !labelObj.Tag.length) continue;
					if (tagCount >= MAX_LABEL_TAGS)
					{
						truncated = true; break;
					}
					tagParts.push(`<span class="tag is-small is-rounded tag-outlined-primary">${ libUserMarkdown.escapeHTML(labelTypeKey) }: ${ libUserMarkdown.escapeHTML(labelObj.Tag) }</span>`);
					tagCount++;
				}
				if (truncated) break;
			}
			if (tagParts.length)
			{
				labelTagsHtml = `
				<div class="card-details-row">
					<div class="card-details-field" style="grid-column: span 2;">
						<div class="title is-7 has-text-text-50 mb-1">Labels</div>
						<div class="card-details-labels">${ tagParts.join(' ') }${ truncated ? '<span class="tag is-small is-rounded has-text-text-50">...</span>' : '' }</div>
					</div>
				</div>`;
			}
		}

		// Details layout — postcard-style with image on the left and observation metadata on the right (similar to print standard card)
		const detailsLayout =
		`<article class="card-layout-details" style="position: relative;">
			<span class="${ currentSettings.hideIndexTags ? 'is-hidden' : '' } card-index badge is-info">${ tmpOrdinal }</span>
			${ removeButtonTemplate }
			<span class="${ currentSettings.preventReorderingCards ? 'is-hidden' : '' } card-drag-handle icon has-text-text-50">
				<i class="fa fa-fw fa-grip"></i>
			</span>
			<div class="card-details-grid">
				<figure class="card-details-image has-background-text-soft">
					<img loading="lazy" class="photo-image is-clickable" src="/1.0/Observation/${ pObservation.IDObservation }/Image/Preview?t=${ new Date().getTime() }"
						onclick="_Pict.providers.ObservationCardDeckProvider.handleClickForCardThumbnail(event, '${ pView.Hash }', '${ pInput.Hash }', ${ pObservation.IDObservation })" />
				</figure>
				<div class="card-details-metadata">
					<div class="card-details-header has-background-text-soft px-3 py-2">
						<strong class="title is-6 mb-0">${ libUserMarkdown.escapeHTML(pObservation.Name || `Observation #${pObservation.IDObservation}`) }</strong>
						<span class="subtitle is-7 has-text-text-30">#${ pObservation.IDObservation }</span>
					</div>
					<div class="card-details-body px-3 py-2">
						<div class="card-details-row">
							<div class="card-details-field">
								<div class="title is-7 has-text-text-50 mb-1">Created by</div>
								<div class="subtitle is-7 card-details-creator-name" data-creator-id="${ pObservation.CreatingIDUser || '' }">${ libUserMarkdown.escapeHTML(tmpCreatorName) || 'N/A' }</div>
							</div>
							<div class="card-details-field">
								<div class="title is-7 has-text-text-50 mb-1">Designated Time</div>
								<div class="subtitle is-7">${ tmpDesignatedDate }${ tmpDesignatedTime ? ' at ' + tmpDesignatedTime : '' }</div>
							</div>
						</div>
						<div class="card-details-row">
							<div class="card-details-field" style="grid-column: span 2;">
								<div class="title is-7 has-text-text-50 mb-1">Description</div>
								<div class="subtitle is-7">${ fullDescription || '<span class="has-text-text-50">No description provided</span>' }</div>
							</div>
						</div>
						${ labelTagsHtml }
					</div>
				</div>
			</div>
		</article>`;

		return compactLayout + detailsLayout;
	}

	/**
	 * Rebuild every card showing the given observation, so a change saved elsewhere (the Observation Details panel's Edit details, a File selector Rename)
	 * shows without reopening the form. Each card element stays in place, keeping its drag listeners and position; a deck marshal can't do this, because it
	 * keeps cards that are already on the deck as they are. Called by ObservationProvider.applyUpdatedObservation.
	 * @param {Object} pObservation - the observation record the server returned
	 */
	refreshObservationCards(pObservation)
	{
		if (!pObservation || !pObservation.IDObservation || !this.pict.AppData.Interactive)
		{
			return;
		}
		const tmpCards = document.querySelectorAll(`.${this.deckClassName} .${this.cardClassName}[${this.cardAttributeId}="${pObservation.IDObservation}"]`);
		for (const tmpCard of tmpCards)
		{
			const tmpDeck = tmpCard.closest(`.${this.deckClassName}`);
			const tmpView = this.pict.views[tmpDeck.getAttribute(this.deckViewHashAttribute)];
			const tmpInput = tmpView ? tmpView.getInputFromHash(tmpDeck.getAttribute(this.deckInputHashAttribute)) : null;
			if (!tmpInput)
			{
				continue;
			}
			const tmpIndex = (parseInt(tmpCard.querySelector('.card-index')?.textContent, 10) || 1) - 1;
			tmpCard.innerHTML = this.buildObservationCardMarkup(tmpView, tmpInput, pObservation, tmpIndex);
		}
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.pict.log.info(`Marshaling data to form for Observation Card Deck input with value: ${ pValue } and HTML selector: ${ pHTMLSelector }`);
		const tmpMarshalPromise = this._onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector)
		.catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		this.marshalPromises.push(tmpMarshalPromise);
		// EVERY marshal gates print rendering. This used to register only the first marshal per input hash, on the
		// assumption that it already covered the deck build — it does not. prepareUIBeforeBuildingCardDeck fires the
		// async print build separately, so a later marshal's build could begin after completeRender() had already
		// drained, and the rasterizer would snapshot an empty deck. registerRenderDependency is a no-op in interactive
		// mode, so this only accumulates across the finite marshal cycle of a print render.
		if (!this.registeredDependencyInputHashList.includes(pInput.Hash))
		{
			this.registeredDependencyInputHashList.push(pInput.Hash);
		}
		this.registerRenderDependency(tmpMarshalPromise);
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Custom CSS injection for the observation card deck. This is needed to apply specific styles to the deck and its cards that are not covered by the primary CSS collection, especially during development when we are iterating on the design and functionality of the deck and may need to make frequent updates to the styles without worrying about caching issues. We will eventually migrate these styles to the primary CSS collection, but for now this allows us to develop the functionality and styles in parallel.
	 * @param {Object} currentSettings - the current settings for the deck, which can be used to conditionally apply styles based on the configuration of the deck (ex: different styles for print view vs interactive view, or based on the selected display style for the deck)
	 */
	injectCustomCSS(currentSettings)
	{
		if (this.customCSSInjected || document.getElementById('ObservationCardDeck-Styles')) return;

		const styleElement = document.createElement('style');
		styleElement.id = 'ObservationCardDeck-Styles';
		styleElement.textContent = /*CSS*/`
			/* Observation Card Deck (Collection) specific styles */
			.observation-card-deck {
				/* NOTE: Would be nice if we could add an extension for Bulma cards instead of this duplication... */
				/* duplication of the ".card" class from Bulma, but renamed to avoid confusion with our Card elements inside the deck which aren't necessarily Bulma cards and to allow for more customization specific to the deck container itself */
				--bulma-card-color: var(--bulma-text);
				--bulma-card-background-color: var(--bulma-scheme-main);
				--bulma-card-shadow: var(--bulma-shadow);
				--bulma-card-radius: 0.75rem;
				--bulma-card-header-background-color: transparent;
				--bulma-card-header-color: var(--bulma-text-strong);
				--bulma-card-header-padding: 0.75rem 1rem;
				--bulma-card-header-shadow: 0 0.125em 0.25em hsla(var(--bulma-scheme-h), var(--bulma-scheme-s), var(--bulma-scheme-invert-l), 0.1);
				--bulma-card-header-weight: var(--bulma-weight-bold);
				--bulma-card-content-background-color: transparent;
				--bulma-card-content-padding: 1.5rem;
				--bulma-card-media-margin: var(--bulma-block-spacing);
				background-color: var(--bulma-card-background-color);
				border-radius: var(--bulma-card-radius);
				box-shadow: var(--bulma-card-shadow);
				color: var(--bulma-card-color);
				max-width: 100%;
				position: relative;
				margin-top: 0.5rem;
				margin-bottom: 0.5rem;
			}
			/* initialization message to show that something is happening */
			.observation-card-deck .initial-loading-message {
				vertical-align: middle;
				color: var(--bulma-text-soft);
				text-align: center;
				width: 100%;
				display: block;
				font-size: 3.25rem;
				padding: 2rem 0 1rem 0;
			}
			/* specific styles for the observation cards inside the deck */
			.observation-card-deck .observation-card .card-delete {
				display: none;
			}
			.observation-card-deck .observation-card:hover .card-delete {
				display: block;
			}
			.observation-card-deck.deck-filters-applied .observation-card .card-delete,
			.observation-card-deck.deck-filters-applied .observation-card:hover .card-delete {
				display: none;
			}
			.observation-card-deck .observation-card .badge.card-index {
				position: absolute; 
				top: 12px; 
				left: -4px; 
				z-index: 1; 
				right: auto;
			}
			.observation-card-deck .observation-card .card-delete {
				position: absolute; 
				top: 2px; 
				right: 2px; 
				z-index: 1;
				background-color: var(--bulma-danger-50);
				opacity: 0.8;
			}
			.observation-card-deck .observation-card-deck-header {
				border-start-start-radius: var(--bulma-card-radius);
				border-start-end-radius: var(--bulma-card-radius);
				background-color: var(--bulma-card-header-background-color);
				align-items: stretch;
				box-shadow: var(--bulma-card-header-shadow);
				display: flex;
				padding: var(--bulma-card-header-padding);
				position: relative;
				z-index: 1;
				justify-content: space-between;
				flex-wrap: wrap;
				gap: 0.5rem;
				font-weight: var(--bulma-card-header-weight);
				color: var(--bulma-card-header-color);
			}
			.observation-card-deck .observation-card-deck-header .card-deck-header-actions {
				display: flex;
				align-items: center;
				gap: 0.5rem;
				vertical-align: middle;
			}
			.observation-card-deck .observation-card-deck-header .card-deck-header-filters {
				display: flex;
				align-items: center;
				gap: 0.5rem;
				vertical-align: middle;
				/* this is to allow the filter tags to wrap to a new line if there are too many to fit in the header, instead of overflowing and breaking the layout */
				flex-wrap: wrap;
				max-width: 100%;
				justify-content: flex-start;
				flex-grow: 1; /* allow the filters container to grow and take up available space when it wraps */
				/* we also want to make sure that the filters don't get pushed down below the header on smaller screens, so we can set a min-width on the header to prevent that from happening */
				min-width: 300px;
				/* and we want to make sure that the filters don't get too squished on smaller screens, so we can set a max-width on the filters container to prevent that from happening */
				max-width: 100%;
				/* if there are a lot of filters and they wrap to a new line, we want to add some spacing between the header and the filters to visually separate them, so we can add a margin-top to the filters container when it wraps */
				margin-top: 0.5rem;
			}
			.observation-card-deck .observation-card-deck-header .card-deck-header-observation-set-selector {
				display: block;
				align-items: center;
				vertical-align: middle;
				max-width: 100%;
				font-size: 0.9rem;
				flex-grow: 1; /* allow the filters container to grow and take up available space when it wraps */
			}
			.observation-card-deck .observation-card-deck-body,
			.observation-card-deck .observation-card-deck-empty-message {
				gap: calc(var(--bulma-column-gap) * 0.5);
				padding: calc(var(--bulma-column-gap) * 0.75);
				display: flex;
				flex-wrap: wrap;
				position: relative;
				background-color: var(--bulma-background);
				justify-content: flex-start;
				align-items: flex-start;
				border: 1px solid var(--bulma-border);
				border-bottom-left-radius: var(--bulma-card-radius);
				border-bottom-right-radius: var(--bulma-card-radius);
				border-top: none;
				/* no forced min-height — cards dictate the height naturally.
				   The empty-message sibling handles its own min-height when the body is :empty.
				   The initial-loading-message spinner has its own padding to prevent full collapse during load. */
				/* transition for when hovering over cards and the deck resizes due to description popovers showing up and changing the size of the cards */
				transition: all 0.3s ease;
				/* this is to prevent the deck from jumping around when we hover over cards and the popovers show up and change the size of the cards, by allowing the deck to grow in height instead of overflowing and causing a scrollbar to appear */
				overflow: visible;
			}
			.observation-card-deck .observation-card-deck-body:empty {
				display: none;
			}
			.observation-card-deck .observation-card-deck-body:not(:empty) + .observation-card-deck-empty-message {
				display: none;
			}
			.observation-card-deck .observation-card-deck-body:empty + .observation-card-deck-empty-message {
				display: flex;
				align-items: center;
				justify-content: center;
				color: var(--bulma-text-50);
				min-height: 75px;
				font-size: 0.875rem;
			}
			/* Observation Card (Item) specific styles */
			@keyframes observationCardFadeIn {
				from { opacity: 0; }
				to { opacity: 1; }
			}
			.observation-card {
				border: 3px solid transparent;
				border-radius: 6px;
				width: calc(128px + (3px * 2)); /* thumbnail size + border */
				animation: observationCardFadeIn 0.25s ease-in;
			}
			.observation-card img {
				opacity: 1;
				transition: opacity 0.25s ease-in;
				height: 100%;
				width: 100%;
				object-fit: cover;
			}
			.observation-card:hover {
				border: 3px solid var(--bulma-primary);
			}
			.observation-card .card-layout-compact {
				margin: 0;
			}
			.observation-card .observation-card-content-inner {
				display: flex;
				flex-direction: column;
				align-items: center;
				gap: 0.5rem;
			}
			.observation-deck-style-thumbnail .observation-card {
				width: calc(128px + (3px * 2)); /* thumbnail size + border */
			}
			.observation-deck-style-list .observation-card {
				width: 100%;
			}
			.observation-card-deck .media-content {
				display: none;
			}
			.observation-deck-style-list figure.is-128x128 {
				display: flex;
				align-items: flex-start;
				gap: 1rem;
				height: 64px;
				width: 64px;
			}
			.observation-deck-style-list .media {
				border: 1px solid var(--bulma-border);
				padding: 0.5rem;
				border-radius: 4px;
			}
			.observation-deck-style-list .media-content {
				display: block;
			}
			.observation-card-deck .observation-card .media-content .card-drag-handle {
				position: absolute;
				right: 5px;
				top: 50%;
				transform: translateY(-50%);
				z-index: 1;
			}
			.observation-deck-style-list .card-observation-read-more-button {
				display: none;
			}

			/* Details layout — postcard-style view: hide by default, show only in details mode */
			.observation-card-deck .card-layout-details {
				display: none;
			}
			.observation-deck-style-details .card-layout-details {
				display: block;
			}
			.observation-deck-style-details .card-layout-compact {
				display: none;
			}
			.observation-deck-style-details .observation-card {
				width: 100%;
			}
			.card-details-grid {
				display: grid;
				grid-template-columns: 300px 1fr;
				border: 1px solid var(--bulma-border);
				border-radius: 6px;
				overflow: hidden;
				min-height: 300px;
			}
			.card-details-image {
				margin: 0;
				display: flex;
				align-items: center;
				justify-content: center;
				overflow: hidden;
			}
			.card-details-image img {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
			.card-details-header {
				display: flex;
				align-items: baseline;
				gap: 0.5rem;
				border-bottom: 1px solid var(--bulma-border);
			}
			.card-details-row {
				display: grid;
				grid-template-columns: 1fr 1fr;
				gap: 0.5rem;
				margin-bottom: 0.5rem;
			}
			.card-details-row:last-child {
				margin-bottom: 0;
			}
			.card-details-labels {
				display: flex;
				flex-wrap: wrap;
				gap: 0.25rem;
			}
			.tag.tag-outlined-primary {
				background-color: transparent;
				border: 1px solid var(--bulma-primary);
				color: var(--bulma-primary);
			}
			.observation-deck-style-details .card-layout-details .card-drag-handle {
				position: absolute;
				right: 0.75rem;
				top: 0.75rem;
				z-index: 1;
			}

			.observation-card-deck-style-toggle .button.is-ghost {
				color: var(--bulma-text-soft);
			}
			.observation-card-deck-style-toggle .button.is-ghost.is-active {
				color: var(--bulma-text);
			}

			/* Print Only types for PDF viewer */
			.for-print-only .observation-card-deck,
			.for-print-only .observation-card-deck .observation-card-deck-body {
				display: block;
				padding: 0;
				border: none;
				border-radius: 0;
				gap: 0;
				background-color: transparent;
				min-height: auto;
				transition: none;
				overflow: visible;
				page-break-inside: auto;
				box-shadow: none;
				width: 100%;
				max-width: none;
			}
			.for-print-only .observation-card-deck .observation-card {
				page-break-inside: avoid;
				border: none;
				border-radius: 0;
				width: auto;
				margin-bottom: 1rem;
			}
			/* TODO: we might want to show the filters in the header for print as well, but for now we'll hide the header entirely for print since the filters are more for interactive use and don't make as much sense in a static format like PDF */
			.for-print-only .observation-card-deck .observation-card-deck-header {
				display: none;
			}
			.for-print-only .observation-card-deck .observation-card-deck-empty-message {
				border-radius: 5px;
				border: none;
			}
			`;
		document.head.appendChild(styleElement);
		this.customCSSInjected = true;
	};

	/**
	 * Attempts to get the observation set for the deck based on the passed value (expected to be only the Observation Set ID),
	 * or if no passed value, then it will attempt to find the observation set based on the input hash as the namespace.
	 * If that doesn't work, it will attempt to create a new observation set based on the document observations and any filters provided in options.
	 * Finally, it will build the observation card deck based on the observation set it found or created.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 */
	async _onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector)
	{
		if (!this.pict.AppData.DocumentData)
		{
			this.pict.log.error('Document data or document ID is missing from AppData. Cannot marshal data to form for Observation Card Deck.');
			return;
		}
		// always start by making sure our MediaRegistry is up to date with the latest document observation list (in case anything was added externally, etc)
		this.pict.providers.MediaRegistry.updateRegistryFromFormData();
		// blend the default and current settings passed in from PictForm.
		// Persisted user overrides (FormData.ObservationCardDeckOverrides) are applied last inside setCurrentSettingsForInput
		// so toggled preferences like excludeObservationSets survive save/reload and Edit→Print view switches.
		const currentSettings = this.setCurrentSettingsForInput(pInput, pInput?.PictForm?.CardDeckSettings);
		// Keep the exclude hidden input in sync with the resolved setting
		const excludeHiddenInput = this.pict.ContentAssignment.getElement(this.getCardDeckHeaderExcludeObservationSetHiddenInputHTMLID(pInput.Macro.RawHTMLID))?.[0];
		if (excludeHiddenInput)
		{
			excludeHiddenInput.value = currentSettings.excludeObservationSets ? 'true' : 'false';
		}
		this.injectCustomCSS(currentSettings);
		// If we are using sets, we need to check the hidden value or prepare for the possibility of not having a value if the user hasn't selected/created an observation set yet.
		// The selector in the header is optional, and new pristine selectors that are using sets will have a temporary namespace until the first observation is add, which creates the set ID.
		// NOTE: Filters are not used for the IDObservationSet case, because we assume that if an IDObservationSet is provided, then the user has already applied any necessary filters to create that set, and they want to see that specific set in the deck.
		// If we apply additional filters on top of that, we might end up with an empty deck or a different set of observations than the user intended to display.
		currentSettings.useObservationSet ? await this.handleOnDataMarshalForObservationSetCase(pView, pInput, pValue) : await this.handleOnDataMarshalForObservationListCase(pView, pInput);
	}

	/**
	 * Toggles the visibility of the "Add" and "Quick Capture" buttons in the header of the card deck based on the config settings.
	 * @param {Object} pInput - The input object for which to toggle the visibility of the header buttons (used to get the raw HTML ID for the input, which we use to find the header button elements)
	 */
	toggleVisibilityForAddCardHeaderButtons(pInput)
	{
		const currentSettings = this.getCurrentSettingsForInput(pInput);
		const addButton = this.pict.ContentAssignment.getElement(this.getCardDeckHeaderActionsButtonAddHTMLID(pInput.Macro.RawHTMLID))?.[0];
		const quickCaptureButton = this.pict.ContentAssignment.getElement(this.getCardDeckHeaderActionsButtonQuickCaptureHTMLID(pInput.Macro.RawHTMLID))?.[0];
		// when requireExplicitSetCreation is enabled, check whether a set is currently selected —
		// if not, hide Add/QuickCapture so the user must go through "Create New Set" first
		let hasSelectedSet = true;
		if (currentSettings?.requireExplicitSetCreation && currentSettings?.useObservationSetSelector)
		{
			const tmpHiddenValue = this.pict.ContentAssignment.getElement(this.getHiddenInputHTMLID(pInput.Macro.RawHTMLID))?.[0]?.value;
			hasSelectedSet = !isNaN(parseInt(tmpHiddenValue));
		}
		// show the add/qc buttons in the header if we're not preventing adding cards and the document isn't locked and is editable
		const canAddCards = !currentSettings?.preventAddingCards && !this.isDocumentLocked && this.canEditDocument && (!this.hasFiltersApplied(pInput) || this.hasActionableFilters(pInput));
		if (canAddCards)
		{
			if (addButton)
			{
				addButton.classList.remove('is-hidden', 'is-skeleton');
			}
			if (quickCaptureButton)
			{
				quickCaptureButton.classList.remove('is-hidden', 'is-skeleton');
			}
			// when the buttons are visible but no set is selected, show them as disabled instead of hiding
			// so the user can see the actions are available once they create/select a set
			if (!hasSelectedSet)
			{
				addButton?.setAttribute('disabled', 'disabled');
				quickCaptureButton?.setAttribute('disabled', 'disabled');
			}
			else
			{
				addButton?.removeAttribute('disabled');
				quickCaptureButton?.removeAttribute('disabled');
			}
		}
		else
		{
			if (addButton)
			{
				addButton.classList.add('is-hidden');
			}
			if (quickCaptureButton)
			{
				quickCaptureButton.classList.add('is-hidden');
			}
		}
	}

	/**
	 * Handles adjusting the header UI depending on which settings are enabled for the deck, such as showing/hiding the filter tags or the observation set selector
	 * in the header based on whether we're using sets or just a filtered list of observations, and based on the specific settings for what to show in the header.
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {string} pNamespace - The namespace for the deck.
	 */
	handleHeaderContentForCardDeck(pView, pInput, pNamespace)
	{
		// if we are using sets, we do some additional set up in the header
		const currentSettings = this.getCurrentSettingsForInput(pInput);
		const usingSets = currentSettings.useObservationSet;
		const usingSetSelector = currentSettings.useObservationSetSelector;
		const tmpIDObservationSet = usingSets && !pNamespace.startsWith('TEMP-') ? parseInt(pNamespace.split('-').pop()) : null;
		const headerFilterContainer = this.getCardDeckHeaderFilterContainerHTMLID(pInput.Macro.RawHTMLID);
		const headerSetSelectorContainer = this.getCardDeckHeaderObservationSetSelectorContainerHTMLID(pInput.Macro.RawHTMLID);
		const headerStaticObservationSetDisplayContainer = this.getCardDeckHeaderObservationSetDisplayHTMLID(pInput.Macro.RawHTMLID);
		// first let's hide everything, then add back what we need based on the settings
		this.pict.ContentAssignment.addClass(headerFilterContainer, 'is-hidden');
		this.pict.ContentAssignment.addClass(headerSetSelectorContainer, 'is-hidden');
		this.pict.ContentAssignment.addClass(headerStaticObservationSetDisplayContainer, 'is-hidden');

		if (usingSets && usingSetSelector)
		{
			this.buildSetSelectorOptionForCurrentObservationSetID(pInput, tmpIDObservationSet);
			this.pict.ContentAssignment.removeClass(headerSetSelectorContainer, 'is-hidden');
		}
		else if (usingSets)
		{
			// Prefer the user-given Name (pre-populated in the set-name cache by handleOnDataMarshalForObservationSetCase),
			// falling back to "Observation Set {id}" for unnamed/auto-timestamped sets so users don't see ugly default labels.
			const tmpLabel = this.getDisplayLabelForObservationSet(tmpIDObservationSet);
			// Show a pencil-edit affordance when the user can actually rename the set — hidden for locked or read-only documents,
			// and for brand-new (not-yet-saved) sets that don't have an ID to save under yet.
			const canRename = !!tmpIDObservationSet && !this.isDocumentLocked && this.canEditDocument;
			let renameButtonHTML = '';
			if (canRename)
			{
				const renameOnClick = `_Pict.providers.ObservationCardDeckProvider.handleInlineRenameSetClick('${pInput.Macro.RawHTMLID}', ${tmpIDObservationSet})`;
				renameButtonHTML = `<button type="button" class="button is-ghost is-small p-0 ml-1" title="Rename Observation Set" style="height: auto; min-height: 0;" onclick="${renameOnClick}"><span class="icon is-small"><i class="fa fa-fw fa-pencil"></i></span></button>`;
			}
			const staticContent =
			`<div class="tag mt-2 pl-1">
				<span class="icon mr-0">
					<i class="fa fa-fw fa-layer-group"></i>
				</span>
				<span class="icon-text">${tmpLabel}</span>
				${renameButtonHTML}
			</div>`;
			// clear the display container and replace the content with the new static content template
			const tmpDisplayContainer = this.pict.ContentAssignment.getElement(headerStaticObservationSetDisplayContainer)?.[0];
			if (tmpDisplayContainer)
			{
				tmpDisplayContainer.innerHTML = '';
				tmpDisplayContainer.insertAdjacentHTML('beforeend', staticContent);
				this.pict.ContentAssignment.removeClass(headerStaticObservationSetDisplayContainer, 'is-hidden');
			}
		}
		else
		{
			this.buildFilterTagsForCardDeckHeader(pView, pInput, currentSettings?.hideFiltersInHeader);
			this.pict.ContentAssignment.removeClass(headerFilterContainer, 'is-hidden');
			// Apply the deck-filters-applied CSS marker to hide card delete buttons when non-label filters are active.
			// Label-only filtered decks are actionable (we can auto-apply labels), so delete buttons remain visible.
			const deckContainerElement = this.pict.ContentAssignment.getElement(this.getCardDeckContainerHTMLID(pInput.Macro.RawHTMLID) + (` .${this.deckClassName}`))?.[0];
			if (deckContainerElement)
			{
				if (this.hasFiltersApplied(pInput) && !this.hasActionableFilters(pInput))
				{
					deckContainerElement.classList.add('deck-filters-applied');
				}
				else
				{
					deckContainerElement.classList.remove('deck-filters-applied');
				}
			}
		}
		this.toggleVisibilityForAddCardHeaderButtons(pInput);
	}

	/**
	 * Prepares the UI before building the card deck. This includes setting up any necessary headers, selectors, and draggable configurations based on the current settings and observation list.
	 * Also handles the branching paths between Interactive and Print views to make sure we are only building the necessary elements for each view based on their specific needs and use cases.
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {string} pNameSpace - The namespace for the deck.
	 * @param {any[]} pObservationList - The list of observations to display in the deck.
	 */
	prepareUIBeforeBuildingCardDeck(pView, pInput, pNameSpace, pObservationList)
	{
		const currentSettings = this.getCurrentSettingsForInput(pInput);
		// this will handle the branching paths of what to show in the header of the card deck
		this.handleHeaderContentForCardDeck(pView, pInput, pNameSpace);
		// tag the deck container with the marker attributes used by cross-deck lookups (e.g. getObservationSetNamespacesOnDOM)
		// before handing off to either the interactive or print builder — print decks also need these markers so
		// persisted excludeObservationSets can resolve set membership when rendering PDFs.
		this.applyDeckContainerMarkerAttributes(pView, pInput, currentSettings);
		// handle which view we are going into, templates are built differently for each since Interactive has more dynamic functionality and options,
		// while Print is optimized for static display and performance for large decks in PDF view, so we want to make sure we are only building the necessary elements for each view
		if (this.pict.AppData.Interactive)
		{
			this.setDraggableConfigurationForDeck(pInput, currentSettings, pNameSpace);
			this.buildObservationCardDeck(pView, pInput, pNameSpace, pObservationList);
			// asynchronously resolve creator names for the details view — cards render immediately with placeholders,
			// and the creator name cells are back-filled once the lookup completes
			this.resolveCreatorNamesForDeck(pInput, pObservationList);
		}
		else
		{
			// The print build is async — pdf.js fetches each embedded PDF artifact and rasterizes it page by page — and it
			// clears the deck container before it appends. It MUST gate the render: prepareForPrintingAsync() sets
			// window.status='ready_to_print' as soon as completeRender() drains, and the rasterizer snapshots on that
			// signal, so leaving the build unregistered captures an empty Media section.
			this.registerRenderDependency(this.buildCardDeckCardsForPrintView(pView, pInput, pNameSpace, pObservationList));
			// print cards render before the name lookup returns, so back-fill them too — registered so the PDF waits for it
			this.registerRenderDependency(this.resolveCreatorNamesForDeck(pInput, pObservationList));
		}
		// Safety net: always remove the initial loading spinner after deck building completes.
		// buildObservationCardDeck calls removeInitialLoadingMessage internally, but if it bails early
		// (e.g. container elements not yet in the DOM on the synchronous useObservationSet NaN path),
		// the spinner would persist indefinitely. This explicit call ensures it is always cleared.
		this.removeInitialLoadingMessage(pInput);
		// invoke the custom after-marshal callback if registered (e.g. bridge defect photo count label update on initial load / set selection)
		if (typeof this.pict.AppData.ObservationCardDeckProvider.customOnAfterMarshal === 'function')
		{
			this.pict.AppData.ObservationCardDeckProvider.customOnAfterMarshal(pInput, pObservationList);
		}
	}

	/**
	 * Asynchronously resolves creator display names for observations in the deck. Collects unique CreatingIDUser values
	 * that are not already cached, fetches them via UserProvider.getUsersByIDList, and back-fills any rendered creator
	 * name cells (interactive `.card-details-creator-name`, print `.media-card-creator-name`) with the resolved names.
	 * @param {Object} pInput - The input object.
	 * @param {Array} pObservationList - The list of observations to resolve names for.
	 */
	async resolveCreatorNamesForDeck(pInput, pObservationList)
	{
		if (!pObservationList || !pObservationList.length) return;
		const cache = this.pict.AppData.ObservationCardDeckProvider.userNameCache;
		// collect unique user IDs that aren't already cached
		const uncachedIDs = [ ...new Set(
			pObservationList
				.map((obs) => obs.CreatingIDUser)
				.filter((id) => id && !cache[id]),
		) ];
		if (uncachedIDs.length > 0 && this.pict.providers.UserProvider)
		{
			try
			{
				const userRecords = await this._withTimeout(this.pict.providers.UserProvider.getUsersByIDList(uncachedIDs), this.creatorNameLookupTimeoutMS, 'Creator name lookup');
				if (userRecords && Array.isArray(userRecords))
				{
					for (const user of userRecords)
					{
						cache[user.IDUser] = `${user.NameFirst || ''} ${user.NameLast || ''}`.trim() || 'N/A';
					}
				}
			}
			catch (err)
			{
				this.pict.log.warn('[ObservationCardDeck] Failed to resolve creator names:', err);
			}
		}
		// back-fill any rendered creator name placeholders in this deck
		const deckContent = document.getElementById(this.getCardDeckContentHTMLID(pInput.Macro.RawHTMLID).replace('#', ''));
		if (!deckContent) return;
		const creatorCells = deckContent.querySelectorAll('.card-details-creator-name[data-creator-id], .media-card-creator-name[data-creator-id]');
		creatorCells.forEach((/** @type {HTMLElement} */ cell) =>
		{
			const userId = cell.getAttribute('data-creator-id');
			if (userId && cache[userId])
			{
				cell.textContent = cache[userId];
			}
		});
	}

	/**
	 * Applies the deck-type marker attributes (observation-set, document-list, filtered, excluded-set) and the view/input hash attributes
	 * to the deck container element. Shared by both the interactive and print rendering paths so cross-deck lookups work in both modes.
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {Object} pCurrentSettings - The resolved settings for the deck.
	 */
	applyDeckContainerMarkerAttributes(pView, pInput, pCurrentSettings)
	{
		const deckContainerElement = this.pict.ContentAssignment.getElement(this.getCardDeckContainerHTMLID(pInput.Macro.RawHTMLID) + (` .${this.deckClassName}`))?.[0];
		if (!deckContainerElement)
		{
			return;
		}
		deckContainerElement.setAttribute(this.deckViewHashAttribute, pView.Hash);
		deckContainerElement.setAttribute(this.deckInputHashAttribute, pInput.Hash);
		if (pCurrentSettings?.useObservationSet)
		{
			deckContainerElement.setAttribute(this.deckIsObservationSetAttribute, 'true');
			return;
		}
		deckContainerElement.setAttribute(this.deckIsDocumentListAttribute, 'true');
		if (this.hasFiltersApplied(pInput))
		{
			deckContainerElement.setAttribute(this.deckIsFilteredDocumentListAttribute, 'true');
		}
		if (pCurrentSettings?.excludeObservationSets)
		{
			deckContainerElement.setAttribute(this.deckIsExcludedSetAttribute, 'true');
		}
		else
		{
			deckContainerElement.removeAttribute(this.deckIsExcludedSetAttribute);
		}
	}

	/**
	 * Handles the case where we are not using observation sets. In this case, we will just apply any filters provided in the options to the full list of observations from the registry
	 * and build the deck based on that filtered list, and we will update the deck dynamically as the filters change by reapplying the filters to the full list of observations and rebuilding
	 * the deck on each filter change.
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @returns {Promise<void>} - Returns a promise that resolves when the data has been marshaled and the deck has been built.
	 */
	async handleOnDataMarshalForObservationListCase(pView, pInput)
	{
		const currentSettings = this.getCurrentSettingsForInput(pInput);
		// if we're not using observation sets, then we will just apply any filters provided in the options to the full list of observations from the registry and build the deck based on that filtered list, and we will update the deck dynamically as the filters change by reapplying the filters to the full list of observations and rebuilding the deck on each filter change
		let tmpCurrentObservationList;
		// Prefer DOM-discovered set decks (accurate for the current form) but fall back to the persisted NamespaceObservationSetMap
		// so exclude filtering still applies on cold reload before set decks have set their marker attributes —
		// otherwise the registry gets rebuilt with the full, unfiltered document list and the saved 6-item state reverts to 7.
		const excludeNamespaces = currentSettings.excludeObservationSets ? this.getObservationSetNamespacesForExcludeFiltering() : null;
		if (this._isRemarshalInProgress)
		{
			// During remarshal, use the authoritative local observation list that was already
			// updated by refreshCardDecks — avoids stale API re-fetch
			const localObservations = this.pict.AppData.DocumentData.ReportData.Observations || [];
			if (currentSettings.excludeObservationSets)
			{
				const currentSetObservationIDs = new Set();
				// Use DOM-discovered namespaces when available, otherwise all persisted set namespaces
				const namespacesToCheck = excludeNamespaces || Object.keys(this.pict.AppData.MediaRegistryStore?.NamespaceObservationSetMap || {});
				for (const namespace of namespacesToCheck)
				{
					const setObservations = await this.pict.providers.MediaRegistry.getMediaSet(namespace);
					for (const o of setObservations)
					{
						currentSetObservationIDs.add(o.IDObservation);
					}
				}
				tmpCurrentObservationList = localObservations.filter((/** @type {any} */ o) => !currentSetObservationIDs.has(o.IDObservation));
			}
			else
			{
				tmpCurrentObservationList = localObservations;
			}
		}
		else
		{
			tmpCurrentObservationList = currentSettings.excludeObservationSets ? await this.pict.providers.MediaRegistry.getDocumentObservationsExcludingCurrentSets(excludeNamespaces) : await this.pict.providers.MediaRegistry.getDocumentObservations();
		}
		const tmpNamespaceFromInputHash = this.buildNamespaceForCardDeck(pInput.Hash);
		this.pict.log.info(`Marshaling data to form for Observation Card Deck using input hash as namespace: ${pInput.Hash}`);
		let observationListFromRegistry = await this.pict.providers.MediaRegistry.getMediaSet(tmpNamespaceFromInputHash);
		// Preserve Ordinal values from the registry onto the local observation list
		// so that any user-defined ordering (e.g. from drag-and-drop) is not lost during remarshal
		if (observationListFromRegistry && observationListFromRegistry.length)
		{
			const registryOrdinalMap = new Map();
			for (const regObs of observationListFromRegistry)
			{
				if (regObs.Ordinal !== undefined)
				{
					registryOrdinalMap.set(regObs.IDObservation, regObs.Ordinal);
				}
			}
			if (registryOrdinalMap.size)
			{
				const knownObservations = [];
				const newObservations = [];
				for (const obs of tmpCurrentObservationList)
				{
					if (registryOrdinalMap.has(obs.IDObservation))
					{
						obs.Ordinal = registryOrdinalMap.get(obs.IDObservation);
						knownObservations.push(obs);
					}
					else
					{
						newObservations.push(obs);
					}
				}
				knownObservations.sort((a, b) => a.Ordinal - b.Ordinal);
				if (currentSettings.appendNewCardToStartOfDeck)
				{
					tmpCurrentObservationList = [ ...newObservations, ...knownObservations ];
				}
				else
				{
					tmpCurrentObservationList = [ ...knownObservations, ...newObservations ];
				}
			}
		}
		// set the current filters (or default ones) passed in from Pict config — filters now live as a sub-object of settings (CardDeckSettings.filters)
		const tmpFilters = this.setCurrentFiltersForInput(pInput, { ...this.patchedConfiguration[pInput?.PictForm?.InputType]?.filters, ...(pInput?.PictForm?.CardDeckSettings?.filters || this.defaultConfiguration.filters) });
		// if we find an existing observation list in the registry for this namespace, we need to compare it to the current document observations to see if there have been any changes (observations added or removed)
		// since the last time we marshaled data to the form for this input and update the registry if needed before we build the deck, because we want to make sure we are working with the most up to date data
		// from the document when we build the deck
		if (observationListFromRegistry && observationListFromRegistry.length)
		{
			const addedObservationIDs = [];
			const removedObservationIDs = [];
			// new observations added to the document, not yet in the registry set for this namespace
			for (const obs of tmpCurrentObservationList)
			{
				if (!observationListFromRegistry.find((/** @type {any} */ o) => o.IDObservation === obs.IDObservation))
				{
					this.pict.log.info(`Observation with ID ${obs.IDObservation} is in the document observations but not in the registry set for namespace ${tmpNamespaceFromInputHash}. Adding it to the registry set.`);
					if (currentSettings.appendNewCardToStartOfDeck)
					{
						observationListFromRegistry.unshift(obs);
					}
					else
					{
						observationListFromRegistry.push(obs);
					}
					addedObservationIDs.push(obs.IDObservation);
				}
			}
			// observations removed from the document, but still in the registry set for this namespace
			for (const obs of [ ...observationListFromRegistry ])
			{
				if (!tmpCurrentObservationList.find((/** @type {any} */ o) => o.IDObservation === obs.IDObservation))
				{
					this.pict.log.info(`Observation with ID ${obs.IDObservation} is in the registry set for namespace ${tmpNamespaceFromInputHash} but not in the document observations. Removing it from the registry set.`);
					observationListFromRegistry = observationListFromRegistry.filter((/** @type {any} */ o) => o.IDObservation !== obs.IDObservation);
					removedObservationIDs.push(obs.IDObservation);
				}
			}
			// if there were any additions or removals, we need to update the registry with the new list for that namespace so that it's up to date for the next time we marshal data to the form for this input
			if (addedObservationIDs.length || removedObservationIDs.length)
			{
				let updatedObservationList = [];
				this.pict.log.info(`Updated observation list for namespace ${tmpNamespaceFromInputHash}. Added: ${addedObservationIDs.join(', ')}, Removed: ${removedObservationIDs.join(', ')}`);
				updatedObservationList = this.pict.providers.ObservationProvider.handleFilteringObservationsInList(tmpFilters, tmpCurrentObservationList);
				updatedObservationList = await this.pict.providers.MediaRegistry.registerMediaSet(tmpNamespaceFromInputHash, updatedObservationList);
				this.prepareUIBeforeBuildingCardDeck(pView, pInput, tmpNamespaceFromInputHash, updatedObservationList);
				return;
			}
			else
			{
				this.pict.log.info(`Existing observation list for namespace ${tmpNamespaceFromInputHash} contains ${observationListFromRegistry.length} observations.`);
				this.prepareUIBeforeBuildingCardDeck(pView, pInput, tmpNamespaceFromInputHash, observationListFromRegistry);
				return;
			}
		}
		else
		{
			this.pict.log.warn(`No observation set found in MediaRegistry for namespace observation list at hash: ${pInput.Hash}. Will attempt to create new namespace and compile observation list based on options and document observations.`);
			const tmpObservationList = this.pict.providers.ObservationProvider.handleFilteringObservationsInList(tmpFilters, tmpCurrentObservationList);
			observationListFromRegistry = await this.pict.providers.MediaRegistry.registerMediaSet(tmpNamespaceFromInputHash, tmpObservationList);
			this.pict.log.info(`Registered new media set in MediaRegistry for namespace ${tmpNamespaceFromInputHash} with ${observationListFromRegistry.length} observations after filtering.`);
			this.prepareUIBeforeBuildingCardDeck(pView, pInput, tmpNamespaceFromInputHash, observationListFromRegistry);
			return;
		}
	}

	/**
	 * Handles the case where we are using observation sets for the deck, which means we expect the value passed in to be the ID of the observation set we want to display in the deck,
	 * so we need to get that set from the registry and build the deck based on that set. If we don't have a valid ID passed in, then we will wait until we have an ID to pull the set
	 * and build the deck, which will happen when the user adds their first observation and we create a new set for them based on the input hash as the namespace and the current document observations.
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled, expected to be the ID of the observation set to display in the deck.
	 * @returns {Promise<void>} - A promise that resolves when the deck has been built based on the observation set, or if we are waiting for an ID to be provided, it will resolve immediately and we will build the deck later when we have an ID to work with.
	 */
	async handleOnDataMarshalForObservationSetCase(pView, pInput, pValue)
	{
		// if we are using observation sets, then we expect the value passed in to be the ID of the observation set we want to display in the deck, so we need to get that set from the registry and build the deck based on that set
		const tmpIDObservationSet = parseInt(pValue);
		// the "empty"/"temp" set case where we don't have a valid observation set ID provided yet, which will be the case when the user first adds the deck to the form and hasn't added any observations to it yet,
		// so we don't have an observation set created for it yet since sets are created based on the input hash as the namespace once the first observation is added to the deck.
		if (isNaN(tmpIDObservationSet))
		{
			this.pict.log.info(`No valid Observation Set ID provided for input ${pInput.Macro.RawHTMLID}. Value received: ${pValue}. Will attempt to build deck with a temp ID until an image is added and we can generate a proper Observation Set record.`);
			const currentSettings = this.getCurrentSettingsForInput(pInput);
			const tmpNamespaceForIDObservationSet = this.buildNamespaceForCardDeck(pInput.Hash, null, true);
			// Register an empty observation list for the temp namespace — no API call needed since there's no
			// real ObservationSet record yet.  The previous call to registerMediaSetByIDObservationSet(ns, [])
			// passed [] which is truthy, bypassing the falsy guard and calling loadSetObservations([]) which
			// hit the server at /1.0/ObservationSet/ with an empty ID, producing a "record not found" error
			// on every marshal cycle.
			this.pict.providers.MediaRegistry.registerMediaSet(tmpNamespaceForIDObservationSet, []);
			// Yield to the browser before touching the DOM — unlike the non-NaN set path and the
			// non-set observation list path (both of which do async API work that naturally defers
			// DOM manipulation), this NaN path is fully synchronous.  When the framework calls
			// onDataMarshalToForm during the initial render cycle, the section template may not
			// have been flushed to the DOM yet, so buildObservationCardDeck can't find its container
			// elements and bails.  A single await on a resolved promise yields to the microtask
			// queue, giving the browser a chance to commit the pending template HTML before we
			// attempt to query and manipulate the deck elements.
			await Promise.resolve();
			this.prepareUIBeforeBuildingCardDeck(pView, pInput, tmpNamespaceForIDObservationSet, []);
			// Show a contextual empty message when using the set selector with no set selected
			if (currentSettings.useObservationSetSelector)
			{
				this.updateEmptyMessageText(pInput, 'No observation set is currently selected.');
			}
			this.handleObservationSetEditButtonStateOnChange(pInput);
			return;
		}
		// if we have a set id, we're good to go, we can pull the observations for that set from the registry and build the deck, if not,
		// we'll wait until we have an ID to pull the set and build the deck, which will happen when the user adds their first observation
		// and we create a new set for them based on the input hash as the namespace and the current document observations
		else
		{
			const tmpNamespaceForIDObservationSet = this.buildNamespaceForCardDeck(pInput.Hash, tmpIDObservationSet);
			this.pict.log.info(`Marshaling data to form for Observation Card Deck with provided Observation Set ID: ${tmpIDObservationSet}`);
			// Populate dynamicGUIDMap during marshal so other decks' Select2 processResults
			// wrappers can detect this set as "in use" on initial form load — not just after
			// user interaction.  Without this, reopening a saved form leaves the GUID map empty
			// until a user manually changes a selector, allowing duplicate set selection.
			this.pict.AppData.ObservationCardDeckProvider.dynamicGUIDMap[pInput.Macro.RawHTMLID] = tmpIDObservationSet;
			// Populate the set-name cache before rendering so the header selector/static label use the saved Name on first paint
			// and survive subsequent re-renders without flashing the "Observation Set {id}" fallback back over the correct name.
			await this.fetchObservationSetName(tmpIDObservationSet);
			// this will fetch or create a namespace for the observation set based on the ID and return the observations in that set for us to render in the deck
			let observationListFromRegistry = await this.pict.providers.MediaRegistry.getObservationSetByNamespace(tmpNamespaceForIDObservationSet);
			// if we don't find any observations in the registry for that namespace, then we need to create the namespace and register the observation set based on the ID provided, then fetch the list again to build the deck
			const currentSettings = this.getCurrentSettingsForInput(pInput);
			if (observationListFromRegistry && observationListFromRegistry.length)
			{
				this.pict.log.info(`Found existing observation set in MediaRegistry for namespace ${tmpNamespaceForIDObservationSet} with ${observationListFromRegistry.length} observations.`);
				// If appendNewCardToStartOfDeck is enabled, detect new observations by comparing against
				// the previously tracked set of IDs and move them to the front of the list
				if (currentSettings.appendNewCardToStartOfDeck && this._previousObservationSetIDs?.[tmpNamespaceForIDObservationSet])
				{
					const previousIDs = this._previousObservationSetIDs[tmpNamespaceForIDObservationSet];
					const existingObs = observationListFromRegistry.filter((/** @type {any} */ o) => previousIDs.has(o.IDObservation));
					const newObs = observationListFromRegistry.filter((/** @type {any} */ o) => !previousIDs.has(o.IDObservation));
					if (newObs.length)
					{
						observationListFromRegistry = [ ...newObs, ...existingObs ];
					}
				}
				// Track current IDs so the next remarshal can detect new additions
				this._previousObservationSetIDs[tmpNamespaceForIDObservationSet] = new Set(observationListFromRegistry.map((/** @type {any} */ o) => o.IDObservation));
				this.prepareUIBeforeBuildingCardDeck(pView, pInput, tmpNamespaceForIDObservationSet, observationListFromRegistry);
				this.handleObservationSetEditButtonStateOnChange(pInput);
				return;
			}
			else
			{
				this.pict.log.warn(`No observation set found in MediaRegistry for namespace ${tmpNamespaceForIDObservationSet}. Will attempt to create new namespace and compile observation list based on observation set ${tmpIDObservationSet}.`);
				observationListFromRegistry = await this.pict.providers.MediaRegistry.registerMediaSetByIDObservationSet(tmpNamespaceForIDObservationSet, tmpIDObservationSet);
				this.pict.log.info(`Registered new media set in MediaRegistry for namespace ${tmpNamespaceForIDObservationSet} with ${observationListFromRegistry.length} observations from observation set ID ${tmpIDObservationSet}.`);
				// Establish baseline IDs on first render so subsequent remarshals can detect new additions
				this._previousObservationSetIDs[tmpNamespaceForIDObservationSet] = new Set(observationListFromRegistry.map((/** @type {any} */ o) => o.IDObservation));
				this.prepareUIBeforeBuildingCardDeck(pView, pInput, tmpNamespaceForIDObservationSet, observationListFromRegistry);
				this.handleObservationSetEditButtonStateOnChange(pInput);
				return;
			}
		}
	}

	/**
	 * Sets the draggable configuration for the deck based on the current settings for the input, and registers the onDrop event handler to update the order of the cards in the registry when a card is dropped in a new position in the deck.
	 * @param {Object} pInput - The input object containing the macro and raw HTML ID.
	 * @param {Object} currentSettings - The current settings for the deck, which can be used to conditionally enable or disable dragging and dropping of cards based on the configuration of the deck (ex: preventing reordering of cards will disable dragging and dropping for reordering, but we may still want to allow dragging and dropping for moving cards between decks if we build that functionality in the future, so we can use different settings to control those different behaviors).
	 * @param {string} pNamespace - The namespace for the observation set associated with this deck, which is used to pull the list of observations from the registry and update the registry with the new order when cards are reordered in the deck.
	 */
	setDraggableConfigurationForDeck(pInput, currentSettings, pNamespace)
	{
		if (currentSettings.preventReorderingCards)
		{
			this.pict.log.info(`Card deck for input ${pNamespace} is set to prevent reordering of cards. Draggable configuration will not be applied to the deck.`);
			this.draggableConfigurationForDeck = { disabled: true };
			return;
		}
		this.draggableConfigurationForDeck = {
			limitToOwnCollection: true || currentSettings.preventCrossDeckDragging, // NOTE: Must be true until we build cross-apply filtering - see options above for more details
			disabled: this.isDocumentLocked || !this.canEditDocument || currentSettings.preventReorderingCards,
			elementSelector: `.${this.cardClassName}`,
			onDrop: async (/** @type {any} */ pItem, /** @type {any} */ pEvent) =>
			{
				const deckContainer = this.pict.ContentAssignment.getElement(this.getCardDeckContainerHTMLID(pInput.Macro.RawHTMLID))?.[0];
				const currentObservationList = await this.pict.providers.MediaRegistry.getMediaSet(pNamespace);
				if (!deckContainer)
				{
					this.pict.log.error(`Could not find card deck container for input ${pNamespace} when handling drop event for card reordering. HTML ID used to query: ${this.getCardDeckContainerHTMLID(pInput.Macro.RawHTMLID)}. Cannot update card order in registry.`);
					return;
				}
				// update the MediaRegistry order based on the new order of the cards in the deck after the drop is completed
				const cardElements = deckContainer.querySelectorAll(`.${this.cardClassName}`);
				// get the array of ids in order
				const newOrderArray = Array.from(cardElements)
				.map((card) =>
				{
					const observationID = card.getAttribute(this.cardAttributeId);
					return parseInt(observationID);
				})
				.filter((id) => !isNaN(id));
				const newOrderList = [];
				// loop through the new order array and build a new ordered list of observation objects based on the new order of ids
				for (const [ i, obsID ] of newOrderArray.entries())
				{
					const obs = currentObservationList.find((/** @type {any} */ o) => o.IDObservation == obsID);
					// reset the ordinal for all obs and use the new order we pass in
					if (obs)
					{
						obs.Ordinal = i + 1;
						newOrderList.push(obs);
					}
				}
				this.pict.providers.MediaRegistry.registerMediaSet(pNamespace, newOrderList);
				_Pict.providers.ObservationCardDeckProvider.updateIndexNumbersOnUIForDeck(pInput, newOrderList);
				this.pict.log.info(`Cards reordered in deck for input ${pNamespace}.`);
			},
		};
	}

	/**
	 * Returns a Set of observation set IDs that are currently assigned to other card deck selectors on the form,
	 * excluding the given input's own set. Used to disable already-in-use options in the select2 dropdown.
	 *
	 * Validates that each entry's hidden input still exists in the DOM before including it.
	 * When a containing row (e.g. a bridge defect) is removed, the hidden input is destroyed
	 * but the dynamicGUIDMap entry can linger — without this check, deleted rows' sets stay
	 * permanently marked as "in use" in the remaining selectors.
	 * @param {Object} pInput - The input object (used to identify the current deck via RawHTMLID).
	 * @returns {Set<number>} - Set of IDObservationSet values in use by other decks.
	 */
	getObservationSetIDsInUseByOtherDecks(pInput)
	{
		const inUseIDs = new Set();
		const guidMap = this.pict.AppData.ObservationCardDeckProvider.dynamicGUIDMap;
		for (const [ rawHTMLID, idObservationSet ] of Object.entries(guidMap))
		{
			if (rawHTMLID !== pInput.Macro.RawHTMLID && idObservationSet)
			{
				// Verify the hidden input still exists in the DOM — if the containing
				// row (e.g. a bridge defect) was deleted, the input is gone and
				// this entry is stale.
				const hiddenInput = this.pict.ContentAssignment.getElement(this.getHiddenInputHTMLID(rawHTMLID))?.[0];
				if (hiddenInput)
				{
					inUseIDs.add(Number(idObservationSet));
				}
				else
				{
					// Prune the stale entry so it doesn't accumulate across sessions
					delete guidMap[rawHTMLID];
				}
			}
		}
		return inUseIDs;
	}

	/**
	 * Returns the fallback display label used before the real saved Name has been fetched.
	 * @param {string|number|null} pIDObservationSet - The observation set ID, or null for a brand-new (unsaved) set.
	 * @returns {string}
	 */
	getFallbackObservationSetLabel(pIDObservationSet)
	{
		return pIDObservationSet ? `Observation Set ${pIDObservationSet}` : 'New Observation Set';
	}

	/**
	 * Returns the cached saved Name for an observation set, or null if no resolved value is in the cache yet.
	 * Pending in-flight fetches (Promise values) are treated as uncached so callers fall back to the placeholder label.
	 * @param {string|number|null} pIDObservationSet - The observation set ID to look up.
	 * @returns {string|null}
	 */
	getCachedObservationSetName(pIDObservationSet)
	{
		if (!pIDObservationSet)
		{
			return null;
		}
		const cached = this._observationSetNameCache[String(pIDObservationSet)];
		return typeof cached === 'string' ? cached : null;
	}

	/**
	 * Detects Names that were auto-generated by createNewObservationSetForCardDeck / handleConfirmCreateSetModal —
	 * both of those append a Date.toLocaleString()-style "(M/D/YYYY, H:MM:SS AM)" timestamp to the set label.
	 * Names matching that signature are ugly to show to users, so the UI treats them as "unnamed" and falls back to "Observation Set {id}".
	 *
	 * The match is anchored to the start of the string so that names with a meaningful prefix
	 * (e.g. "Elevation Photo (5/23/2025, 11:48:24 AM)") are preserved as-is in the UI.
	 * Only names that consist *entirely* of a timestamp are treated as auto-generated.
	 * @param {string|null|undefined} pName - The set Name to check.
	 * @returns {boolean}
	 */
	isAutoGeneratedSetName(pName)
	{
		if (!pName || !String(pName).trim())
		{
			return true;
		}
		return /^\s*\(?\s*\d{1,2}\/\d{1,2}\/\d{2,4},?\s+\d{1,2}:\d{1,2}(:\d{1,2})?\s*(AM|PM)?\s*\)?\s*$/i.test(pName);
	}

	/**
	 * Returns the label to show in the deck header for an observation set: the user-given Name when set, or "Observation Set {id}" otherwise.
	 * Names detected as auto-generated by isAutoGeneratedSetName are treated as unset so users don't see timestamp noise in the header.
	 * @param {string|number|null} pIDObservationSet - The observation set ID.
	 * @returns {string}
	 */
	getDisplayLabelForObservationSet(pIDObservationSet)
	{
		const cachedName = this.getCachedObservationSetName(pIDObservationSet);
		if (cachedName && !this.isAutoGeneratedSetName(cachedName))
		{
			return cachedName;
		}
		return this.getFallbackObservationSetLabel(pIDObservationSet);
	}

	/**
	 * Fetches the saved Name for an observation set (cached by ID). Returns the fallback label on failure so callers always get a usable string.
	 * @param {string|number} pIDObservationSet - The observation set ID to look up.
	 * @returns {Promise<string>} - Resolves to the saved Name, or the fallback "Observation Set {id}" label if the fetch fails.
	 */
	async fetchObservationSetName(pIDObservationSet)
	{
		if (!pIDObservationSet)
		{
			return this.getFallbackObservationSetLabel(pIDObservationSet);
		}
		const cacheKey = String(pIDObservationSet);
		const cached = this._observationSetNameCache[cacheKey];
		if (cached)
		{
			return cached;
		}
		const tmpPromise = (async () =>
		{
			try
			{
				const tmpSet = await this.pict.providers.ObservationSetProvider.loadSet(pIDObservationSet);
				const tmpName = tmpSet?.Name || this.getFallbackObservationSetLabel(pIDObservationSet);
				this._observationSetNameCache[cacheKey] = tmpName;
				return tmpName;
			}
			catch (error)
			{
				this.pict.log.error(`Error loading observation set ${pIDObservationSet} for label: ${error}`);
				delete this._observationSetNameCache[cacheKey];
				return this.getFallbackObservationSetLabel(pIDObservationSet);
			}
		})();
		this._observationSetNameCache[cacheKey] = tmpPromise;
		return tmpPromise;
	}

	/**
	 * Builds the option element for the current observation set ID and updates the select element with it.
	 * @param {Object} pInput - The input object containing the macro and raw HTML ID.
	 * @param {string|number} tmpIDObservationSet - The ID of the current observation set.
	 */
	buildSetSelectorOptionForCurrentObservationSetID(pInput, tmpIDObservationSet)
	{
		// update the selector with the current IDObservationSet value so that if the user changes the filters and we need to re-render the deck,
		// we know which observation set ID to pull from the registry to get the updated list of observations to render in the deck based on the selected filters
		const tmpSelectElement = this.pict.ContentAssignment.getElement(this.getCardDeckHeaderObservationSetSelectorHTMLID(pInput.Macro.RawHTMLID))?.[0];
		if (!tmpSelectElement)
		{
			this.pict.log.error(`Could not find select element for observation set selector with HTML ID ${this.getCardDeckHeaderObservationSetSelectorHTMLID(pInput.Macro.RawHTMLID)}. Cannot update selector value for Observation Set ID ${tmpIDObservationSet}.`);
			this.removeInitialLoadingMessage(pInput);
			return;
		}
		// only create and select an option when we have a valid observation set ID —
		// when NaN (no set yet), leave the selector on the empty placeholder so it renders
		// as "Select a set..." and the user must explicitly create or pick one
		const hasValidSetID = !isNaN(Number(tmpIDObservationSet)) && tmpIDObservationSet !== null;
		if (hasValidSetID)
		{
			// build the prefilled value for the select dropdown.
			// Prefer the user-given Name (pre-populated in the set-name cache before render) and fall back to "Observation Set {id}"
			// for unnamed/auto-timestamped sets so re-renders stay stable and users don't see ugly defaults.
			const tmpOption = tmpSelectElement.querySelector(`[value="${tmpIDObservationSet}"]`) || document.createElement('option');
			tmpOption.value = tmpIDObservationSet;
			tmpOption.text = this.getDisplayLabelForObservationSet(tmpIDObservationSet);
			tmpOption.selected = true;
			if (!tmpSelectElement.querySelector(`[value="${tmpIDObservationSet}"]`))
			{
				tmpSelectElement.appendChild(tmpOption);
			}
		}
		else
		{
			// ensure the placeholder <option value=""> is selected so Select2 shows "Select a set..."
			const tmpPlaceholderOption = tmpSelectElement.querySelector('option[value=""]');
			if (tmpPlaceholderOption)
			{
				tmpPlaceholderOption.selected = true;
			}
		}
		// initialize the select2 dropdown on the selector element if it's available, which will handle the searching and selecting of different observation sets to display in the deck
		// if the user wants to switch to a different set
		if (window['jQuery'] && window['jQuery'].fn.select2)
		{
			const tmpConfigOptions = {
				Input: pInput,
				SearchFallbackPlaceholder: `Select Observation Set...`,
				ApplySort: true,
				Disabled: this.isDocumentLocked || !this.canEditDocument,
				HandleSearchFilters: (/** @type {string[]} */ pFilters) =>
				{
					const customFilter = this.pict.AppData.ObservationCardDeckProvider.customHandleSearchFiltersForSelector;
					if (typeof customFilter === 'function')
					{
						return customFilter(pFilters);
					}
					if (this.pict.AppData?.DocumentData?.ReportData?.IDProject)
					{
						pFilters.push('FBV~IDProject~EQ~' + this.pict.AppData?.DocumentData?.ReportData?.IDProject);
					}
					return pFilters;
				},
			};
			const tmpSelectOptions = this.pict.providers.Select2Util.getOptionsAjax('ObservationSet', tmpConfigOptions);
			// Override noResults to show a contextual message when document scope filter is
			// active but no sets exist on this document yet, instead of generic "No results found."
			const providerAppData = this.pict.AppData.ObservationCardDeckProvider;
			tmpSelectOptions.language = Object.assign({}, tmpSelectOptions.language, {
				noResults: () =>
				{
					if (providerAppData?.documentScopeEmptyState)
					{
						return 'No sets created in this document yet';
					}
					return 'No results found.';
				},
			});
			// Wrap processResults to disable observation sets already in use by other card decks
			const originalProcessResults = tmpSelectOptions.ajax.processResults;
			tmpSelectOptions.ajax.processResults = (/** @type {any} */ data, /** @type {any} */ params) =>
			{
				const pageInfo = originalProcessResults(data, params);
				const inUseIDs = this.getObservationSetIDsInUseByOtherDecks(pInput);
				for (const result of pageInfo.results)
				{
					if (inUseIDs.has(Number(result.id)))
					{
						result.disabled = true;
						result.text = result.text + ' (in use)';
					}
				}
				return pageInfo;
			};
			window['jQuery'](tmpSelectElement).select2(tmpSelectOptions);
			// when no valid set ID, explicitly clear Select2's internal selection so it shows the placeholder
			// (Select2 can cache stale selections from prior initializations on the same DOM element)
			if (!hasValidSetID)
			{
				window['jQuery'](tmpSelectElement).val('').trigger('change.select2');
			}
		}
	}

	/**
	 * Sets the current settings for the given input in the dynamicSettings, which is used to store the configuration settings for each input separately so that we can apply different configurations to different decks on the same page if needed.
	 * @param {Object} pInput - The input object to set the settings for (used to access the input hash for storing in the dynamicSettings).
	 * @param {Object} pSettings - The settings object to set for the given input, see defaultConfiguration for the structure of this object and what settings are available to be set for each input.
	 */
	setCurrentSettingsForInput(pInput, pSettings)
	{
		const tmpSettings = JSON.parse(JSON.stringify(Object.assign({}, this.defaultConfiguration, this.patchedConfiguration[pInput?.PictForm?.InputType], pSettings)));
		// guard: normalize indexTagScope to 'local' if it's not a recognized value
		if (tmpSettings.indexTagScope !== 'local' && tmpSettings.indexTagScope !== 'document')
		{
			tmpSettings.indexTagScope = 'local';
		}
		// apply address-based overrides registered by consuming views (e.g. bridge element conditions)
		// These take precedence over patchedConfiguration and pSettings but yield to persisted user overrides.
		const addressOverrides = this.pict.AppData.ObservationCardDeckProvider?.settingsOverridesByInputAddress?.[pInput?.Address];
		if (addressOverrides)
		{
			Object.assign(tmpSettings, JSON.parse(JSON.stringify(addressOverrides)));
		}
		// apply any persisted user-toggled overrides (from FormData) so they survive save/reload and remarshals within a session
		const persistedOverrides = this.getPersistedOverridesForInput(pInput);
		if (persistedOverrides)
		{
			Object.assign(tmpSettings, persistedOverrides);
		}
		// alwaysExcludeObservationSets is a structural override that cannot be toggled by the user —
		// force excludeObservationSets on AFTER all other merges so nothing can turn it off.
		if (tmpSettings.alwaysExcludeObservationSets)
		{
			tmpSettings.excludeObservationSets = true;
		}
		this.pict.AppData.ObservationCardDeckProvider.dynamicSettingsMap[pInput.Hash] = tmpSettings;
		return tmpSettings;
	}

	/**
	 * Gets the current settings for the given input from the dynamicSettings, or returns the default settings if no settings are set for that input.
	 * @param {Object} pInput - The input object to get the settings for (used to access the input hash for looking up in the dynamicSettings).
	 * @returns {Object} - The settings object for the given input, either from the dynamicSettings or the default settings if no settings are set.
	 */
	getCurrentSettingsForInput(pInput)
	{
		return this.pict.AppData.ObservationCardDeckProvider.dynamicSettingsMap[pInput.Hash] || this.defaultConfiguration;
	}

	/**
	 * Sets the current filters for the given input as a sub-object of the input's settings. This keeps all deck configuration in one collection
	 * while still allowing filters to be set independently from other settings.
	 * @param {Object} pInput - The input object to set the filters for (used to access the input hash for storing in the settings).
	 * @param {Object} filters - The filters object to set for the given input, which should have the structure of { Include: { ObservationTypes: [], LabelTypes: [] }, Exclude: { ObservationTypes: [], LabelTypes: [] } } or a subset of those properties depending on what filters are being applied.
	 */
	setCurrentFiltersForInput(pInput, filters)
	{
		const currentSettings = this.getCurrentSettingsForInput(pInput);
		// When no Include ObservationTypes are explicitly configured, default to the supported media
		// types so that non-media observations (e.g. Weather) don't leak into the deck display.
		// Mark with _isDefaultTypeFilter so hasFiltersApplied can distinguish injected defaults from
		// explicit user/config filters — the default should not trigger filter tag rendering in the header.
		if (!filters?.Include?.ObservationTypes?.length)
		{
			if (!filters.Include)
			{
 filters.Include = {};
			}
			filters.Include.ObservationTypes = [ ...this.actionableObservationTypes ];
			filters._isDefaultTypeFilter = true;
		}
		else
		{
			delete filters._isDefaultTypeFilter;
		}
		currentSettings.filters = filters;
		this.pict.AppData.ObservationCardDeckProvider.dynamicSettingsMap[pInput.Hash] = currentSettings;
		return filters;
	}

	/**
	 * Checks whether the given input's deck has filters applied and those filters are all "actionable" —
	 * meaning we can safely allow Add/Remove/QuickCapture on the deck because:
	 * - ObservationType filters (if any) only include media types that quick capture/upload naturally produces
	 * - LabelType filters (if any) can be auto-applied to new observations
	 * - No Description filters are present (not supported)
	 * @param {Object} pInput - The input object to check filters for.
	 * @returns {boolean} - True if filters are applied and all are actionable.
	 */
	hasActionableFilters(pInput)
	{
		if (!this.hasFiltersApplied(pInput))
		{
			return false;
		}
		const currentFilters = this.getCurrentFiltersForInput(pInput);
		for (const filterDirection of [ 'Include', 'Exclude' ])
		{
			if (currentFilters[filterDirection]?.Descriptions?.length)
			{
				return false;
			}
			const obsTypes = currentFilters[filterDirection]?.ObservationTypes;
			if (obsTypes?.length && !obsTypes.every((/** @type {any} */ t) => this.actionableObservationTypes.includes(t)))
			{
				return false;
			}
		}
		return true;
	}

	/**
	 * Checks whether a single observation's ObservationType passes the deck's Include/Exclude ObservationType filters.
	 * Returns true when no ObservationType filters are configured (i.e. unrestricted decks always pass).
	 * Used as a gate before adding observations to filtered decks so that mismatched types are rejected
	 * with a user-visible warning instead of silently disappearing from the deck.
	 * @param {Object} pInput - The input object whose current filters should be checked.
	 * @param {Object} pObservation - The observation record (must have an `ObservationType` field).
	 * @returns {boolean} True if the observation is allowed by the filters, false if it should be rejected.
	 */
	observationMatchesTypeFilter(pInput, pObservation)
	{
		if (!pObservation?.ObservationType)
		{
			return true; // no type to check — allow
		}
		// When no explicit filters are configured, default to only allowing the supported media types
		// (Legacy Image, Legacy Video, Legacy Audio, File). We haven't built e2e support for other
		// observation types yet, so this prevents users from depending on unsupported types.
		if (!this.hasFiltersApplied(pInput))
		{
			return this.actionableObservationTypes.includes(pObservation.ObservationType);
		}
		const currentFilters = this.getCurrentFiltersForInput(pInput);
		const includeTypes = currentFilters?.Include?.ObservationTypes;
		if (includeTypes?.length && !includeTypes.includes(pObservation.ObservationType))
		{
			return false; // observation type not in the Include list
		}
		const excludeTypes = currentFilters?.Exclude?.ObservationTypes;
		if (excludeTypes?.length && excludeTypes.includes(pObservation.ObservationType))
		{
			return false; // observation type is in the Exclude list
		}
		return true;
	}

	/**
	 * Shows a transient user-facing warning when an observation's type doesn't match the deck's filter.
	 * Uses the Headlight notification system if available, otherwise falls back to a console warning.
	 * @param {Object} pInput - The input object (used for deck name context).
	 * @param {string} pObservationType - The observation's type that was rejected.
	 */
	showFilterMismatchWarning(pInput, pObservationType)
	{
		const deckName = pInput?.Name || 'this deck';
		const currentFilters = this.getCurrentFiltersForInput(pInput);
		const allowedTypes = currentFilters?.Include?.ObservationTypes || [];
		const allowedLabel = (allowedTypes.length ? allowedTypes : this.actionableObservationTypes).map((/** @type {string} */ t) => t.replace(/^Legacy\s*/, '')).join(', ');
		const displayType = pObservationType.replace(/^Legacy\s*/, '');
		const message = `Cannot add "${displayType}" observation to ${deckName} — this deck only accepts: ${allowedLabel}.`;
		// Use the Headlight notification/toast system if available, otherwise fall back to a basic alert
		if (this.pict?.views?.HeadlightNotifications?.addNotification)
		{
			this.pict.views.HeadlightNotifications.addNotification({ message: message, type: 'warning', duration: 5000 });
		}
		else if (this.pict?.providers?.Informary?.addWarning)
		{
			this.pict.providers.Informary.addWarning(message);
		}
		else
		{
			this.pict.log.warn(message);
		}
	}

	/**
	 * Applies the Include LabelType filters from the deck's current filter configuration to a newly created observation,
	 * so that it matches the filter criteria and appears in the filtered deck. Only applies labels that are not already
	 * present on the observation. Persists the updated observation to the server.
	 * @param {Object} pInput - The input object to get the filters from.
	 * @param {Object} pObservationRecord - The observation record to apply labels to.
	 */
	async applyFilterLabelsToObservation(pInput, pObservationRecord)
	{
		const currentFilters = this.getCurrentFiltersForInput(pInput);
		const includeLabelFilters = currentFilters?.Include?.LabelTypes;
		if (!includeLabelFilters?.length)
		{
			return;
		}
		// Build the Tags array as tag value strings — the API expects Tags as string values (not IDs or objects)
		const existingTags = Array.isArray(pObservationRecord.Tags) ? pObservationRecord.Tags.filter((/** @type {any} */ t) => t != null) : [];
		/**
		 * @type {any[]}
		 */
		const tagsToAdd = [];
		for (const labelFilter of includeLabelFilters)
		{
			for (const tagValue of labelFilter.Values)
			{
				if (!existingTags.includes(tagValue) && !tagsToAdd.includes(tagValue))
				{
					tagsToAdd.push(tagValue);
				}
			}
		}
		if (!tagsToAdd.length)
		{
			return;
		}
		pObservationRecord.Tags = [ ...existingTags, ...tagsToAdd ];
		await this.pict.providers.ObservationProvider.updateObservation(pObservationRecord, () =>
		{
			this.pict.log.info(`Applied filter labels to observation ${pObservationRecord.IDObservation}: Tags=${pObservationRecord.Tags}`);
		});
	}

	/**
	 * Gets the current filters for the given input from the settings, or returns an empty object if no filters are set for that input.
	 * @param {Object} pInput - The input object to get the filters for (used to access the input hash for looking up in the settings).
	 * @returns {Object} - The filters object for the given input from the settings, or an empty object if no filters are set.
	 */
	getCurrentFiltersForInput(pInput)
	{
		return this.getCurrentSettingsForInput(pInput).filters || {};
	}

	/**
	 * Returns true if the given input's deck has any Include or Exclude filters applied (non-empty filter keys).
	 * When only the default actionable-type filter has been injected (marked by `_isDefaultTypeFilter`),
	 * this returns false so that UI paths (header tags, exclude-sets toggle) treat the deck as unfiltered.
	 * The injected defaults still apply at the data-filtering level — callers that need the raw filter
	 * arrays (e.g. `observationMatchesTypeFilter`) read them directly from `getCurrentFiltersForInput`.
	 * @param {Object} pInput - The input object to check filters for.
	 * @returns {boolean}
	 */
	hasFiltersApplied(pInput)
	{
		const currentFilters = this.getCurrentFiltersForInput(pInput);

		// When the only populated filter is the injected default ObservationTypes (matching the
		// actionableObservationTypes list exactly), treat as "no filters" for UI purposes — the toggle,
		// header tags, and add-button visibility should behave as if the deck is unfiltered.
		// The defaults still gate observations at the data level via observationMatchesTypeFilter.
		// We check both the `_isDefaultTypeFilter` flag AND a content-based comparison so the detection
		// survives code paths that deep-clone or re-spread settings without preserving the flag.
		const isDefaultOnly = this._isOnlyDefaultTypeFilter(currentFilters);
		if (isDefaultOnly)
		{
			return false;
		}

		return Object.keys(currentFilters).some((key) =>
		{
			if (key === '_isDefaultTypeFilter')
			{
				return false;
			}
			const val = currentFilters[key];
			return val && typeof val === 'object' && Object.keys(val).length > 0;
		});
	}

	/**
	 * Returns true when the given filters object contains ONLY the injected default ObservationTypes
	 * (matching `actionableObservationTypes` exactly) and no other explicit filter criteria.
	 * Uses a flag-first check for speed, then falls back to a content comparison so deep-cloned
	 * or re-spread filter objects are still recognized as defaults.
	 * @param {Object} currentFilters - The filters object from getCurrentFiltersForInput.
	 * @returns {boolean}
	 */
	_isOnlyDefaultTypeFilter(currentFilters)
	{
		if (!currentFilters)
		{
			return true;
		} // no filters at all

		// Quick check: if there are any Exclude entries with values, this is not default-only
		if (currentFilters.Exclude && Object.keys(currentFilters.Exclude).some((k) => currentFilters.Exclude[k]?.length > 0))
		{
			return false;
		}
		// Quick check: if there are Include entries beyond ObservationTypes, this is not default-only
		if (currentFilters.Include)
		{
			const includeKeys = Object.keys(currentFilters.Include);
			if (includeKeys.some((k) => k !== 'ObservationTypes' && currentFilters.Include[k]?.length > 0))
			{
				return false;
			}
		}

		// Flag-based check: if the flag was set by setCurrentFiltersForInput and survived, trust it
		if (currentFilters._isDefaultTypeFilter)
		{
			return true;
		}

		// Content-based fallback: compare Include.ObservationTypes to actionableObservationTypes
		const includeTypes = currentFilters.Include?.ObservationTypes;
		if (!includeTypes?.length)
		{
			return true; // no Include types at all — treat as default (empty/unfiltered)
		}
		// Match if the Include types are exactly the actionable defaults (same items, same order)
		if (includeTypes.length !== this.actionableObservationTypes.length)
		{
			return false;
		}
		return includeTypes.every((/** @type {string} */ t, /** @type {number} */ i) => t === this.actionableObservationTypes[i]);
	}

	/**
	 * Handles the data request event for a select input in the PictProviderInputSelect class.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object (NOTE: Doesn't work on buttons)
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @return {boolean}
	 */
	onDataRequest(pView, pInput, pValue, pHTMLSelector)
	{
		// Read the newly selected value from the set selector dropdown (where the user made their choice).
		// Only fall back to the hidden input when the selector element doesn't exist (non-selector cases).
		// When the selector exists, always trust its value — an empty string means the user cleared the selection.
		const tmpSelectorElement = this.pict.ContentAssignment.getElement(this.getCardDeckHeaderObservationSetSelectorHTMLID(pInput.Macro.RawHTMLID))?.[0];
		const tmpInputSelectValue = tmpSelectorElement ? tmpSelectorElement.value : this.pict.ContentAssignment.readContent(this.getHiddenInputHTMLID(pInput.Macro.RawHTMLID));
		// Write the new value to both the hidden input and the form data address so the value is persisted
		this.pict.ContentAssignment.assignContent(this.getHiddenInputHTMLID(pInput.Macro.RawHTMLID), tmpInputSelectValue);
		this.pict.ContentAssignment.assignContent(pHTMLSelector, tmpInputSelectValue);
		// Keep the dynamicGUIDMap up to date so other deck selectors know which sets are in use
		if (tmpSelectorElement && tmpInputSelectValue)
		{
			this.pict.AppData.ObservationCardDeckProvider.dynamicGUIDMap[pInput.Macro.RawHTMLID] = tmpInputSelectValue;
		}
		else if (tmpSelectorElement)
		{
			delete this.pict.AppData.ObservationCardDeckProvider.dynamicGUIDMap[pInput.Macro.RawHTMLID];
		}
		// Only notify the framework of a data change when the resolved value actually differs from
		// the value that was passed in.  Previously, dataChanged() was called unconditionally which
		// triggered a full marshalToView() cycle on every onDataRequest invocation — even when the
		// value hadn't changed.  When a new defect row is added with an empty Observation Set value,
		// the marshal→request→dataChanged→marshal cycle would repeat indefinitely, causing an
		// infinite loop that locked up the browser.
		if (tmpInputSelectValue !== pValue)
		{
			pView.dataChanged(pInput.Hash);
			this.pict.log.info(`Observation Set selection changed for input ${pInput.Macro.RawHTMLID}. New value: ${tmpInputSelectValue}. Updating deck to reflect new selection.`);
			this.updateDeckForObservationSetSelectionChange(pInput, tmpInputSelectValue);
		}
		this.handleObservationSetEditButtonStateOnChange(pInput);
		return super.onDataRequest(pView, pInput, pValue, pHTMLSelector);
	}

	/**
	 * @param {any} pInput - The input object.
	 * @param {string} tmpInputSelectValue - The new value selected in the observation set selector.
	 */
	async updateDeckForObservationSetSelectionChange(pInput, tmpInputSelectValue)
	{
		const tmpParsedID = parseInt(tmpInputSelectValue);
		// Handle the "cleared" case — user cleared the selector, so reset the deck to an empty state
		if (!tmpInputSelectValue || isNaN(tmpParsedID))
		{
			this.pict.log.info(`Observation Set selector cleared for input ${pInput.Macro.RawHTMLID}. Resetting deck to empty state.`);
			const cardDeckContentElement = document.querySelector(this.getCardDeckContentHTMLID(pInput.Macro.RawHTMLID));
			if (cardDeckContentElement)
			{
				cardDeckContentElement.innerHTML = '';
			}
			this.updateEmptyMessageText(pInput, 'No observation set is currently selected.');
			this.toggleVisibilityForAddCardHeaderButtons(pInput);
			return;
		}
		// Restore the default empty message before building the deck with the new set
		const currentSettings = this.getCurrentSettingsForInput(pInput);
		this.updateEmptyMessageText(pInput, currentSettings.customEmptyStateMessage);
		const tmpNamespaceForIDObservationSet = this.buildNamespaceForCardDeck(pInput.Hash, tmpParsedID);
		try
		{
			// Populate the set-name cache before rendering so the header label uses the saved Name on first paint
			await this.fetchObservationSetName(tmpParsedID);
			let observationListFromRegistry = await this.pict.providers.MediaRegistry.getObservationSetByNamespace(tmpNamespaceForIDObservationSet);
			if (observationListFromRegistry && observationListFromRegistry.length)
			{
				this.pict.log.info(`Found existing observation set in MediaRegistry for namespace ${tmpNamespaceForIDObservationSet} with ${observationListFromRegistry.length} observations. Updating deck to display this set.`);
			}
			else
			{
				// Set not yet in registry — fetch it from the server and register it so the deck can display it
				this.pict.log.info(`No observation set in MediaRegistry for namespace ${tmpNamespaceForIDObservationSet}. Fetching and registering set ${tmpParsedID}.`);
				observationListFromRegistry = await this.pict.providers.MediaRegistry.registerMediaSetByIDObservationSet(tmpNamespaceForIDObservationSet, tmpParsedID);
			}
			this.prepareUIBeforeBuildingCardDeck(this.pict.views[pInput.ViewHash], pInput, tmpNamespaceForIDObservationSet, observationListFromRegistry);
		}
		catch (error)
		{
			this.pict.log.error(`Error updating deck for observation set selection change (namespace: ${tmpNamespaceForIDObservationSet}): ${error}.`);
		}
	}

	/**
	 * Handles the click event for the manage set button.
	 * @param {string} pRawID - The id of the hidden input element.
	 * @param {string} pViewHash - The hash of the view.
	 * @param {string} pInputHash - The hash of the input.
	 */
	async handleObservationSetEditButtonClick(pRawID, pViewHash, pInputHash)
	{
		// set the button to loading state
		const tmpButton = document.querySelector(this.getCardDeckHeaderObservationSetEditButtonHTMLID(pRawID));
		if (tmpButton)
		{
			tmpButton.classList.add('is-loading');
		}
		// handle the empty message display
		let tmpSetId = 0;
		// check if the user has selected a new set name from the dropdown or an existing set
		const tmpEl = document.querySelector(this.getCardDeckHeaderObservationSetSelectorHTMLID(pRawID));
		if (!(tmpEl instanceof HTMLSelectElement))
		{
			// set the button to normal state
			if (tmpButton)
			{
				tmpButton.classList.remove('is-loading');
			}
			return;
		}
		const tmpSelectedValue = tmpEl.value;
		if (tmpSelectedValue && !isNaN(parseInt(tmpSelectedValue)))
		{
			tmpSetId = parseInt(tmpSelectedValue);
		}
		this.pict.fable.services.HeadlightPanels.applyUIHighlight(this.getCardDeckContentHTMLID(pRawID));
		// open the image panel for the user to select images
		this.pict.views.ObservationSet.handleFormManageSetButtonClick(tmpSetId, () =>
		{
			// set the button to normal state
			if (tmpButton)
			{
				tmpButton.classList.remove('is-loading');
			}
			this.pict.views.ObservationSet.handlePanelAddObservationButtonClick(null);
		});
	}

	/**
	 * Handles the click event for the create set button. Opens a modal with a name input and optional next-action selector.
	 * @param {string} pRawID - The raw HTML ID of the hidden input element.
	 * @param {string} pViewHash - The view hash for resolving the input context.
	 * @param {string} pInputHash - The input hash for resolving the input context.
	 */
	async handleObservationSetCreateSetButtonClick(pRawID, pViewHash, pInputHash)
	{
		const inputEl = this.pict.ContentAssignment.getElement(`#${ pRawID }`)?.[0];
		if (!inputEl)
		{
			this.pict.log.warn('Could not find obs set input for create set modal.');
			return;
		}
		// store context so the confirm handler can resolve the correct view/input
		this._pendingCreateSetContext = { rawID: pRawID, viewHash: pViewHash, inputHash: pInputHash };
		// get the previously saved user preferences for the next action to take after the set is created
		const tmpNextAction = this.pict.views.HeadlightPaneSettings.readUserPreference('ObservationSetNextAction') || '';
		this.nextActionAfterCreate = tmpNextAction;
		// if we have a custom handler, use it instead of the default modal
		if (this.pict.AppData.ObservationCardDeckProvider.customHandleObservationSetCreateSetButtonClick)
		{
			const tmpCreateButton = document.querySelector(this.getCardDeckHeaderObservationSetCreateButtonHTMLID(pRawID));
			if (tmpCreateButton)
			{
				tmpCreateButton.classList.add('is-loading');
			}
			await this.pict.AppData.ObservationCardDeckProvider.customHandleObservationSetCreateSetButtonClick(pRawID, tmpNextAction);
			if (tmpCreateButton)
			{
				tmpCreateButton.classList.remove('is-loading');
			}
			return;
		}
		// build modal body with set name input
		const modalBodyTemplate = /*html*/`
			<div class="columns is-mobile">
				<div class="column">
					<div class="field">
						<label class="label">Set Name</label>
						<div class="control">
							<input class="input is-fullwidth" id="ObservationSetCreateNameInput" type="text" value="New Observation Set" placeholder="New Observation Set" onfocus="this.select()" />
						</div>
					</div>
				</div>
			</div>`;
		// build modal footer with next-action selector on the left and action buttons on the right
		const modalFooterTemplate = /*html*/`
			<div class="columns is-flex-grow-1 is-vcentered">
				<div class="column">
					<div class="field">
						<div class="control select is-fullwidth">
							<select id="CreateSetNextActionSelect">
								<option value="" ${ !tmpNextAction ? 'selected' : '' }>Pick an action (optional)</option>
								<option value="manage" ${ tmpNextAction === 'manage' ? 'selected' : '' }>Manage Set</option>
								<option value="capture" ${ tmpNextAction === 'capture' ? 'selected' : '' }>Quick Capture</option>
							</select>
						</div>
					</div>
				</div>
				<div class="column is-narrow">
					<div class="buttons is-right">
						<button type="button" class="button is-link is-soft" onclick="_Pict.providers.ObservationCardDeckProvider.handleCancelCreateSetModal()">
							<span class="icon"><i class="fas fa-fw fa-times"></i></span>
							<span>Cancel</span>
						</button>
						<button type="button" class="button is-link" onclick="_Pict.providers.ObservationCardDeckProvider.handleConfirmCreateSetModal()">
							<span class="icon"><i class="fas fa-fw fa-plus"></i></span>
							<span>Create New Set</span>
						</button>
					</div>
				</div>
			</div>`;
		this.fable.HeadlightModal.spawnModal({
			title: 'Create Observation Set',
			modalBodyTemplate: modalBodyTemplate,
			modalFooterTemplate: modalFooterTemplate,
			hideModalFooter: false,
			disableBackgroundClickToClose: true,
		});
		// set the button to loading state
		const tmpCreateButton = document.querySelector(this.getCardDeckHeaderObservationSetCreateButtonHTMLID(pRawID));
		if (tmpCreateButton)
		{
			tmpCreateButton.classList.add('is-loading');
		}
	}

	/**
	 * Handles the cancel action for the create set modal. Dismisses the modal and removes the loading state from the create button.
	 */
	handleCancelCreateSetModal()
	{
		this.fable.HeadlightModal.dismissModals();
		if (this._pendingCreateSetContext)
		{
			const tmpCreateButton = document.querySelector(this.getCardDeckHeaderObservationSetCreateButtonHTMLID(this._pendingCreateSetContext.rawID));
			if (tmpCreateButton)
			{
				tmpCreateButton.classList.remove('is-loading');
			}
			this._pendingCreateSetContext = null;
		}
	}

	/**
	 * Handles the confirm action for the create set modal. Creates the set, updates the selector and deck, then applies the selected next action.
	 */
	async handleConfirmCreateSetModal()
	{
		if (!this._pendingCreateSetContext)
		{
			this.pict.log.error('No pending create set context found. Cannot create set.');
			return;
		}
		const { rawID, viewHash, inputHash } = this._pendingCreateSetContext;
		// read the set name from the modal input
		const tmpNameInput = /** @type {HTMLInputElement} */ (document.getElementById('ObservationSetCreateNameInput'));
		const tmpName = tmpNameInput?.value?.trim() || 'New Observation Set (' + new Date().toLocaleString() + ')';
		// read the next action selection and persist the preference
		const tmpNextActionSelect = /** @type {HTMLSelectElement} */ (document.getElementById('CreateSetNextActionSelect'));
		const tmpNextAction = tmpNextActionSelect?.value || '';
		this.nextActionAfterCreate = tmpNextAction;
		this.pict.views.HeadlightPaneSettings.updateUserPreferences('ObservationSetNextAction', tmpNextAction);
		// create and save the new observation set
		const tmpProjectId = this.pict.AppData?.DocumentData?.ReportData?.IDProject || null;
		const tmpNewSet = this.pict.providers.ObservationSetProvider.createSet(tmpName, tmpProjectId);
		let savedSet;
		try
		{
			savedSet = await this.pict.providers.ObservationSetProvider.saveSet(tmpNewSet);
		}
		catch (error)
		{
			this.pict.log.error(`Error creating observation set: ${error}`);
			this.handleCancelCreateSetModal();
			return;
		}
		const pView = this.pict.views[viewHash];
		const pInput = pView?.getInputFromHash(inputHash);
		// seed the set-name cache so header labels resolve immediately without a follow-up network fetch
		if (savedSet?.IDObservationSet && savedSet?.Name)
		{
			this._observationSetNameCache[String(savedSet.IDObservationSet)] = savedSet.Name;
		}
		// update the GUID map and hidden input with the new set ID
		this.pict.AppData.ObservationCardDeckProvider.dynamicGUIDMap[rawID] = savedSet.IDObservationSet;
		this.pict.ContentAssignment.assignContent(this.getHiddenInputHTMLID(rawID), savedSet.IDObservationSet);
		// update the select2 dropdown with the new set option
		const tmpSelectElement = document.querySelector(this.getCardDeckHeaderObservationSetSelectorHTMLID(rawID));
		if (tmpSelectElement && window['jQuery'])
		{
			const newOption = new Option(savedSet.Name, `${ savedSet.IDObservationSet }`, true, true);
			window['jQuery'](tmpSelectElement).append(newOption).trigger('change');
		}
		// marshal the form data so the new value is persisted
		if (pView && pInput)
		{
			pView.dataChanged(pInput.Hash);
		}
		// invoke the custom post-create callback if registered (e.g. bridge defect sequence number increment)
		if (typeof this.pict.AppData.ObservationCardDeckProvider.customOnAfterObservationSetCreated === 'function')
		{
			this.pict.AppData.ObservationCardDeckProvider.customOnAfterObservationSetCreated(savedSet, pInput);
		}
		// auto-save the document if applicable
		if (this.pict.AppData?.DocumentData?.ReportData?.IDDocument)
		{
			this.pict.providers.DocumentProvider.handleSave(true);
		}
		// update the deck to display the new (empty) set
		if (pInput)
		{
			await this.updateDeckForObservationSetSelectionChange(pInput, `${ savedSet.IDObservationSet }`);
			this.handleObservationSetEditButtonStateOnChange(pInput);
		}
		// dismiss the modal and clear loading state
		this.fable.HeadlightModal.dismissModals();
		const tmpCreateButton = document.querySelector(this.getCardDeckHeaderObservationSetCreateButtonHTMLID(rawID));
		if (tmpCreateButton)
		{
			tmpCreateButton.classList.remove('is-loading');
		}
		this._pendingCreateSetContext = null;
		// apply the next action if one was selected
		if (tmpNextAction === 'manage')
		{
			this.handleObservationSetEditButtonClick(rawID, viewHash, inputHash);
		}
		else if (tmpNextAction === 'capture')
		{
			// programmatically trigger the quick capture file input
			const tmpQuickCaptureLabel = document.querySelector(this.getCardDeckHeaderActionsButtonQuickCaptureHTMLID(rawID));
			const tmpFileInput = /** @type {HTMLInputElement} */ (tmpQuickCaptureLabel?.querySelector('input[type="file"]'));
			if (tmpFileInput)
			{
				tmpFileInput.click();
			}
		}
	}

	/**
	 * Sets a custom handler for the create set button click event.
	 * @param {Function} pFunction - The custom handler function.
	 */
	setCustomHandleObservationSetCreateSetButtonClick(pFunction)
	{
		this.pict.AppData.ObservationCardDeckProvider.customHandleObservationSetCreateSetButtonClick = pFunction;
	}

	/**
	 * Sets a custom callback invoked after a new observation set is created via the create set modal.
	 * Useful for external components that need to perform additional actions after set creation (e.g. incrementing sequence numbers).
	 * @param {Function} pFunction - Callback receiving (pSavedSet, pInput).
	 */
	setCustomOnAfterObservationSetCreated(pFunction)
	{
		this.pict.AppData.ObservationCardDeckProvider.customOnAfterObservationSetCreated = pFunction;
	}

	/**
	 * Sets a custom callback invoked after an observation is added to or removed from an observation set.
	 * Useful for external components that need to react to set content changes (e.g. updating photo count labels).
	 * @param {Function} pFunction - Callback receiving (pInput, pIDObservation, pObservationSet).
	 */
	setCustomOnAfterObservationSetChanged(pFunction)
	{
		this.pict.AppData.ObservationCardDeckProvider.customOnAfterObservationSetChanged = pFunction;
	}

	/**
	 * Sets a document-level context callback invoked after observation set content changes.
	 * Useful for triggering broader document recalculations (e.g. orphan observation list reorganization).
	 * @param {Function} pFunction - Callback receiving no arguments.
	 */
	setDocumentContextCallback(pFunction)
	{
		this.pict.AppData.ObservationCardDeckProvider.documentContextCallback = pFunction;
	}

	/**
	 * Sets a custom HandleSearchFilters callback for the observation set selector's Select2 AJAX query.
	 * When provided, this replaces the default project-scoped filter. The callback receives the current
	 * filter array and should return the modified filter array.
	 * @param {((pFilters: string[]) => string[]) | null} pFunction - The custom filter callback, or null to reset.
	 */
	setCustomHandleSearchFiltersForSelector(pFunction)
	{
		this.pict.AppData.ObservationCardDeckProvider.customHandleSearchFiltersForSelector = pFunction;
	}

	/**
	 * Sets a custom callback invoked after the CardDeck finishes marshalling and rendering cards for an input.
	 * Useful for external components that need to react to deck content after the DOM is populated (e.g. updating photo count labels on initial load or set selection).
	 * @param {Function} pFunction - Callback receiving (pInput, pObservationList).
	 */
	setCustomOnAfterMarshal(pFunction)
	{
		this.pict.AppData.ObservationCardDeckProvider.customOnAfterMarshal = pFunction;
	}

	/**
	 * Opens the inline rename modal for an observation set attached to a non-selector card deck.
	 * Triggered by the pencil icon rendered next to the static set label.
	 * @param {string} pRawHTMLID - The raw HTML ID of the deck input, used to locate the display container on confirm.
	 * @param {number|string} pIDObservationSet - The set to rename.
	 */
	async handleInlineRenameSetClick(pRawHTMLID, pIDObservationSet)
	{
		if (!pIDObservationSet || this.isDocumentLocked || !this.canEditDocument)
		{
			return;
		}
		let tmpSet;
		try
		{
			tmpSet = await this.pict.providers.ObservationSetProvider.loadSet(pIDObservationSet);
		}
		catch (error)
		{
			this.pict.log.error(`Error loading observation set ${pIDObservationSet} for rename: ${error}`);
			return;
		}
		if (!tmpSet)
		{
			this.pict.log.error(`No observation set record returned for ID ${pIDObservationSet}. Cannot open rename modal.`);
			return;
		}
		// If the stored name is auto-generated (timestamped), start with an empty input so the user isn't renaming the timestamp
		const tmpInitialValue = this.isAutoGeneratedSetName(tmpSet.Name) ? '' : (tmpSet.Name || '');
		const tmpFallbackLabel = this.getFallbackObservationSetLabel(pIDObservationSet);
		this._pendingRenameSetContext = { rawHTMLID: pRawHTMLID, idObservationSet: pIDObservationSet, setRecord: tmpSet };
		const tmpInitialValueEscaped = tmpInitialValue.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
		const tmpFallbackEscaped = tmpFallbackLabel.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
		const modalBodyTemplate = /*html*/`
			<div class="columns is-mobile">
				<div class="column">
					<div class="field">
						<label class="label">Set Name</label>
						<div class="control">
							<input class="input is-fullwidth" id="ObservationSetRenameInput" type="text" value="${tmpInitialValueEscaped}" placeholder="${tmpFallbackEscaped}" onfocus="this.select()" />
						</div>
						<p class="help">Leave blank to keep the default "${tmpFallbackEscaped}" label.</p>
					</div>
				</div>
			</div>`;
		const modalFooterTemplate = /*html*/`
			<div class="buttons is-right is-flex-grow-1">
				<button type="button" class="button is-link is-soft" onclick="_Pict.providers.ObservationCardDeckProvider.handleCancelRenameSetModal()">
					<span class="icon"><i class="fas fa-fw fa-times"></i></span>
					<span>Cancel</span>
				</button>
				<button type="button" class="button is-link" onclick="_Pict.providers.ObservationCardDeckProvider.handleConfirmRenameSetModal()">
					<span class="icon"><i class="fas fa-fw fa-check"></i></span>
					<span>Save</span>
				</button>
			</div>`;
		this.fable.HeadlightModal.spawnModal({
			title: 'Rename Observation Set',
			modalBodyTemplate: modalBodyTemplate,
			modalFooterTemplate: modalFooterTemplate,
			hideModalFooter: false,
			disableBackgroundClickToClose: true,
		});
	}

	/**
	 * Handles the cancel action for the rename set modal. Dismisses the modal and clears pending context.
	 */
	handleCancelRenameSetModal()
	{
		this.fable.HeadlightModal.dismissModals();
		this._pendingRenameSetContext = null;
	}

	/**
	 * Handles the confirm action for the rename set modal. Saves the new name to the server, updates the set-name cache,
	 * and refreshes the header label so the change is reflected immediately without requiring a full remarshal.
	 */
	async handleConfirmRenameSetModal()
	{
		if (!this._pendingRenameSetContext)
		{
			this.pict.log.error('No pending rename set context found. Cannot save rename.');
			return;
		}
		const { rawHTMLID, idObservationSet, setRecord } = this._pendingRenameSetContext;
		const tmpInput = /** @type {HTMLInputElement} */ (document.getElementById('ObservationSetRenameInput'));
		const tmpNewName = tmpInput?.value?.trim() || '';
		// Empty input → keep the previously-stored Name unchanged; display logic will still show the fallback label if it's auto-generated
		const tmpNameToSave = tmpNewName || setRecord.Name || this.getFallbackObservationSetLabel(idObservationSet);
		// Short-circuit if nothing actually changed
		if (tmpNameToSave === setRecord.Name)
		{
			this.handleCancelRenameSetModal();
			return;
		}
		setRecord.Name = tmpNameToSave;
		try
		{
			await this.pict.providers.ObservationSetProvider.saveSet(setRecord);
		}
		catch (error)
		{
			this.pict.log.error(`Error saving renamed observation set ${idObservationSet}: ${error}`);
			this.handleCancelRenameSetModal();
			return;
		}
		// Update the cache so any subsequent re-render uses the new name synchronously
		this._observationSetNameCache[String(idObservationSet)] = tmpNameToSave;
		// Refresh the header label directly so the change appears without waiting for a full marshal cycle
		const tmpDisplayContainer = this.pict.ContentAssignment.getElement(this.getCardDeckHeaderObservationSetDisplayHTMLID(rawHTMLID))?.[0];
		const tmpTextSpan = tmpDisplayContainer?.querySelector('.icon-text');
		if (tmpTextSpan)
		{
			tmpTextSpan.textContent = this.getDisplayLabelForObservationSet(idObservationSet);
		}
		this.fable.HeadlightModal.dismissModals();
		this._pendingRenameSetContext = null;
	}

	/**
	 * Handle the state of the edit button based on the hidden input value.
	 * @param {Object} pInput - The input object.
	 */
	handleObservationSetEditButtonStateOnChange(pInput)
	{
		const tmpHiddenElement = this.pict.ContentAssignment.getElement(this.getHiddenInputHTMLID(pInput.Macro.RawHTMLID))?.[0];
		const tmpValue = tmpHiddenElement instanceof HTMLInputElement && tmpHiddenElement.value;
		const tmpEditButton = this.pict.ContentAssignment.getElement(this.getCardDeckHeaderObservationSetEditButtonHTMLID(pInput.Macro.RawHTMLID))?.[0];
		if (!tmpValue)
		{
			tmpEditButton?.setAttribute('disabled', 'disabled');
		}
		else
		{
			tmpEditButton?.removeAttribute('disabled');
		}
	}

	/**
	 * Handles the the inline add button click event for the given input.
	 * This event is triggered when the user clicks the inline add button for an input.
	 * @param {Object} event - The event object.
	 * @param {string} pViewHash - The view hash.
	 * @param {string} pInputHash - The input hash.
	 * @param {boolean} pQuickCaptureButton - Whether the button clicked was the quick capture button.
	 */
	async handleAddNewCardToDeckButtonEvent(event, pViewHash, pInputHash, pQuickCaptureButton = false)
	{
		const currentSettings = this.getCurrentSettingsForInput(this.pict.views[pViewHash].getInputFromHash(pInputHash));
		// The raw event target — for the Add button, this can be a child of the button (the icon
		// <span> or <i>) depending on where the click actually landed; for Quick Capture, this is
		// always the <input type="file"> (since the change event fires on the input).
		/** @type {HTMLElement} */
		const tmpRawEventTarget = event.target;
		// The visible button surface that should receive the `is-loading` class. Resolve via
		// event.currentTarget (the element the listener is attached to via onclick/onchange) when
		// it already has the .button class; otherwise climb to the nearest .button so the loading
		// state always lands on the visible button — for Quick Capture this is the wrapping <label>.
		/** @type {HTMLElement} */
		const currentButtonElement = (event.currentTarget && event.currentTarget.classList?.contains('button')) ? event.currentTarget : (tmpRawEventTarget?.closest?.('.button') || event.currentTarget || tmpRawEventTarget);
		let tmpView = this.pict.views[pViewHash];
		let tmpInput = tmpView.getInputFromHash(pInputHash);
		currentButtonElement.classList.add('is-loading');
		// track for panel-close cleanup — only the panel-opening (non-quick-capture) path can leave
		// this stuck if the user dismisses the panel without completing the add flow, but it's safe
		// to track quick capture too since handleAddCardToNewSetRecord/the upload path always reaches
		// its callback and unregisters synchronously after removing the class.
		this._pendingAddButtonElements.add(currentButtonElement);

		if (!currentSettings?.useObservationSet && this.hasFiltersApplied(tmpInput) && !this.hasActionableFilters(tmpInput))
		{
			this.pict.log.warn('Cannot add new observation through the card deck when non-label filters are applied on a non-set based deck.');
			currentButtonElement.classList.remove('is-loading');
			this._pendingAddButtonElements.delete(currentButtonElement);
			return false;
		}
		// for the Obeservation Set Path
		if (currentSettings.useObservationSet)
		{
			// if we are using observation sets, we need to make sure we have an observation set to add the new observation to,
			// since the set is what manages the list of observations that are displayed in the deck and the filters that are applied to those observations,
			// so if we don't have a set to add the new observation to, then we won't be able to display it in the deck or apply any filters to it
			const tmpSelectElement = this.pict.ContentAssignment.getElement(this.getCardDeckHeaderObservationSetSelectorHTMLID(tmpInput.Macro.RawHTMLID))?.[0];
			let currentObservationSetID = isNaN(parseInt(tmpSelectElement?.value)) ? null : parseInt(tmpSelectElement?.value);
			// If the selector element doesn't exist (useObservationSetSelector is false), fall back to the hidden input
			// which always contains the IDObservationSet from the initial data marshal
			if (!currentObservationSetID)
			{
				const tmpHiddenInputValue = this.pict.ContentAssignment.getElement(this.getHiddenInputHTMLID(tmpInput.Macro.RawHTMLID))?.[0]?.value;
				currentObservationSetID = isNaN(parseInt(tmpHiddenInputValue)) ? null : parseInt(tmpHiddenInputValue);
			}
			let currentObservationSet = currentObservationSetID ? await this.pict.providers.ObservationSetProvider.loadSet(currentObservationSetID) : null;
			// if there's no set id yet, we have to go create one before we can begin adding images
			if (!currentObservationSetID && !currentObservationSet)
			{
				// when requireExplicitSetCreation is enabled, block the auto-create fallback — the user
				// must use "Create New Set" (which goes through the custom handler) before adding images
				if (currentSettings.requireExplicitSetCreation)
				{
					this.pict.log.warn('Cannot add observation — no set is selected and requireExplicitSetCreation is enabled. Use "Create New Set" first.');
					currentButtonElement.classList.remove('is-loading');
					this._pendingAddButtonElements.delete(currentButtonElement);
					return false;
				}
				this.pict.log.info('No Observation Set ID found, creating a new one.');
				const newSet = await this.createNewObservationSetForCardDeck(pViewHash, pInputHash);
				currentObservationSetID = newSet.IDObservationSet;
				currentObservationSet = newSet;
				// get the temp namespace so we can clear it out now (garbage collection effort)
				const tmpNamespaceForTempIDObservationSet = this.buildNamespaceForCardDeck(pInputHash, null, true);
				// check the media registry to see find the TEMP namespace we created for this input hash when we first built the deck, and if it exists, we need to update that namespace to point to the new set ID and pull the observations for that set so that we can update the deck with the correct set of observations based on the new set ID
				await this.pict.providers.MediaRegistry.clearNamespace(tmpNamespaceForTempIDObservationSet);
				// update the card deck header selector with the new set ID so that it's selected and we can pull the correct set from the registry to display in the deck
				const tmpNamespaceForNewIDObservationSet = this.buildNamespaceForCardDeck(pInputHash, currentObservationSetID);
				await this.pict.providers.MediaRegistry.registerMediaSetByIDObservationSet(tmpNamespaceForNewIDObservationSet, currentObservationSetID);
				this.handleHeaderContentForCardDeck(tmpView, tmpInput, tmpNamespaceForNewIDObservationSet);
				// update the views so we have the latest state of elements to inject the image into
				tmpView = this.pict.views[pViewHash];
				tmpInput = tmpView.getInputFromHash(pInputHash);
			}
			// set the IDObservationSet for the image panel so that interactions there will apply to the current set
			this.pict.views.ObservationSet.setObservationSetID(currentObservationSet.IDObservationSet);
			this.pict.views.ObservationSet.ActiveObservationSet = currentObservationSet;

			// Quick capture will bypass the observation selection panel and just add the captured image directly to the set and deck,
			// while the regular add button will open the observation panel for the user to select/search/upload images to add to the set and deck
			if (pQuickCaptureButton)
			{
				// quickCaptureImage expects the underlying <input type="file">, not the wrapping
				// <label class="button"> that currentButtonElement now resolves to.
				const tmpFileInput = /** @type {HTMLInputElement} */ (tmpRawEventTarget);
				this.pict.views.ObservationCreateMedia.quickCaptureImage(tmpFileInput, { IDDocument: this.pict?.AppData?.DocumentData?.ReportData?.IDDocument },
					async (/** @type {any[]} */ observationInfos) =>
					{
						await this.handleAddCardToNewSetRecord(pViewHash, pInputHash, observationInfos);
						currentButtonElement.classList.remove('is-loading');
						this._pendingAddButtonElements.delete(currentButtonElement);
						tmpFileInput.value = ''; // reset the file input so that the same file can be uploaded again if needed
					});
			}
			else
			{
				this.pict.views.HeadlightPanels.handlePanelToggle('Observations', 'open',
					() =>
					{
						// start at the upload tab
						this.pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-ObservationPanel-Upload', 'Observation');
						// 'add-to-set' is the recognized CSS mode that triggers the green banner and add-mode UI
						this.pict.views.HeadlightPaneObservations.setObservationPanelTarget(tmpView.Hash, tmpInput.Hash, this.pict.views.ObservationSet.ActiveObservationSet?.Name || tmpInput.Name, 'add-to-set');
						this.pict.views.ObservationSearch.SearchResultActionLabel = 'Add to Observation Set';
						this.pict.views.HeadlightPaneObservations.SearchResultActionLabel = 'Add to Observation Set';
						/** @type {(observationInfos: Array<{ IDObservation: number | string, Observation?: any, FileDetails?: { } }>) => Promise<void>} */
						this.pict.views.ObservationCreateMedia.InstanceConfiguration.onAfterCallback = async (observationInfos) =>
						{
							await this.handleAddCardToNewSetRecord(pViewHash, pInputHash, observationInfos);
							currentButtonElement.classList.remove('is-loading');
							this._pendingAddButtonElements.delete(currentButtonElement);
						};
					});
			}
			return true;
		}
		// For the Document List Path (non-set decks, with or without label-only filters)
		if (pQuickCaptureButton)
		{
			// quickCaptureImage expects the underlying <input type="file">, not the wrapping
			// <label class="button"> that currentButtonElement now resolves to.
			const tmpFileInput = /** @type {HTMLInputElement} */ (tmpRawEventTarget);
			this.pict.views.ObservationCreateMedia.quickCaptureImage(tmpFileInput, { IDDocument: this.pict?.AppData?.DocumentData?.ReportData?.IDDocument },
				async (/** @type {any[]} */ observationInfos) =>
				{
					// The new observation is already in DocumentData.ReportData.Observations (added by quickCaptureImage).
					// Gate: reject observations whose type doesn't match the deck's ObservationType filters.
					const validObservations = observationInfos.filter((obsInfo) =>
					{
						const obsRecord = obsInfo.Observation ||
							this.pict.AppData?.DocumentData?.ReportData?.Observations?.find((/** @type {any} */ o) => o.IDObservation == obsInfo.IDObservation);
						if (obsRecord && !this.observationMatchesTypeFilter(tmpInput, obsRecord))
						{
							this.pict.log.warn(`Observation ${obsInfo.IDObservation} (type "${obsRecord.ObservationType}") does not match the deck's ObservationType filter — skipping.`);
							this.showFilterMismatchWarning(tmpInput, obsRecord.ObservationType);
							return false;
						}
						return true;
					});
					// If label-only filters are active, auto-apply the Include labels so the new observation matches the filter criteria.
					for (const obsInfo of validObservations)
					{
						if (obsInfo.Observation && this.hasActionableFilters(tmpInput))
						{
							await this.applyFilterLabelsToObservation(tmpInput, obsInfo.Observation);
						}
					}
					// Refresh the MediaRegistry's document observations and remarshal all document-list decks to pick it up.
					await this.pict.providers.MediaRegistry.refreshDocumentObservations(this.pict.AppData.DocumentData.ReportData.Observations);
					for (const obsInfo of validObservations)
					{
						await this.reMarshalAllViewsWithCardDecks(obsInfo.IDObservation, 'AllDocumentLists', true);
					}
					currentButtonElement.classList.remove('is-loading');
					this._pendingAddButtonElements.delete(currentButtonElement);
					tmpFileInput.value = '';
				});
		}
		else
		{
			// open the observation panel for the user to select/search/upload images to add to the document
			tmpView.pict.views.HeadlightPanels.handlePanelToggle('Observations', 'open',
				async () =>
				{
					// start at the upload tab
					this.pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-ObservationPanel-Upload', 'Observation');
					tmpView.pict.views.HeadlightPaneObservations.setObservationPanelTarget(tmpView.Hash, tmpInput?.Hash, tmpInput?.Name, 'add-to-document');
					this.pict.views.ObservationSearch.SearchResultActionLabel = 'Add to Document';
					this.pict.views.HeadlightPaneObservations.SearchResultActionLabel = 'Add to Document';
					/** @type {(observationInfos: Array<{ IDObservation: number | string, Observation?: any, FileDetails?: { } }>) => Promise<void>} */
					this.pict.views.ObservationCreateMedia.InstanceConfiguration.onAfterCallback = async (observationInfos) =>
					{
						// Gate: reject observations whose type doesn't match the deck's ObservationType filters.
						const validObservations = observationInfos.filter((obsInfo) =>
						{
							const obsRecord = obsInfo.Observation ||
								this.pict.AppData?.DocumentData?.ReportData?.Observations?.find((/** @type {any} */ o) => o.IDObservation == obsInfo.IDObservation);
							if (obsRecord && !this.observationMatchesTypeFilter(tmpInput, obsRecord))
							{
								this.pict.log.warn(`Observation ${obsInfo.IDObservation} (type "${obsRecord.ObservationType}") does not match the deck's ObservationType filter — skipping.`);
								this.showFilterMismatchWarning(tmpInput, obsRecord.ObservationType);
								return false;
							}
							return true;
						});
						// If label-only filters are active, auto-apply the Include labels so the new observation matches the filter criteria.
						for (const obsInfo of validObservations)
						{
							if (obsInfo.Observation && this.hasActionableFilters(tmpInput))
							{
								await this.applyFilterLabelsToObservation(tmpInput, obsInfo.Observation);
							}
						}
						await this.pict.providers.MediaRegistry.refreshDocumentObservations(this.pict.AppData.DocumentData.ReportData.Observations);
						for (const obsInfo of validObservations)
						{
							await this.reMarshalAllViewsWithCardDecks(obsInfo.IDObservation, 'AllDocumentLists', true);
						}
						currentButtonElement.classList.remove('is-loading');
						this._pendingAddButtonElements.delete(currentButtonElement);
					};
				});
		}
		return true;
	}

	/**
	 * Clear the "is-loading" state from any Add or QuickCapture buttons that opened a panel-driven
	 * add flow but never completed it. This is called from HeadlightPaneObservations.closeHandler()
	 * when the user dismisses the Observations panel (X button, escape, background click, toggle).
	 *
	 * The button only gets removed from the tracking set after a successful completion of the
	 * add flow (where its onAfterCallback fires), so anything still in the set at panel-close time
	 * is by definition stuck. Defensive: we'd rather show a button as not-loading momentarily
	 * than leave it semi-permanently stuck for the user.
	 * @returns {void}
	 */
	clearPendingAddButtonLoadingState()
	{
		if (!this._pendingAddButtonElements || this._pendingAddButtonElements.size === 0)
		{
			return;
		}
		for (const tmpButtonElement of this._pendingAddButtonElements)
		{
			tmpButtonElement?.classList?.remove('is-loading');
		}
		this._pendingAddButtonElements.clear();
	}

	/**
	 * Handles the process of adding a card to the deck and the corresponding observation to the active observation set when a new observation is added through the card deck UI, including adding the observation to the set if it's not already in there and removing the card from any decks that are configured to exclude the set that the observation was added to.
	 * @param {string} pViewHash - The view hash.
	 * @param {string} pInputHash - The input hash.
	 * @param {Array<{ IDObservation: number | string, Observation?: any, FileDetails?: { } }>} pObservations - An array of objects containing information about the observations that were added through the card deck UI, including their IDs and any additional details that may be needed for processing.
	 */
	async handleAddCardToNewSetRecord(pViewHash, pInputHash, pObservations)
	{
		if (!this.pict.views.ObservationSet.ActiveObservationSet)
		{
			this.pict.log.warn('No active observation set to add observation to.');
			return;
		}
		const tmpView = this.pict.views[pViewHash];
		const tmpInput = tmpView?.getInputFromHash(pInputHash);
		const tmpIDObservationSet = this.pict.views.ObservationSet.ActiveObservationSet.IDObservationSet;
		for (const tmpObservation of pObservations)
		{
			if (!tmpObservation.IDObservation)
			{
				this.pict.log.warn('Observation info is missing IDObservation:', tmpObservation);
				continue;
			}
			// Gate: reject observations whose type doesn't match the deck's ObservationType filters.
			// Resolve the full observation record from the callback payload, the document list, or a fresh API fetch.
			const tmpObsRecord = tmpObservation.Observation ||
				this.pict.AppData?.DocumentData?.ReportData?.Observations?.find((/** @type {any} */ o) => o.IDObservation == tmpObservation.IDObservation);
			if (tmpInput && tmpObsRecord && !this.observationMatchesTypeFilter(tmpInput, tmpObsRecord))
			{
				this.pict.log.warn(`Observation ${tmpObservation.IDObservation} (type "${tmpObsRecord.ObservationType}") does not match the deck's ObservationType filter — skipping.`);
				this.showFilterMismatchWarning(tmpInput, tmpObsRecord.ObservationType);
				continue;
			}
			// if the observation is not already in the active set, add it to the set before we add the card to the deck
			if (!this.pict.views.ObservationSet.ActiveObservationSet.Observations.find((/** @type {any} */ obs) => obs.IDObservation === tmpObservation.IDObservation))
			{
				await this.pict.views.ObservationSet.addObservationToSet(tmpObservation.IDObservation);
			}
			// refresh the MediaRegistry namespace for the active observation set so addCardToDeck can find the newly added observation
			if (tmpIDObservationSet)
			{
				await this.pict.providers.MediaRegistry.updateNamespacesByIDObservationSet(tmpIDObservationSet);
			}
			// after we add the observation to the set, we can add the card to the deck
			await this.addCardToDeck(pViewHash, pInputHash, tmpObservation.IDObservation)
			.then(async () =>
			{
				this.pict.log.info(`Added card to deck for observation ID ${tmpObservation.IDObservation} after adding observation to set.`);
				// for all other non-sets based decks, we need refresh the decks so they pick up the new image in the document observation list update
				await this.reMarshalAllViewsWithCardDecks(tmpObservation.IDObservation, 'AllDocumentLists', true);
			})
			.catch((error) =>
			{
				this.pict.log.error(`Error adding card to deck for observation ID ${tmpObservation.IDObservation}: ${error}`);
				return;
			});
		}
		// remarshal all observation-set-backed decks so any other decks showing this set also reflect the new observation(s)
		if (tmpIDObservationSet && pObservations.length)
		{
			await this.reMarshalAllViewsWithCardDecks(pObservations[pObservations.length - 1].IDObservation, 'AllObservationSets', true);
		}
		// invoke the custom observation set changed callback if registered (e.g. bridge defect photo count label update)
		if (typeof this.pict.AppData.ObservationCardDeckProvider.customOnAfterObservationSetChanged === 'function')
		{
			this.pict.AppData.ObservationCardDeckProvider.customOnAfterObservationSetChanged(tmpInput, pObservations[pObservations.length - 1]?.IDObservation, this.pict.views.ObservationSet.ActiveObservationSet);
		}
		// invoke the document context callback if registered (e.g. orphan observation recalculation)
		if (typeof this.pict.AppData.ObservationCardDeckProvider.documentContextCallback === 'function')
		{
			this.pict.AppData.ObservationCardDeckProvider.documentContextCallback();
		}
	}

	/**
	 * Remarshals all views that have card decks in them based on the given observation ID and package type, which is used to trigger a refresh of the decks in those views so that
	 * they can pick up any changes to the observations (ex: new image added to an observation, observation added/removed from the document, etc.) and update the cards in the deck
	 * accordingly, and optionally runs a cache busting function for the images in those decks if needed.
	 * @param {number|string} pIDObservation - The ID of the observation that was changed and is being used as the basis for determining which decks need to be refreshed.
	 * @param {string} pPackType - The package type that is used to determine which views have card decks that may need to be refreshed based on the observation change.
	 * @param {boolean} pRunCacheBust - Whether to run the cache busting function for the images in the decks that are being refreshed, which may be needed if the observation change involves an update to an existing image rather than just adding a new image or adding/removing an observation from the document.
	 */
	async reMarshalAllViewsWithCardDecks(pIDObservation, pPackType, pRunCacheBust = false)
	{
		const allPotentialDecksToReMarshal = document.querySelectorAll(this.packageTypeSelectorMap[pPackType]);
		if (!allPotentialDecksToReMarshal.length)
		{
			this.pict.log.info('No card decks found to refresh.');
			return;
		}
		// for each view that has a card deck, we'll remarshal that view (rather than individual inputs)
		/**
		 * @type {any[]}
		 */
		const viewsToRefresh = [];
		allPotentialDecksToReMarshal.forEach((deck) =>
		{
			const viewHash = deck.getAttribute(this.deckViewHashAttribute);
			if (viewHash)
			{
				if (!viewsToRefresh.includes(viewHash))
				{
					viewsToRefresh.push(viewHash);
				}
			}
		});
		if (viewsToRefresh.length)
		{
			for (const viewHash of viewsToRefresh)
			{
				const tmpView = this.pict.views[viewHash];
				if (tmpView)
				{
					this._isRemarshalInProgress = true;
					tmpView.marshalToView();
					// wait for all async card deck marshal work to complete before moving to the next view,
					// preventing concurrent rebuilds from racing over shared MediaRegistry state
					await this.waitForPendingMarshals();
					this._isRemarshalInProgress = false;
					if (pRunCacheBust && pIDObservation)
					{
						await this.pict.providers.ObservationProvider.cacheBustObsImages(pIDObservation, this.cacheBusterAttemptTimers);
					}
				}
			}
		}
	}

	/**
	 * Handles the click event for a card thumbnail in the deck.
	 * @param {Event} event - The click event object.
	 * @param {string} pViewHash - The view hash.
	 * @param {string} pInputHash - The input hash.
	 * @param {number} pIDObservation - The observation ID associated with the clicked thumbnail.
	 */
	handleClickForCardThumbnail(event, pViewHash, pInputHash, pIDObservation)
	{
		if (event)
		{
			event.preventDefault();
			event.stopPropagation();
		}
		const currentSettings = this.getCurrentSettingsForInput(this.pict.views[pViewHash].getInputFromHash(pInputHash));
		// if there's a custom callback defined for when clicking on a thumbnail in the deck, use that, otherwise fall back to the default behavior of opening the image panel with the observation details
		if (currentSettings.onClickThumbnailClickCallback && typeof currentSettings.onClickThumbnailClickCallback === 'function')
		{
			currentSettings.onClickThumbnailClickCallback(event, this.pict.views[pViewHash], this.pict.views[pViewHash].getInputFromHash(pInputHash), pIDObservation);
		}
		else
		{
			// for the annotation workflow path, we want to open the observation detail view in the side panel when clicking on a thumbnail, and if the ObservationSubImageSelector view is active,
			// we want to set the intent for that view so that it knows to update based on interactions in the observation detail view
			if (this.pict?.views?.ObservationSubImageSelector?.intent)
			{
				const parentID = event.target instanceof HTMLElement && event.target.parentElement?.parentElement?.id;
				const obsSetMappedID = parentID?.replace('IMAGE-CONTAINER-FOR-', '') || 0;
				if (obsSetMappedID)
				{
					this.pict.fable.services.HeadlightPanels.applyUIHighlight(`#${ parentID?.replace('IMAGE-CONTAINER-FOR-', 'ELEMENT-CONTAINER-FOR-') }`);
					this.pict.log.info('Found ObservationSet ID: ', obsSetMappedID);
					this.pict.views.ObservationSubImageSelector.intent =
					{
						type: 'ObservationSet',
						IDObservationSet: this.pict.AppData.ObservationCardDeckProvider.dynamicGUIDMap[obsSetMappedID],
					};
				}
			}
			this.pict.views.HeadlightPaneObservations.showObservationDetailView(event, pIDObservation, null);
		}
	}

	/**
	 * Adds a card to the deck for the given observation ID.
	 * @param {string} pViewHash - The view hash.
	 * @param {string} pInputHash - The input hash.
	 * @param {number|string} pIDObservation - The observation ID.
	 * @return {Promise<void>}
	 */
	async addCardToDeck(pViewHash, pInputHash, pIDObservation)
	{
		if (!pIDObservation)
		{
			this.pict.log.warn('No observation ID provided. Cannot add card to deck.');
			return Promise.reject(new Error('No observation ID provided. Cannot add card to deck.'));
		}
		if (!pViewHash || !pInputHash)
		{
			this.pict.log.warn('No view hash or input hash provided. Cannot add card to deck.');
			return Promise.reject(new Error('No view hash or input hash provided. Cannot add card to deck.'));
		}
		const tmpView = this.pict.views[pViewHash];
		if (!tmpView)
		{
			this.pict.log.warn(`View with hash ${pViewHash} not found. Cannot add card to deck.`);
			return Promise.reject(new Error(`View with hash ${pViewHash} not found. Cannot add card to deck.`));
		}
		const tmpInput = tmpView.getInputFromHash(pInputHash);
		if (!tmpInput)
		{
			this.pict.log.warn(`Input with hash ${pInputHash} not found in view ${pViewHash}. Cannot add card to deck.`);
			return Promise.reject(new Error(`Input with hash ${pInputHash} not found in view ${pViewHash}. Cannot add card to deck.`));
		}
		const tmpCurrentSettings = this.getCurrentSettingsForInput(tmpInput);
		// disable adding cards if the setting is enabled for the deck
		if (tmpCurrentSettings.preventAddingCards)
		{
			return;
		}
		const deckContainer = this.pict.ContentAssignment.getElement(this.getCardDeckContentHTMLID(tmpInput.Macro.RawHTMLID))?.[0];
		if (tmpInput && deckContainer)
		{
			let tmpObservationRecord;
			// check sets for the observation before we add it to the deck, since if we're using sets to manage the lists of observations that are displayed in the deck
			if (tmpCurrentSettings.useObservationSet)
			{
				const tmpIDObservationSet = this.pict.ContentAssignment.getElement(this.getHiddenInputHTMLID(tmpInput.Macro.RawHTMLID))?.[0]?.value;
				const tmpNamespaceForIDObservationSet = this.buildNamespaceForCardDeck(pInputHash, tmpIDObservationSet);
				const tmpObservationSetList = await this.pict.providers.MediaRegistry.getMediaSet(tmpNamespaceForIDObservationSet);
				tmpObservationRecord = tmpObservationSetList.find((/** @type {any} */ obs) => obs.IDObservation == pIDObservation);
				if (!tmpObservationRecord)
				{
					this.pict.log.error(`Could not find observation with ID ${pIDObservation} in the observation set for input ${tmpInput.Macro.RawHTMLID}. Cannot add card to deck.`);
					return Promise.reject(new Error(`Could not find observation with ID ${pIDObservation} in the observation set for input ${tmpInput.Macro.RawHTMLID}. Cannot add card to deck.`));
				}
			}
			else
			{
				const currentFilters = this.getCurrentFiltersForInput(tmpInput);
				if (currentFilters && Object.keys(currentFilters).length)
				{
					tmpObservationRecord = this.pict.AppData.DocumentData.ReportData.Observations.find((/** @type {any} */ obs) => obs.IDObservation == pIDObservation);
				}
				if (!tmpObservationRecord)
				{
					this.pict.log.error(`Could not find observation with ID ${pIDObservation} in document observations. Cannot add card to deck.`);
					return Promise.reject(new Error(`Could not find observation with ID ${pIDObservation} in document observations. Cannot add card to deck.`));
				}
			}

			return Promise.resolve(tmpObservationRecord);
		}
	}

	/**
	 * Removes a card from the deck for the given observation ID.
	 * @param {string} pViewHash - The view hash.
	 * @param {string} pInputHash - The input hash.
	 * @param {string|number} pIDObservation - The observation ID.
	 * @return {Promise<void>}
	 */
	async removeCardFromDeck(pViewHash, pInputHash, pIDObservation)
	{
		const tmpView = this.pict.views[pViewHash];
		const tmpInput = tmpView.getInputFromHash(pInputHash);
		const tmpCurrentSettings = this.getCurrentSettingsForInput(tmpInput);
		// disabled removing cards if the setting is enabled for the deck
		if (tmpCurrentSettings.preventRemovingCards)
		{
			return;
		}
		if (tmpInput)
		{
			if (this.isDocumentLocked || !this.canEditDocument)
			{
				this.pict.views.HeadlightPanels.toast({
					message: 'This document is locked. You cannot remove images from the set.',
					type: 'is-warning',
				});
				return;
			}
			this.pict.log.info(`Removing observation ${pIDObservation} from set for input ${tmpInput.Macro.RawHTMLID}.`);
			// NOTE: We intentionally are not doing confirmation prompt for sets as a UX choice - we want to encourage users to add/remove images to the set, so we want to make it easy to remove them.
			// for sets vs non-sets
			const tmpSetID = this.pict.ContentAssignment.getElement(this.getHiddenInputHTMLID(tmpInput.Macro.RawHTMLID))?.[0]?.value;
			if (tmpCurrentSettings.useObservationSet && tmpSetID)
			{
				let tmpSet = await this.pict.providers.ObservationSetProvider.loadSet(tmpSetID);
				const tmpObservation = tmpSet.Observations.find((/** @type {any} */ pObservation) => pObservation.IDObservation == pIDObservation);
				if (!tmpObservation)
				{
					this.pict.log.error(`Could not find observation with ID ${pIDObservation} in observation set with ID ${tmpSetID}. Cannot remove card from deck.`);
					return;
				}
				tmpSet = await this.pict.providers.ObservationSetProvider.removeObservation(tmpSet, tmpObservation).catch((/** @type {any} */ error) =>
				{
					this.pict.log.error(`Error removing observation with ID ${pIDObservation} from observation set with ID ${tmpSetID}: ${error}`);
					throw error;
				});
				this.pict.providers.ObservationSetProvider.saveSet(tmpSet);
				// invoke the custom observation set changed callback if registered (e.g. bridge defect photo count label update)
				if (typeof this.pict.AppData.ObservationCardDeckProvider.customOnAfterObservationSetChanged === 'function')
				{
					this.pict.AppData.ObservationCardDeckProvider.customOnAfterObservationSetChanged(tmpInput, pIDObservation, tmpSet);
				}
				// invoke the document context callback if registered (e.g. orphan observation recalculation)
				if (typeof this.pict.AppData.ObservationCardDeckProvider.documentContextCallback === 'function')
				{
					this.pict.AppData.ObservationCardDeckProvider.documentContextCallback();
				}
			}
			else
			{
				// remove this observation from the document observation list (which will update if they save)
				this.pict.AppData.DocumentData.ReportData.Observations = this.pict.AppData.DocumentData.ReportData.Observations.filter((/** @type {any} */ obs) => obs.IDObservation != pIDObservation);
				this.pict.providers.MediaRegistry.refreshDocumentObservations(this.pict.AppData.DocumentData.ReportData.Observations);
			}
			// refresh the decks so they pick up the removed image in the document observation list update
			await this.reMarshalAllViewsWithCardDecks(pIDObservation, 'AllDocumentLists', false);
			// also remarshal all observation-set-backed decks so the deck that just had the observation
			// removed (and any other decks showing the same set) reflect the change — without this,
			// useObservationSet decks (which are marked with data-deck-is-observation-set, not
			// data-deck-is-document-list) would never be re-rendered and the removed card would
			// remain visible in the UI
			await this.reMarshalAllViewsWithCardDecks(pIDObservation, 'AllObservationSets', false);
		}
	}

	/**
	 * This will create a new Observation Set if one doesn't already exist for this input.
	 * @param {string} pViewHash - The view hash.
	 * @param {string} pInputHash - The input hash.
	 * @return {Promise<*>}
	 */
	async createNewObservationSetForCardDeck(pViewHash, pInputHash)
	{
		return new Promise(async (resolve, reject) =>
		{
			this.pict.log.info('Creating a new Observation Set');

			const pView = this.pict.views[pViewHash];
			const pInput = pView.getInputFromHash(pInputHash);
			// make a new set if we don't have a value yet (should only be done the first time per form load)
			const tmpLabel = document.querySelector(this.getLabelHTMLID(pInput.Macro.RawHTMLID))['innerText'] || 'New Observation Set';
			const tmpName = tmpLabel + ' (' + new Date().toLocaleString() + ')';
			const tmpProjectId = this.pict.AppData?.DocumentData?.ReportData?.IDProject || null;
			// create a new observation set
			const tmpNewSet = this.pict.providers.ObservationSetProvider.createSet(tmpName, tmpProjectId);
			// and save the new set
			try
			{
				const pResult = await this.pict.providers.ObservationSetProvider.saveSet(tmpNewSet);
				// collect the new set ID and update the map
				this.pict.AppData.ObservationCardDeckProvider.dynamicGUIDMap[pInput.Macro.RawHTMLID] = pResult.IDObservationSet;
				// set the IDObservationSet value for the hidden input so that it gets saved in the FormData and we can reference it later when adding observations to the set
				this.pict.ContentAssignment.assignContent(this.getHiddenInputHTMLID(pInput.Macro.RawHTMLID), pResult.IDObservationSet);
				// marshal the form to data so that it saves properly in the FormData
				pView.dataChanged(pInput.Hash);
				// if it's a document, we should auto-save the document now too
				if (this.pict.AppData?.DocumentData?.ReportData?.IDDocument)
				{
					this.pict.providers.DocumentProvider.handleSave(true);
				}
				this.pict.log.info('New Observation Set created successfully. Created Set ID: ' + pResult.IDObservationSet);
				resolve(pResult);
			}
			catch (pError)
			{
				this.pict.log.error(`Error in createNewObservationSetForCardDeck: ${pError}`);
				resolve(false);
			}
		});
	}
}

module.exports = ObservationCardDeck;
